// ═══════════════════════════════════════════════════════════════════════════════
// DESKTOP ORGANIZER PAGE - 7,608 lines
// ═══════════════════════════════════════════════════════════════════════════════
// NAVIGATION: Use Cmd+Shift+O to jump to regions/functions
//
// STRUCTURE:
//   [UTILITIES] - Helper functions above component
//   [TYPES] - TypeScript interfaces
//   [COMPONENT] - Main OrganizerPage  
//   [STATE] - React state
//   [EFFECTS] - useEffect hooks
//   [HANDLERS] - Event handlers
//   [RENDER] - JSX render
// ═══════════════════════════════════════════════════════════════════════════════

//#region UTILITIES
let inviteHealthCache = { ok: false, ts: 0 };

async function ensureInviteHealth(supabaseUrl: string, supabaseAnon: string) {
  const cacheAge = Date.now() - inviteHealthCache.ts;
  if (inviteHealthCache.ok && cacheAge < 5 * 60 * 1000) return;

  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/send-invite`, {
      method: 'OPTIONS',
      headers: {
        Authorization: `Bearer ${supabaseAnon}`,
        apikey: supabaseAnon,
      },
    });

    if (!res.ok) {
      console.warn('[Invite] Health check failed (non-OK)', res.status);
      return; // allow send attempt; server may still succeed on POST
    }

    inviteHealthCache = { ok: true, ts: Date.now() };
  } catch (err) {
    console.warn('[Invite] Health check blocked (likely CORS preflight). Proceeding to send anyway.', err);
    // Do not block; allow send attempt to continue.
  }
}
//#endregion

//#region TYPES
// Type Definitions
interface ClassNotebook {
  id: string;
  name: string;
  title?: string;
  course?: string;
  instructor?: string;
  color: string;
  categories: CategoryItem[];
  archived?: boolean; // New: Allow hiding/archiving classes
}

interface MeetingTranscript {
  id: string;
  title?: string;
  topic?: string;
  content: string;
  date?: string;
  created_at?: string;
  createdAt?: string;
}

interface CategoryItem {
  id: string;
  name: string;
  items?: TimelineItem[];
  buckets?: {
    upcoming: TimelineItem[];
    completed: TimelineItem[];
  };
}

interface TimelineItem {
  id: string | number;
  title: string;
  description?: string;
  dueDate?: string;
  location?: string;
  completed?: boolean;
  type?: string;
  notes?: string;
  status?: string;
  createdAt?: string;
  eventType?: string;
  created_at?: string;
}

interface NotebookCategory {
  id: string;
  name: string;
  items?: TimelineItem[];
  buckets?: {
    upcoming: TimelineItem[];
    completed: TimelineItem[];
  };
}

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle,
  FileText,
  NotebookPen,
  Video,
  BellRing,
  DollarSign,
  Upload,
  Calendar,
  Link2,
  Utensils,
  Home,
  Laptop,
  Plane,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import StudentVirtualRoom, { type StudentVirtualRoomProps } from "@/components/StudentVirtualRoom";
// already imported above
import { OnlineStatusToggle } from "@/components/OnlineStatusToggle";
import { OnlineStatusDropdown } from "@/components/OnlineStatusDropdown";
import { IntegratedMessaging } from "@/components/IntegratedMessagingDB";
import { DigitalWalletPanel } from "@/components/DigitalWalletPanel";
import { GmailImportModal } from "@/components/GmailImportModal";
import { categorizeWalletDocument, extractIdentifiers } from "@/lib/walletCategorizer";
import { ChatRoom } from "@/components/ChatRoomsPanel";
import { GroupChatWindow } from "@/components/GroupChatWindow";
import { FeatureTour } from "@/components/FeatureTour";
import { SuggestionBoxButton } from "@/components/SuggestionBoxButton";
import { AdminNotifications } from "@/components/AdminNotifications";
import { NotificationPanel } from "@/components/NotificationPanel";
import FeatureTutorial from "@/components/FeatureTutorial";
import { TermsOfService, hasAcceptedTerms, acceptTerms } from "@/components/TermsOfServiceNew";
import { SharedFilesViewer } from "@/components/SharedFilesViewer";
import { AdvertisementBanner } from "@/components/AdvertisementBanner";
import { BetaWarningBanner } from "@/components/BetaWarningBanner";
import { AutoSaveIndicator } from "@/components/AutoSaveIndicator";
import TodoListPIP from "@/components/TodoListPIP";
import QuickAccessPanel from "@/components/QuickAccessPanel";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import OrganizerAIPanel from "@/components/OrganizerAIPanel";
import OrganizerScratchPadPanel from "@/components/OrganizerScratchPadPanel";
import CalendarImportModal from "@/components/CalendarImportModal";
import { NotificationManager } from "@/lib/notifications";
import { sendEmail } from "@/lib/email";
import * as MessagingAPI from "@/lib/messaging-api";
import EventAssignmentModal, { EventAssignment } from "@/components/EventAssignmentModal";
import { CalendarEvent } from "@/lib/icalParser";
import { useAutoSave } from "@/hooks/useAutoSave";
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, completeCalendarEvent, getCompletedCalendarEvents, migrateLocalStorageCalendar } from "@/lib/calendarRepository";
import { loadNotebooks, saveNotebooks, loadMeetingTranscripts, saveMeetingTranscripts, loadTimelineOverrides, saveTimelineOverrides, loadScheduledSessions, saveScheduledSessions } from "@/lib/organizerRepository";
import { migrateLocalStorageToDatabase, hasLocalStorageData, isMigrationCompleted, clearLocalStorageAfterMigration } from "@/lib/organizerMigration";
import AgeVerification, { useAgeRestrictions, AgeRestrictedFeature, type AgeRestrictions } from "@/components/AgeVerification";
import { AddClassModal } from "@/components/AddClassModal";
import { SuggestionBox } from "@/components/SuggestionBox";
import { QuickTooltip } from "@/components/QuickTooltip";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Target,
  BookOpen,
  Download,
  Printer,
  CheckSquare,
  Lock,
  RefreshCcw,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEntity } from "@/hooks/useEntity";
import { organizerItemEntityConfig } from "@/entities/OrganizerItem";
import { documentEntityConfig } from "@/entities/Document";
import { userEntityConfig } from "@/entities/User";
import LateNightSnackPanel, {
  type SnackSpot as SnackSpotRecord,
  type SnackSpotDraft,
} from "@/components/LateNightSnackPanel";
import { snackSpotEntityConfig } from "@/entities/SnackSpot";
import JournalPage from "@/Pages/JournalPage";
import AppsIntegrationPanel from "@/components/AppsIntegrationPanel";
import WhiptTranscriptsPanel from "@/components/WhiptTranscriptsPanel";
import SessionRecordingsPanel from "@/components/SessionRecordingsPanel";
import WhiptSummPanel from "@/components/WhiptSummPanel";
import CalendarView from "@/components/CalendarView";
import BudgetAssistant from "@/components/BudgetAssistant";
import { exportTranscriptAsJSON, exportTranscriptAsPDF, exportTranscriptAsText } from "@/services/webrtc/TranscriptionService";
import MusicPlayerDock from "@/components/MusicPlayerDock";
// MusicPlayerDock imported near top of file
import TabTemplateModal from "@/components/TabTemplateModal";
import DayCounterWidget from "@/components/DayCounterWidget";
import PTOSettingsEditor from "@/components/PTOSettingsEditor";
import ShoppingListForm from "@/components/ShoppingListForm";
import BillsTrackerForm from "@/components/BillsTrackerForm";
import MeetingForm from "@/components/MeetingForm";
import { printFilteredList, downloadFilteredList } from "@/lib/printExport";
import RecipeForm from "@/components/RecipeForm";
import HomeImprovementForm from "@/components/HomeImprovementForm";
import DeadlineForm from "@/components/DeadlineForm";
import PackingListForm from "@/components/PackingListForm";
import MaintenanceReminderForm from "@/components/MaintenanceReminderForm";
import ItineraryForm from "@/components/ItineraryForm";
import TravelBudgetForm from "@/components/TravelBudgetForm";
import PasswordProtectionModal from "@/components/PasswordProtectionModal";
import PasswordUnlockModal from "@/components/PasswordUnlockModal";
import { encryptFile, decryptFile } from "@/lib/encryption";

type OrganizerTone = "experiential" | "assignment" | "meeting";

type OrganizerItem = {
  id: number;
  title: string;
  tone: OrganizerTone;
  course?: string;
  instructor?: string;
  dueDate?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
};

type NotebookItem = {
  id: string;
  title: string;
  dueDate?: string;
  status: "upcoming" | "completed";
  description?: string;
  location?: string;
};

type NotebookDocument = {
  id: number;
  title: string;
  notebookId?: string;
  notebookTabId?: string;
  lastEditedAt?: string;
  accent: string;
};

type CategoryBlueprint = {
  id: string;
  label: string;
  icon: LucideIcon;
  accent: string;
  description?: string;
};

const STUDENT_CATEGORY_BLUEPRINTS: CategoryBlueprint[] = [
  // First Row - Academic
  { id: "discussion", label: "Discussion", icon: MessageCircle, accent: "text-cyan-200" },
  { id: "assignment", label: "Assignments", icon: FileText, accent: "text-amber-200" },
  { id: "experiential", label: "Experiential", icon: NotebookPen, accent: "text-emerald-200" },
  { id: "journal", label: "Journal & Logs", icon: BookOpen, accent: "text-purple-200" },
  { id: "video", label: "Social/Family", icon: Video, accent: "text-pink-200" },
  // Second Row - Life Organization
  { id: "home", label: "Home", icon: Home, accent: "text-rose-300" },
  { id: "work", label: "Work", icon: Laptop, accent: "text-indigo-300" },
  { id: "travel", label: "Travel", icon: Plane, accent: "text-sky-300" },
];

const PERSONAL_CATEGORY_BLUEPRINTS: CategoryBlueprint[] = [
  { id: "work", label: "Work", icon: FileText, accent: "text-blue-200" },
  { id: "home", label: "Home", icon: NotebookPen, accent: "text-green-200" },
  { id: "health", label: "Health & Fitness", icon: BellRing, accent: "text-red-200" },
  { id: "finance", label: "Finance", icon: DollarSign, accent: "text-emerald-200" },
  { id: "personal", label: "Personal Projects", icon: Upload, accent: "text-purple-200" },
  { id: "social", label: "Social & Family", icon: MessageCircle, accent: "text-pink-200" },
  { id: "snack", label: "Snack Dial", icon: Utensils, accent: "text-orange-200" },
];

const CATEGORY_BLUEPRINTS = STUDENT_CATEGORY_BLUEPRINTS;

// Quick Action Side Tabs Type
type QuickActionTab = {
  id: string;
  label: string;
  icon: string; // SVG path or emoji
  gradient: string;
  border: string;
  textColor: string;
  onClick: (context: any) => void;
  badge?: (context: any) => number | null;
};

// Quick Actions Side Tabs Array
const QUICK_ACTIONS_TABS: QuickActionTab[] = [
  {
    id: 'todo-list',
    label: 'To-Do List',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    gradient: 'from-purple-600/20 to-indigo-600/20',
    border: 'border-purple-400/30',
    textColor: 'text-purple-100',
    onClick: (ctx) => ctx.setShowTodoList?.(true),
  },
  {
    id: 'music',
    label: 'Music',
    icon: 'M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3',
    gradient: 'from-orange-600/20 to-amber-600/20',
    border: 'border-orange-400/30',
    textColor: 'text-orange-100',
    onClick: (ctx) => ctx.setShowMusicPlayer?.(true),
  },
  {
    id: 'messages',
    label: 'Messages',
    icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
    gradient: 'from-blue-600/20 to-cyan-600/20',
    border: 'border-blue-400/30',
    textColor: 'text-blue-100',
    onClick: (ctx) => {
      ctx.setIsMessagingMinimized?.(false);
      ctx.setShowMessaging?.(true);
    },
    badge: (ctx) => (ctx.unreadCount > 0 ? ctx.unreadCount : null),
  },
  {
    id: 'ai-assistant',
    label: 'AI Assistant',
    icon: 'AI',
    gradient: 'from-indigo-600/20 to-blue-600/20',
    border: 'border-indigo-400/30',
    textColor: 'text-indigo-100',
    onClick: (ctx) => ctx.setShowAIPanel?.(true), // Open AI panel directly, not QuickAccessPanel
  },
  {
    id: 'snack-dial',
    label: 'Snack Dial',
    icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
    gradient: 'from-pink-600/20 to-rose-900/20',
    border: 'border-pink-400/30',
    textColor: 'text-pink-100',
    onClick: (ctx) => ctx.setShowSnackPanel?.(true),
  },
  {
    id: 'apps',
    label: 'Apps',
    icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
    gradient: 'from-cyan-600/20 to-blue-600/20',
    border: 'border-cyan-400/30',
    textColor: 'text-cyan-100',
    onClick: (ctx) => ctx.setShowAppsPanel?.(true),
  },
  {
    id: 'budget',
    label: 'Budget',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    gradient: 'from-green-600/20 to-emerald-600/20',
    border: 'border-emerald-400/30',
    textColor: 'text-emerald-100',
    onClick: (ctx) => ctx.setShowBudgetAssistant?.(true),
  },
  {
    id: 'suggestions',
    label: 'Suggestions',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    gradient: 'from-emerald-600/20 via-pink-500/20 to-teal-600/20',
    border: 'border-emerald-400/40',
    textColor: 'text-emerald-100',
    onClick: (ctx) => ctx.setShowSuggestions?.(true),
  },
  {
    id: 'whipt-summ',
    label: 'SUMM!',
    icon: 'WHIPT',
    gradient: 'from-amber-600/20 to-orange-600/20',
    border: 'border-amber-400/30',
    textColor: 'text-amber-100',
    onClick: () => {}, // No-op - Coming Soon
    disabled: true,
  },
];

const CATEGORY_COLORS: Record<
  string,
  { bg: string; text: string; border: string; shadow: string }
> = {
  discussion: {
    bg: "bg-[#1d4ed8]",
    text: "text-white",
    border: "border-[#1d4ed8]/70",
    shadow: "shadow-[0_15px_35px_rgba(29,78,216,0.35)]",
  },
  assignment: {
    bg: "bg-[#facc15]",
    text: "text-slate-900",
    border: "border-[#facc15]/70",
    shadow: "shadow-[0_15px_35px_rgba(250,204,21,0.45)]",
  },
  experiential: {
    bg: "bg-[#059669]",
    text: "text-white",
    border: "border-[#059669]/70",
    shadow: "shadow-[0_15px_35px_rgba(5,150,105,0.4)]",
  },
  video: {
    bg: "bg-gradient-to-br from-[#ec4899] to-[#8b5cf6]",
    text: "text-white",
    border: "border-[#ec4899]/70",
    shadow: "shadow-[0_15px_35px_rgba(236,72,153,0.4)]",
  },
  snack: {
    bg: "bg-gradient-to-r from-[#f87171] to-[#f472b6]",
    text: "text-white",
    border: "border-[#f472b6]/70",
    shadow: "shadow-[0_15px_35px_rgba(244,114,182,0.45)]",
  },
};

type OrganizerUser = {
  id: number;
  name?: string;
};

type Semester = {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
  color: string;
};

type VirtualRoomProfile = Omit<StudentVirtualRoomProps, "onClose"> & {
  id: string;
};

const VIRTUAL_ROOM_PROFILES: Record<string, VirtualRoomProfile> = {
  discussion: {
    id: "discussion",
    tabLabel: "Discussion",
    roomTitle: "Discussion Session",
    description: "Host a text-friendly salon with live captions for peers who prefer written dialogue.",
    agenda: [],
    accessibilityNote: "Live captions + parallel chat stay pinned so hard-of-hearing students never miss the verbal thread.",
    participants: [
      { id: "1", name: "You", role: "Participant", initials: "YOU", online: true },
    ],
    resources: [],
  },
  assignment: {
    id: "assignment",
    tabLabel: "Assignment",
    roomTitle: "Assignment Session",
    description: "Sprint on drafts with screenshare and shared text-only check-ins.",
    agenda: [],
    accessibilityNote: "Everyone can mute audio and collaborate via captions + chat without leaving the room.",
    participants: [
      { id: "1", name: "You", role: "Participant", initials: "YOU", online: true },
    ],
    resources: [],
  },
  experiential: {
    id: "experiential",
    tabLabel: "Experiential",
    roomTitle: "Experiential Session",
    description: "Upload recordings, narrate observations, and capture captions for community partners.",
    agenda: [],
    accessibilityNote: "Voice, ASL, and typed updates all sync to the same record for later export.",
    participants: [
      { id: "1", name: "You", role: "Participant", initials: "YOU", online: true },
    ],
    resources: [],
  },
  journal: {
    id: "journal",
    tabLabel: "Journal & Logs",
    roomTitle: "Journal & Reflection Space",
    description: "Daily reflections, class notes, and personal logs. Your private space for thoughts and observations.",
    agenda: [],
    accessibilityNote: "Private journaling space with text, voice notes, and timestamps for easy reference.",
    participants: [
      { id: "1", name: "You", role: "Participant", initials: "YOU", online: true },
    ],
    resources: [],
  },
  video: {
    id: "video",
    tabLabel: "Social/Family Room",
    roomTitle: "Social/Family Room",
    description: "Video call with family and friends back home. Stay connected with loved ones while away at school.",
    agenda: [],
    accessibilityNote: "High-quality video with captions for clear communication. Perfect for staying in touch with home.",
    participants: [
      { id: "1", name: "You", role: "Participant", initials: "YOU", online: true },
    ],
    resources: [],
  },
};

const createNotebookCategories = (organizerMode: 'student' | 'personal' = 'student'): NotebookCategory[] => {
  const blueprints = organizerMode === 'personal' ? PERSONAL_CATEGORY_BLUEPRINTS : STUDENT_CATEGORY_BLUEPRINTS;
  return blueprints.map((blueprint) => {
    const category: any = {
      id: blueprint.id,
      label: blueprint.label,
      icon: blueprint.icon,
      accent: blueprint.accent,
      buckets: { upcoming: [], completed: [] },
    };
    
    // Add metadata for Work tabs with PTO/Sick day counters
    if (blueprint.id === 'work') {
      category.metadata = {
        counters: [
          { label: 'PTO Days', used: 0, total: 15, color: 'blue' },
          { label: 'Sick Days', used: 0, total: 10, color: 'red' }
        ]
      };
    }
    
    return category;
  });
};

const FALLBACK_ORGANIZER_SUMMARY = {
  course: "No Class Selected",
  instructor: "Click 'New Class' to get started",
  nextItemLabel: "No upcoming items",
  dueDisplay: "---",
  upcomingCount: 0,
};

const FALLBACK_ORGANIZER_CHIPS: { id: string; label: string; tone: OrganizerTone }[] = [
  { id: "chip-1", label: "Experiential Log #2", tone: "experiential" },
  { id: "chip-2", label: "Field Notes Review", tone: "meeting" },
  { id: "chip-3", label: "Assignment Draft", tone: "assignment" },
];

const normalizeOrganizerTone = (tone?: string): OrganizerTone => {
  if (tone === "experiential" || tone === "assignment" || tone === "meeting") {
    return tone;
  }
  return "assignment";
};

const formatOrganizerDueDisplay = (dueDate?: string): string => {
  if (!dueDate) return "Date TBD";
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) {
    return dueDate;
  }
  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  });
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
};

const FALLBACK_ITEMS: OrganizerItem[] = [
  {
    id: 1,
    title: "Experiential Log #2",
    tone: "experiential",
    course: "SOC 402",
    instructor: "Dr. Ellis",
    dueDate: "2026-01-07T23:59:00.000Z",
    notes: "Upload field notes + reflective commentary",
  },
  {
    id: 2,
    title: "Field Notes Review",
    tone: "meeting",
    course: "SOC 402",
    instructor: "Prof. Kline",
    dueDate: "2026-01-09T18:00:00.000Z",
    notes: "30-min sync with advisor to align findings",
  },
  {
    id: 3,
    title: "Assignment Draft",
    tone: "assignment",
    course: "SOC 402",
    instructor: "Dr. Ellis",
    dueDate: "2026-01-12T13:00:00.000Z",
    notes: "Draft intro + literature review",
  },
];
// Start with completely empty - no demo data
const DEFAULT_CLASS_NOTEBOOKS: ClassNotebook[] = [];

const sanitizeOptionalText = (value?: string | null) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const UPCOMING_BLOCKS = [
  {
    id: "transcripts",
    label: "Meeting Transcriptions",
    description: "AI-generated transcripts and notes from your virtual room sessions.",
    icon: BellRing,
    accent: "text-cyan-300",
  },
  {
    id: "documents",
    label: "Documents Hub",
    description: "All your uploaded files from Discussion, Assignment, and Experiential tabs in one place.",
    icon: NotebookPen,
    accent: "text-purple-300",
  },
  {
    id: "health",
    label: "Health & Fitness",
    description: "Sync steps, sleep, and vitals. Coming soon with device connections.",
    icon: BellRing,
    accent: "text-rose-200",
  },
  {
    id: "uploads",
    label: "Upload Center",
    description: "Quick upload PDFs, DOCX, and files to any tab. Drag & drop supported.",
    icon: Upload,
    accent: "text-fuchsia-300",
  },
  {
    id: "shared-files",
    label: "Shared Files",
    description: "View and manage files shared with you by collaborators.",
    icon: FileText,
    accent: "text-cyan-300",
  },
];
//#endregion

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const OrganizerPage: React.FC = () => {
  //#region STATE DECLARATIONS
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const healthTrackerEnabled = import.meta.env.VITE_FEATURE_HEALTH_TRACKER === 'true';
  const { items: organizerItems } = useEntity<OrganizerItem>(
    organizerItemEntityConfig
  );
  const { items: documents, create, reload, remove: deleteDocument } = useEntity<NotebookDocument>(documentEntityConfig);
  const { items: users } = useEntity<OrganizerUser>(userEntityConfig);
  const {
    items: snackSpots,
    loading: snackLoading,
    create: createSnackSpot,
    update: updateSnackSpot,
    remove: deleteSnackSpot,
  } = useEntity<SnackSpotRecord>(snackSpotEntityConfig);

  const activeUserId = users[0]?.id ?? 301;

  // Initialize empty - will load from database
  const [classNotebooks, setClassNotebooks] = useState<ClassNotebook[]>([]);
  const [activeClassId, setActiveClassId] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState("");
  const [activePhase, setActivePhase] = useState<'upcoming' | 'inProgress' | 'completed'>('upcoming');
  const [activeItemFilter, setActiveItemFilter] = useState<string>('all'); // Filter by item type
  const [showEverythingMode, setShowEverythingMode] = useState(false); // Show all categories or just active one
  const [timelineOverrides, setTimelineOverrides] = useState<Record<string, Record<string, any[]>>>({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [eventType, setEventType] = useState<'assignment' | 'meeting' | 'reminder' | 'event'>('assignment');
  const [meetingType, setMeetingType] = useState<'in-person' | 'virtual'>('in-person');
  const [selectedCategoryForForm, setSelectedCategoryForForm] = useState<string>('');
  const [selectedClassForForm, setSelectedClassForForm] = useState<string>('');
  const [prefilledDate, setPrefilledDate] = useState<string | null>(null);
  const defaultScheduleTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [scheduleForRoom, setScheduleForRoom] = useState<string | null>(null);
  const [scheduledMeetingsRefresh, setScheduledMeetingsRefresh] = useState(0);
  const [scheduledSessions, setScheduledSessions] = useState<any[]>([]);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [agendaForRoom, setAgendaForRoom] = useState<string | null>(null);
  const [roomAgendas, setRoomAgendas] = useState<Record<string, string>>({});
  const [showLinkCopiedModal, setShowLinkCopiedModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');
  const [copiedRoomName, setCopiedRoomName] = useState('');
  const [showDocumentsPanel, setShowDocumentsPanel] = useState(false);
  const [showSnackPanel, setShowSnackPanel] = useState(false);
  const [deleteDocConfirm, setDeleteDocConfirm] = useState<string | null>(null);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [showTranscriptsPanel, setShowTranscriptsPanel] = useState(false);
  const [showRecordingsPanel, setShowRecordingsPanel] = useState(false);
  const [recordingsPanelMinimized, setRecordingsPanelMinimized] = useState(false);
  const [showVirtualRoomsPanel, setShowVirtualRoomsPanel] = useState(false);
  const [transcriptsPanelMinimized, setTranscriptsPanelMinimized] = useState(false);
  const [showCalendarPanel, setShowCalendarPanel] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [showDayEventsPopup, setShowDayEventsPopup] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<any[]>([]);
  const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);
  const [calendarFilters, setCalendarFilters] = useState({ meetings: true, assignments: true, completed: true });
  const [showJournalPanel, setShowJournalPanel] = useState(false);
  const [showAppsPanel, setShowAppsPanel] = useState(false);
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  
  // Z-index management - whichever panel is clicked gets highest z-index
  const [topZIndex, setTopZIndex] = useState(10000);
  const [panelZIndexes, setPanelZIndexes] = useState({
    quickTabs: 1000,
    musicPlayer: 9900,
    todoList: 9950,
    messaging: 9800,
    aiPanel: 9700,
    snackPanel: 9600,
    appsPanel: 9500,
    budgetAssistant: 9400,
    scratchPad: 9300,
  });
  
  const bringToFront = (panelName: keyof typeof panelZIndexes) => {
    const newTopZ = topZIndex + 1;
    setTopZIndex(newTopZ);
    setPanelZIndexes(prev => ({ ...prev, [panelName]: newTopZ }));
  };
  const [musicPlayerSignal, setMusicPlayerSignal] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showTabTemplateModal, setShowTabTemplateModal] = useState(false);
  const [showBudgetAssistant, setShowBudgetAssistant] = useState(false);
  const [showDigitalWallet, setShowDigitalWallet] = useState(false);
  const [showGmailImport, setShowGmailImport] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
  const [showQuickAddMenu, setShowQuickAddMenu] = useState(false);
  const [calendarTypeLocked, setCalendarTypeLocked] = useState(false);
  
  // NEW: Overlay panel state for focused tab view
  const [showTabOverlay, setShowTabOverlay] = useState(false);
  const [overlayTabData, setOverlayTabData] = useState<{
    type: 'discussion' | 'assignment' | 'experiential' | null;
    className: string;
    classId: string;
    categoryId: string;
  }>({ type: null, className: '', classId: '', categoryId: '' });
  const [quickAddMenuPosition, setQuickAddMenuPosition] = useState({ x: 0, y: 0 });
  
  // Password protection state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [protectWithPassword, setProtectWithPassword] = useState(false);
  const [unlockingDocId, setUnlockingDocId] = useState<number | null>(null);
  
  // Helper function to get icon based on event type
  const getEventTypeIcon = (eventType?: string) => {
    switch(eventType) {
      case 'meeting': return '📹'; // Camera for meetings
      case 'reminder': return '⏰'; // Clock for reminders
      case 'event': return '📅'; // Calendar for events
      case 'assignment': return '📝'; // Book for assignments
      default: return '📝'; // Default to book
    }
  };

  const normalizeCalendarEventType = (value?: string): 'assignment' | 'meeting' | 'reminder' | 'event' | null => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'assignment' || normalized === 'meeting' || normalized === 'reminder' || normalized === 'event') {
      return normalized;
    }
    if (normalized === 'calendar-event') return 'event';
    return null;
  };

  const openCalendarFormForType = (
    type: 'assignment' | 'meeting' | 'reminder' | 'event',
    date?: string,
    lockType: boolean = true
  ) => {
    setEventType(type);
    setCalendarTypeLocked(lockType);
    setPrefilledDate(date || null);
    setCalendarOpen(true);
  };

  const sortItemsByDueClosestNow = <T extends { dueDate?: string }>(items: T[]) => {
    const now = Date.now();
    return [...items].sort((firstItem, secondItem) => {
      const firstTime = firstItem.dueDate ? new Date(firstItem.dueDate).getTime() : Number.POSITIVE_INFINITY;
      const secondTime = secondItem.dueDate ? new Date(secondItem.dueDate).getTime() : Number.POSITIVE_INFINITY;

      const firstDistance = Number.isFinite(firstTime) ? Math.abs(firstTime - now) : Number.POSITIVE_INFINITY;
      const secondDistance = Number.isFinite(secondTime) ? Math.abs(secondTime - now) : Number.POSITIVE_INFINITY;

      return firstDistance - secondDistance;
    });
  };

  const openItemEditor = (item: any, classId: string, categoryId: string) => {
    setEditingItemId(String(item.id));
    setEditingItemContext({ classId, categoryId });
    setEditFormData({
      title: item.title || '',
      description: item.description || item.notes || '',
      dueDate: item.dueDate || '',
      location: item.location || '',
    });
  };

  const sanitizeItemDescription = (value?: string) => {
    if (!value) return '';

    return value
      .replace(/\[[^\]]*\]\((https?:\/\/[^)]+)\)/gi, ' ')
      .replace(/\[\]\((https?:\/\/[^)]+)\)/gi, ' ')
      .replace(/https?:\/\/\S+/gi, ' ')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join(' ')
      .trim();
  };

  // Calendar navigation functions
  const goToPreviousMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCalendarDate(new Date());
  };

  const getMonthYearString = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`;
  };
  const [unlockingDocTitle, setUnlockingDocTitle] = useState('');

  // Effect to trigger music player when quick tab is clicked
  useEffect(() => {
    if (showMusicPlayer) {
      setMusicPlayerSignal(prev => prev + 1);
      setShowMusicPlayer(false); // Reset so it can be triggered again
    }
  }, [showMusicPlayer]);
  const [showWhiptSumm, setShowWhiptSumm] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [categoryExpanded, setCategoryExpanded] = useState<Record<string, boolean>>({});
  const [selectedDateEvents, setSelectedDateEvents] = useState<{date: string, events: any[]} | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showCalendarImport, setShowCalendarImport] = useState(false);
  const [importedEvents, setImportedEvents] = useState<CalendarEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]); // All calendar events from database
  const [showEventAssignment, setShowEventAssignment] = useState(false);
  const [uploadTabSelection, setUploadTabSelection] = useState<string>('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemContext, setEditingItemContext] = useState<{ classId: string; categoryId: string } | null>(null);
  const [openMoveMenuId, setOpenMoveMenuId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<{
    title: string;
    description: string;
    dueDate: string;
    location: string;
  }>({
    title: '',
    description: '',
    dueDate: '',
    location: ''
  });
  const [meetingTranscripts, setMeetingTranscripts] = useState<MeetingTranscript[]>([]);
  const [roomNotes, setRoomNotes] = useState<string>(''); // For capturing room session content
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderFormData, setReminderFormData] = useState({ title: '', description: '', dueDate: '' });
  const documentsHubRef = useRef<HTMLButtonElement>(null);
  const [showDownloadNotification, setShowDownloadNotification] = useState(false);
  const [showHealthPanel, setShowHealthPanel] = useState(false);
  
  // Chat state
  const [showMessaging, setShowMessaging] = useState(false);
  const [isMessagingMinimized, setIsMessagingMinimized] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [isRoomChatMinimized, setIsRoomChatMinimized] = useState(false);
  // DISABLED: Terms of Service - skip straight to organizer
  const [showTerms, setShowTerms] = useState(false); // Always false now
  const [showSharedFiles, setShowSharedFiles] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [ageRestrictions, setAgeRestrictions] = useState<AgeRestrictions | null>(null);
  const [unreadMessages, setUnreadMessages] = useState<{[userId: number]: number}>({});
  const [adminUnreadMessages, setAdminUnreadMessages] = useState<number>(0);
  const [mobileHeaderExpanded, setMobileHeaderExpanded] = useState(false);
  const [showMobileMusic, setShowMobileMusic] = useState(false);
  const [musicOpenSignal, setMusicOpenSignal] = useState(0);
  const [showMobileAds, setShowMobileAds] = useState(false);
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
  const [processBarSpacer, setProcessBarSpacer] = useState(true);
  const [mobileTabsVisible, setMobileTabsVisible] = useState(false); // Mobile-only tab reveal
  const tabSwipeRef = useRef<{ x: number; y: number } | null>(null);
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteRoomId, setInviteRoomId] = useState<string | null>(null);
  
  // PIP Widget & Quick Access Panel
  const [showQuickAccessPanel, setShowQuickAccessPanel] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showScratchPadPanel, setShowScratchPadPanel] = useState(false);
  const [showTodoList, setShowTodoList] = useState(false); // Desktop todo list visibility
  const [showArchivedClasses, setShowArchivedClasses] = useState(false); // Show/hide archived classes
  const [inviteEmails, setInviteEmails] = useState<string>('');
  const [inviteMessage, setInviteMessage] = useState('');
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

  // Swipe handle for opening Quick Access without hitting macOS edge gesture
  const onQuickAccessSwipeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    const { clientX, clientY } = e;
    const edgeGuard = 12;
    if (window.innerWidth - clientX < edgeGuard) return; // Ignore true edge to avoid OS gesture
    swipeStartRef.current = { x: clientX, y: clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onQuickAccessSwipeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!swipeStartRef.current) return;
    const dx = swipeStartRef.current.x - e.clientX; // right-to-left drag is positive
    const dy = Math.abs(swipeStartRef.current.y - e.clientY);
    if (dy > 50) return; // vertical motion cancels
    if (dx > 50) {
      setShowQuickAccessPanel(true);
      swipeStartRef.current = null;
    }
  };

  const onQuickAccessSwipeEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    swipeStartRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Swipe down near top to reveal tabs on mobile
  const onTabSwipeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    tabSwipeRef.current = { x: e.clientX, y: e.clientY };
  };

  const onTabSwipeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!tabSwipeRef.current) return;
    const dy = e.clientY - tabSwipeRef.current.y;
    const dx = Math.abs(e.clientX - tabSwipeRef.current.x);
    if (dx > 40) return; // ignore diagonal
    if (dy > 40) {
      setMobileTabsVisible(true);
      tabSwipeRef.current = null;
    }
  };

  const onTabSwipeEnd = () => {
    tabSwipeRef.current = null;
  };

  // Archive state - show items older than 3 months
  const [showArchivedItems, setShowArchivedItems] = useState(false);

  // Virtual room state
  const [virtualRoomOpen, setVirtualRoomOpen] = useState(false);
  const [virtualRoomMinimized, setVirtualRoomMinimized] = useState(false);
  const [virtualRoomContext, setVirtualRoomContext] = useState<any>(null);
  const inviteDeepLinkHandled = useRef(false);
  const guestInviteMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get('room');
    const autoJoin = params.has('autojoin') || params.get('join') === '1';
    return !!roomId && autoJoin;
  }, []);

  // Track if we're currently loading from database to prevent auto-save during load
  const isLoadingFromDatabase = useRef(false);

  // Clear demo snack spots on initial load
  useEffect(() => {
    const clearDemoSnacks = async () => {
      const hasCleared = sessionStorage.getItem('demo_snacks_cleared_v2');
      if (hasCleared) return;
      
      // Delete all existing snack spots (remove any demo or test data)
      for (const spot of snackSpots) {
        try {
          await deleteSnackSpot(spot.id);
        } catch (err) {
          console.error('Failed to delete snack spot:', err);
        }
      }
      
      sessionStorage.setItem('demo_snacks_cleared_v2', 'true');
    };
    
    if (snackSpots.length > 0 && !sessionStorage.getItem('demo_snacks_cleared_v2')) {
      clearDemoSnacks();
    }
  }, []); // Only run once on mount

  // Check age verification on mount
  useEffect(() => {
    const stored = localStorage.getItem('whipt_age_verification');
    if (stored) {
      try {
        setAgeRestrictions(JSON.parse(stored));
      } catch {
        setShowAgeVerification(true);
      }
    } else {
      setShowAgeVerification(true);
    }
  }, []);

  // Flash tab title when there are unread messages or a near-term due date while unfocused
  useEffect(() => {
    const originalTitle = document.title;
    let interval: any;

    const totalUnread = Object.values(unreadMessages).reduce((a, b) => a + b, 0) + adminUnreadMessages;

    const soonestDue = (() => {
      const all: Date[] = [];
      classNotebooks.forEach((nb) => {
        nb.categories.forEach((cat) => {
          const items = cat.buckets?.upcoming || [];
          items.forEach((item) => {
            if (item.dueDate) {
              const d = new Date(item.dueDate);
              if (!isNaN(d.getTime())) all.push(d);
            }
          });
        });
      });
      all.sort((a, b) => a.getTime() - b.getTime());
      return all[0];
    })();

    const hasDueSoon = soonestDue ? soonestDue.getTime() - Date.now() <= 24 * 60 * 60 * 1000 && soonestDue.getTime() > Date.now() : false;

    if ((totalUnread > 0 || hasDueSoon) && !document.hasFocus()) {
      let flag = false;
      interval = setInterval(() => {
        flag = !flag;
        document.title = flag
          ? totalUnread > 0
            ? `🔔 ${totalUnread} new message${totalUnread === 1 ? '' : 's'}`
            : '📅 Due soon'
          : originalTitle;
      }, 1200);
    } else {
      document.title = originalTitle;
    }

    const handleFocus = () => {
      document.title = originalTitle;
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.title = originalTitle;
      if (interval) clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [unreadMessages, adminUnreadMessages, classNotebooks]);

  // Load calendar events from database on mount
  // Get Supabase session for Gmail access
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
    };
    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // REFRESH DATA FUNCTION - Force reload from database
  const refreshDataFromDatabase = useCallback(async () => {
    if (!user?.id) {
      alert('⚠️ No user found. Please log in again.');
      return;
    }
    try {
      // Clear local state first
      setClassNotebooks([]);
      setMeetingTranscripts([]);
      setScheduledSessions([]);
      
      // Reload notebooks
      const notebooks = await loadNotebooks(user.id);
      if (notebooks.length > 0) {
        const restoredNotebooks = notebooks.map((nb: ClassNotebook) => {
          const baseCategories = createNotebookCategories('student');
          const idMap: Record<string, string> = {
            assignments: 'assignment',
            assignment: 'assignment',
            discussion: 'discussion',
            experiential: 'experiential',
          };
          
          (nb.categories || []).forEach((cat) => {
            const targetId = idMap[cat.id] || null;
            if (!targetId) return;
            const target = baseCategories.find((c) => c.id === targetId);
            if (!target) return;
            
            const buckets = cat.buckets || { upcoming: [], completed: [] };
            target.buckets = {
              upcoming: [...(target.buckets?.upcoming || []), ...(buckets.upcoming || [])],
              completed: [...(target.buckets?.completed || []), ...(buckets.completed || [])],
            } as any;
          });
          
          const categories = baseCategories.map((cat) => {
            const blueprint = CATEGORY_BLUEPRINTS.find((b) => b.id === cat.id);
            return { ...cat, icon: blueprint?.icon || NotebookPen };
          });
          
          return { ...nb, categories };
        });
        
        setClassNotebooks(restoredNotebooks);
        // DEBUG: Log what's in each category
        restoredNotebooks.forEach((nb, i) => {
          nb.categories.forEach(cat => {
            const upcomingCount = cat.buckets?.upcoming?.length || 0;
            const completedCount = cat.buckets?.completed?.length || 0;
            if (upcomingCount > 0) {
              console.log(`    First 3 items:`, cat.buckets.upcoming.slice(0, 3).map(item => item.title));
            }
          });
        });
        
        if (!activeClassId && restoredNotebooks.length > 0) {
          setActiveClassId(restoredNotebooks[0].id);
        }
        
        // Auto-expand all categories so items are visible
        const expandAll: Record<string, boolean> = {};
        restoredNotebooks.forEach(nb => {
          nb.categories.forEach(cat => {
            expandAll[cat.id] = true;
          });
        });
        setCategoryExpanded(expandAll);
        console.log('[DEBUG] Auto-expanded all categories:', Object.keys(expandAll));
      }
      
      // Reload transcripts
      const transcripts = await loadMeetingTranscripts(user.id);
      setMeetingTranscripts(transcripts);
      // Reload sessions
      const sessions = await loadScheduledSessions(user.id);
      const normalized = normalizeSessions(sessions);
      setScheduledSessions(normalized);
      // Reload calendar events
      const events = await getCalendarEvents(user.id);
      alert('✅ Data refreshed successfully! All your calendar items have been reloaded from the database.');
      
    } catch (error) {
      console.error('[OrganizerDB] ❌ Error refreshing data:', error);
      alert('❌ Failed to refresh data. Please try again or contact support.');
    }
  }, [user?.id, activeClassId]);

  useEffect(() => {
    const loadCalendarFromDatabase = async () => {
      if (!user?.id) return;

      // Allow forcing a clean student layout, bypassing DB data (set localStorage 'whipt_force_student_defaults' = 'true' or add ?forceStudentDefaults=1)
      const forceStudentDefaults =
        localStorage.getItem('whipt_force_student_defaults') === 'true' ||
        window.location.search.includes('forceStudentDefaults=1');

      try {
        // Check if we need to migrate localStorage data
        const hasLocalData = localStorage.getItem('whipt_class_notebooks');
        const hasMigrated = localStorage.getItem('whipt_calendar_migrated');
        
        if (hasLocalData && !hasMigrated) {
          const count = await migrateLocalStorageCalendar(user.id);
          localStorage.setItem('whipt_calendar_migrated', 'true');
        }
        
        // MIGRATION DISABLED - Using database only, no localStorage migration
        // Set flag to prevent auto-save during load
        isLoadingFromDatabase.current = true;
        
        // If forcing clean student defaults, skip DB notebooks entirely
        if (forceStudentDefaults) {
          const defaults = createNotebookCategories('student');
          const defaultNotebook: ClassNotebook = {
            id: 'class-1',
            name: 'Class 1',
            categories: defaults,
            color: '#1d4ed8',
          };
          setClassNotebooks([defaultNotebook]);
          setActiveClassId('class-1');
          // Don't set activeCategoryId - leave empty so all tabs show
        } else {
          // Load all organizer data from database
          const notebooks = await loadNotebooks(user.id);
          if (notebooks.length > 0) {
            // Restore icons from blueprints
            const idMap: Record<string, string> = {
              assignments: 'assignment',
              assignment: 'assignment',
              tasks: 'assignment',
              goals: 'assignment',
              notes: 'discussion',
              journal: 'discussion',
              discussion: 'discussion',
              experiential: 'experiential',
              video: 'video',
              work: 'work',       // Preserve work items from mobile
              home: 'home',       // Preserve home items from mobile
              travel: 'travel',   // Preserve travel items from mobile
            };

            const restoredNotebooks = notebooks.map((nb: ClassNotebook) => {
              // Start with clean student categories and merge legacy items into the nearest match
              const baseCategories = createNotebookCategories('student');

              (nb.categories || []).forEach((cat) => {
                const targetId = idMap[cat.id] || null;
                if (!targetId) return;
                const target = baseCategories.find((c) => c.id === targetId);
                if (!target) return;

                // Normalize buckets/items
                const buckets = cat.buckets || { upcoming: [], completed: [] };
                const upcoming = buckets.upcoming || [];
                const completed = buckets.completed || [];

                // Some legacy categories store items array instead of buckets; push them into upcoming
                const legacyItems = Array.isArray((cat as any).items) ? (cat as any).items : [];

                target.buckets = {
                  upcoming: [...(target.buckets?.upcoming || []), ...upcoming, ...legacyItems],
                  completed: [...(target.buckets?.completed || []), ...completed],
                } as any;
              });

              // Restore icons
              const categories = baseCategories.map((cat) => {
                const blueprint = CATEGORY_BLUEPRINTS.find((b) => b.id === cat.id);
                return { ...cat, icon: blueprint?.icon || NotebookPen };
              });

              // Map mobile 'professor' field to desktop 'instructor' field
              return {
                ...nb,
                categories,
                instructor: nb.instructor || (nb as any).professor || nb.instructor
              };
            });
            setClassNotebooks(restoredNotebooks);
            // Set active class but NOT category (so all tabs show by default)
            // Filter out archived classes when setting initial active class
            if (!activeClassId && restoredNotebooks.length > 0) {
              const firstActiveClass = restoredNotebooks.find(nb => !nb.archived) || restoredNotebooks[0];
              setActiveClassId(firstActiveClass.id);
              // Don't set activeCategoryId - leave it empty so all tabs are visible
            }
          }
        }
        
        const transcripts = await loadMeetingTranscripts(user.id);
        if (transcripts.length > 0) {
          setMeetingTranscripts(transcripts);
        }
        
        const overrides = await loadTimelineOverrides(user.id);
        setTimelineOverrides(overrides);
        // Load scheduled sessions from database
        const sessions = await loadScheduledSessions(user.id);
        if (sessions.length > 0) {
          const normalized = normalizeSessions(sessions);
          setScheduledSessions(normalized);
          if (normalized.some((s, idx) => getSessionKey(s, idx) !== (sessions[idx]?.sessionId || sessions[idx]?.id))) {
            // persist normalized IDs to DB for consistency
            await saveScheduledSessions(user.id, normalized);
          }
        } else {
          // If database is empty, check localStorage and migrate
          const localSessions = JSON.parse(localStorage.getItem('scheduledSessions') || '[]');
          if (localSessions.length > 0) {
            const normalized = normalizeSessions(localSessions);
            setScheduledSessions(normalized);
            await saveScheduledSessions(user.id, normalized);
          }
        }
        
        // Load events from database
        const events = await getCalendarEvents(user.id);
        setCalendarEvents(events); // Store in state for CalendarView
        console.log(`[Desktop] Loaded ${events.length} calendar events from database`);
        
        // Convert database events back to ClassNotebook structure for compatibility
        // Skip overwrite when forcing student defaults; when off, merge events into student tabs
        if (!forceStudentDefaults && events.length > 0) {
          // Group events by course
          const courseMap = new Map<string, any[]>();
          events.forEach(event => {
            const courseName = event.course || 'General';
            if (!courseMap.has(courseName)) {
              courseMap.set(courseName, []);
            }
            courseMap.get(courseName)!.push({
              id: parseInt(event.id.split('_')[1] || '0'),
              title: event.title,
              dueDate: event.due_date,
              status: event.status || 'pending',
              notes: event.notes || event.description || ''
            });
          });

          // Merge events into existing notebooks without removing student tabs
          setClassNotebooks((prev) => {
            const updated = [...prev];

            const ensureAssignmentCategory = (notebook: ClassNotebook) => {
              const existing = notebook.categories.find((c) => c.id === 'assignment');
              if (existing) return existing;
              const defaults = createNotebookCategories('student');
              const assignCat = defaults.find((c) => c.id === 'assignment');
              if (assignCat) {
                notebook.categories.push({ ...assignCat, buckets: { upcoming: [], completed: [] } as any });
                return notebook.categories[notebook.categories.length - 1];
              }
              return null;
            };

            Array.from(courseMap.entries()).forEach(([courseName, items], idx) => {
              let nb = updated.find((n) => n.course === courseName || n.title === courseName);
              if (!nb) {
                nb = {
                  id: `course-${idx}-${Date.now()}`,
                  title: courseName,
                  course: courseName,
                  instructor: '',
                  categories: createNotebookCategories('student'),
                } as ClassNotebook;
                updated.push(nb);
              }

              const assignmentCat = ensureAssignmentCategory(nb);
              if (!assignmentCat) return;

              const buckets = assignmentCat.buckets || { upcoming: [], completed: [] } as any;
              assignmentCat.buckets = {
                upcoming: [...(buckets.upcoming || []), ...items],
                completed: buckets.completed || [],
              } as any;
            });

            // Restore icons for safety
            return updated.map((nb) => ({
              ...nb,
              categories: nb.categories.map((cat) => {
                const blueprint = CATEGORY_BLUEPRINTS.find((b) => b.id === cat.id);
                return { ...cat, icon: blueprint?.icon || NotebookPen };
              }),
            }));
          });
        }
        
        // Clear loading flag to allow saves from user actions
        setTimeout(() => {
          isLoadingFromDatabase.current = false;
        }, 1000); // Wait 1 second after load to prevent race conditions
        
      } catch (error) {
        console.error('[Calendar] Failed to load from database:', error);
        isLoadingFromDatabase.current = false; // Clear flag even on error
      }
    };
    
    loadCalendarFromDatabase();
  }, [user?.id]);

  // Auto-refresh notebooks when window gains focus (for desktop/mobile sync)
  useEffect(() => {
    const handleFocus = async () => {
      if (user?.id) {
        try {
          const notebooks = await loadNotebooks(user.id);
          if (notebooks.length > 0) {
            const restoredNotebooks = notebooks.map((nb: ClassNotebook) => {
              const baseCategories = createNotebookCategories('student');
              const idMap: Record<string, string> = {
                assignments: 'assignment',
                assignment: 'assignment',
                discussion: 'discussion',
                experiential: 'experiential',
              };
              
              (nb.categories || []).forEach((cat) => {
                const targetId = idMap[cat.id] || null;
                if (!targetId) return;
                const target = baseCategories.find((c) => c.id === targetId);
                if (!target) return;
                
                const buckets = cat.buckets || { upcoming: [], completed: [] };
                target.buckets = {
                  upcoming: [...(target.buckets?.upcoming || []), ...(buckets.upcoming || [])],
                  completed: [...(target.buckets?.completed || []), ...(buckets.completed || [])],
                } as any;
              });
              
              const categories = baseCategories.map((cat) => {
                const blueprint = CATEGORY_BLUEPRINTS.find((b) => b.id === cat.id);
                return { ...cat, icon: blueprint?.icon || NotebookPen };
              });
              
              // Map mobile 'professor' field to desktop 'instructor' field
              return { 
                ...nb, 
                categories,
                instructor: nb.instructor || (nb as any).professor || nb.instructor
              };
            });
            setClassNotebooks(restoredNotebooks);
            
            // If current active class is archived, switch to first non-archived class
            const currentClass = restoredNotebooks.find(nb => nb.id === activeClassId);
            if (currentClass?.archived) {
              const firstActiveClass = restoredNotebooks.find(nb => !nb.archived);
              if (firstActiveClass) {
                setActiveClassId(firstActiveClass.id);
              }
            }
            
            console.log('🔄 Synced notebooks on focus (desktop)');
          }

          // Also reload calendar events
          try {
            const events = await getCalendarEvents(user.id);
            if (events.length > 0) {
              setCalendarEvents(events);
              console.log('🔄 Synced calendar events on focus (desktop)');
            }
          } catch (calErr) {
            console.warn('Could not load calendar events on focus:', calErr);
          }

          // Also reload scheduled sessions
          try {
            const sessions = await loadScheduledSessions(user.id);
            if (sessions.length > 0) {
              setScheduledSessions(sessions);
              console.log('🔄 Synced scheduled sessions on focus (desktop)');
            }
          } catch (sessErr) {
            console.warn('Could not load scheduled sessions on focus:', sessErr);
          }
        } catch (error) {
          console.error('Error syncing notebooks:', error);
        }
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id]);

  // Auto-save calendar to database
  const { triggerSave: triggerCalendarSave, status: autoSaveStatus } = useAutoSave({
    onSave: async () => {
      if (!user?.id) return;
      // Save all assignments from notebooks to database
      for (const notebook of classNotebooks) {
        for (const category of notebook.categories) {
          const items = category.buckets?.upcoming || [];
          for (const item of items) {
            // Sync items with due dates OR meeting notes (video category)
            if (item.dueDate || category.id === 'video') {
              const eventId = `event_${item.id}_${notebook.id}`;
              
              try {
                // Check if event exists, update or create
                await updateCalendarEvent(eventId, {
                  title: item.title,
                  description: item.notes,
                  due_date: item.dueDate || item.createdAt || new Date().toISOString(),
                  course: notebook.course || notebook.title,
                  status: item.status,
                  category: category.name,
                  notes: item.notes
                }).catch(async () => {
                  // If update fails, event doesn't exist - create it
                  await createCalendarEvent({
                    user_id: user.id,
                    title: item.title,
                    description: item.notes,
                    due_date: item.dueDate || item.createdAt || new Date().toISOString(),
                    course: notebook.course || notebook.title,
                    status: item.status,
                    category: category.name,
                    notes: item.notes
                  });
                });
              } catch (error) {
                console.error('[Calendar] Failed to save event:', item.title, error);
              }
            }
          }
        }
      }
    },
    delay: 30000, // 30 seconds
    enabled: !!user?.id
  });

  // Trigger auto-save when classNotebooks change
  useEffect(() => {
    if (classNotebooks.length > 0) {
      triggerCalendarSave();
    }
  }, [classNotebooks, triggerCalendarSave]);

  // Persist classNotebooks to database
  useEffect(() => {
    const saveToDatabase = async () => {
      // Don't save if we're currently loading from database
      if (isLoadingFromDatabase.current) {
        return;
      }
      
      if (classNotebooks.length > 0 && user?.id) {
        try {
          await saveNotebooks(user.id, classNotebooks);
        } catch (error) {
          console.error('[OrganizerDB] Failed to save notebooks:', error);
        }
      }
    };
    
    saveToDatabase();
  }, [classNotebooks, user?.id]);

  // Persist scheduledSessions to database and localStorage
  useEffect(() => {
    const saveSessionsToDatabase = async () => {
      if (isLoadingFromDatabase.current) {
        return;
      }

      // Always mirror to localStorage to keep schedule consistent across tabs
      localStorage.setItem('scheduledSessions', JSON.stringify(scheduledSessions));

      if (user?.id) {
        try {
          await saveScheduledSessions(user.id, scheduledSessions);
        } catch (error) {
          console.error('[OrganizerDB] Failed to save scheduled sessions:', error);
        }
      }
    };
    
    saveSessionsToDatabase();
  }, [scheduledSessions, user?.id]);

  // Poll for new messages and update unread count
  useEffect(() => {
    const checkForNewMessages = async () => {
      const currentUserData = localStorage.getItem('whipt_user');
      if (!currentUserData) return;

      const currentUser = JSON.parse(currentUserData);
      
      // Always use epoch time to catch ALL unread messages
      // The "mark as read" effect will clear counts when messaging panel opens
      const lastCheckTime = new Date(0);

      const debugNotifications = localStorage.getItem('whipt_debug_notifications') === 'true';

      // Check all message threads
      const allKeys = Object.keys(localStorage);
      const messageKeys = forceDbNotifications ? [] : allKeys.filter(key => key.startsWith('whipt_messages_'));

      // IDs in storage are strings (may be UUIDs); compare as strings to avoid NaN mismatches
      let newUnreadCounts: {[userId: string]: number} = {};

      messageKeys.forEach(key => {
        try {
          const messages = JSON.parse(localStorage.getItem(key) || '[]');
          const userIds = key.replace('whipt_messages_', '').split('_');
          const currentId = String(currentUser.id);
          const otherUserId = userIds.find(id => id !== currentId);

          if (otherUserId) {
            // Count ALL unread messages from other user
            const unreadCount = messages.filter((msg: any) => 
              String(msg.from_user_id) === otherUserId && 
              new Date(msg.timestamp) > lastCheckTime &&
              String(msg.from_user_id) !== currentId
            ).length;

            if (unreadCount > 0) {
              newUnreadCounts[otherUserId] = unreadCount;
            }
          }
        } catch (error) {
          console.error('Error checking messages:', error);
        }
      });

      // DB fallback: if no localStorage threads (DB-only mode), query unread by sender from messaging API
      if (Object.keys(newUnreadCounts).length === 0) {
        try {
          const dbUnreadBySender = await MessagingAPI.getUnreadCountsBySender(String(currentUser.id));
          const totalDbUnread = Object.values(dbUnreadBySender).reduce((a, b) => a + b, 0);
          if (totalDbUnread > 0) {
            newUnreadCounts = dbUnreadBySender;
          }
          if (debugNotifications) {
          }
        } catch (err) {
          console.error('Error checking DB unread messages:', err);
        }
      }

      setUnreadMessages(newUnreadCounts);

      // Show notification if there are new messages and messaging panel is closed
      const totalUnread = Object.values(newUnreadCounts).reduce((a, b) => a + b, 0);

       // Debug log to inspect unread state when needed
      if (debugNotifications) {
      }

      if (totalUnread > 0 && !showMessaging) {
        // Show desktop notification using NotificationManager
        NotificationManager.show('message', {
          title: 'New Message',
          body: `You have ${totalUnread} new message${totalUnread > 1 ? 's' : ''}`,
          icon: '/pronoia-wellness-hero2.webp',
          data: { totalUnread }
        });
      }
    };

    // Check immediately
    checkForNewMessages();

    // Poll every 3 seconds
    const interval = setInterval(checkForNewMessages, 3000);

    return () => clearInterval(interval);
  }, [showMessaging]);

  const debugNotifications = false;
  const forceDbNotifications = false;

  // Debug logging removed

  // Mark messages as read for the active thread when messaging panel is open
  useEffect(() => {
    const markActiveThreadRead = async () => {
      if (!showMessaging || !user?.id) return;

      // If an active conversation is open inside the messaging panel, mark it read in DB
      // (IntegratedMessagingDB handles localStorage threads internally). We ensure badge is not wiped globally.
      try {
        const panelState = (window as any).__whiptMessagingState;
        const activeUserId = panelState?.activeUserId as string | undefined;
        const activeGroupId = panelState?.activeGroupId as string | undefined;

        // Only handle 1:1 unread counts here
        if (activeUserId) {
          await MessagingAPI.markMessagesAsRead(String(user.id), String(activeUserId));
        }
      } catch (err) {
        console.error('Failed to mark active thread as read:', err);
      }

      // Refresh unread counts after marking the active thread, without clearing all senders
      try {
        const dbUnreadBySender = await MessagingAPI.getUnreadCountsBySender(String(user.id));
        setUnreadMessages(dbUnreadBySender);
      } catch (err) {
        console.error('Failed to refresh unread after mark-as-read:', err);
      }
    };

    markActiveThreadRead();
  }, [showMessaging, user?.id]);

  // Poll admin notification unread (from WHIPT Admin/system account)
  useEffect(() => {
    let interval: number;
    const checkAdminUnread = async () => {
      try {
        const { getUserByEmail, getUnreadCount } = await import('@/lib/messaging-api');
        const adminUser = await getUserByEmail('system@whipt.app');
        if (!adminUser) {
          setAdminUnreadMessages(0);
          return;
        }
        if (!user?.id) return;
        const count = await getUnreadCount(user.id, adminUser.id);
        setAdminUnreadMessages(count || 0);
      } catch (err) {
        console.error('Failed to fetch admin unread messages:', err);
      }
    };

    checkAdminUnread();
    interval = window.setInterval(checkAdminUnread, 5000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Check for upcoming deadlines and send notifications
  useEffect(() => {
    const sessionKey = 'deadline_session_notified';
    const sessionNotified = sessionStorage.getItem(sessionKey);
    
    const checkDeadlines = () => {
      const now = new Date();
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
      const oneDayFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      // Check all assignments across all notebooks
      classNotebooks.forEach(notebook => {
        notebook.categories.forEach(category => {
          const upcomingItems = category.buckets?.upcoming || [];
          
          upcomingItems.forEach(item => {
            if (!item.dueDate) return;
            
            const dueDate = new Date(item.dueDate);
            const notificationKey = `deadline_notified_${item.id || item.title}_${item.dueDate}`;
            
            // Check if already notified (skip if notified in previous sessions)
            if (localStorage.getItem(notificationKey)) return;
            
            // 1 hour warning
            if (dueDate <= oneHourFromNow && dueDate > now) {
              NotificationManager.show('deadline', {
                title: '⏰ Deadline in 1 Hour!',
                body: `${item.title} - ${notebook.title}`,
                requireInteraction: true
              });
              localStorage.setItem(notificationKey, 'true');
            }
            // 24 hour warning
            else if (dueDate <= oneDayFromNow && dueDate > oneHourFromNow) {
              NotificationManager.show('deadline', {
                title: '📅 Deadline Tomorrow',
                body: `${item.title} - ${notebook.title}`,
                requireInteraction: false
              });
              localStorage.setItem(notificationKey, 'true');
            }
          });
        });
      });
      
      // Mark that we've checked on initial load
      if (!sessionNotified) {
        sessionStorage.setItem(sessionKey, 'true');
      }
    };
    
    // Check immediately on mount and every 10 minutes
    checkDeadlines();
    const interval = setInterval(checkDeadlines, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [classNotebooks]);

  // Check for upcoming scheduled sessions (class reminders)
  useEffect(() => {
    const sessionKey = 'class_session_notified';
    const sessionNotified = sessionStorage.getItem(sessionKey);
    
    const checkClassReminders = () => {
      const now = new Date();
      const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
      
      scheduledSessions.forEach(session => {
        const sessionDate = new Date(session.date);
        const notificationKey = `class_notified_${session.id}_${session.date}`;
        
        // Check if already notified (skip if notified in previous sessions)
        if (localStorage.getItem(notificationKey)) return;
        
        // Notify 15 minutes before class
        if (sessionDate <= fifteenMinutesFromNow && sessionDate > now) {
          NotificationManager.show('class', {
            title: '🎓 Class Starting Soon',
            body: `${session.title} starts in 15 minutes`,
            requireInteraction: false
          });
          localStorage.setItem(notificationKey, 'true');
        }
      });
      
      // Mark that we've checked on initial load
      if (!sessionNotified) {
        sessionStorage.setItem(sessionKey, 'true');
      }
    };
    
    // Check immediately on mount and every 5 minutes
    checkClassReminders();
    const interval = setInterval(checkClassReminders, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [scheduledSessions]);

  // Detect new assignments posted (compare bucket sizes)
  useEffect(() => {
    const storedCounts = localStorage.getItem('assignment_counts');
    const currentCounts: {[key: string]: number} = {};
    
    // Build current assignment counts per class
    classNotebooks.forEach(notebook => {
      const totalAssignments = notebook.categories.reduce((sum, cat) => {
        const upcoming = cat.buckets?.upcoming?.length || 0;
        const inProgress = cat.buckets?.['in-progress']?.length || 0;
        return sum + upcoming + inProgress;
      }, 0);
      currentCounts[notebook.title] = totalAssignments;
    });
    
    if (storedCounts) {
      const previousCounts = JSON.parse(storedCounts);
      
      // Check for increases (new assignments)
      Object.keys(currentCounts).forEach(className => {
        const current = currentCounts[className];
        const previous = previousCounts[className] || 0;
        
        if (current > previous) {
          const diff = current - previous;
          NotificationManager.show('assignment', {
            title: `📝 New Assignment${diff > 1 ? 's' : ''} Posted`,
            body: `${className} - ${diff} new assignment${diff > 1 ? 's' : ''}`,
            requireInteraction: false
          });
        }
      });
    }
    
    // Save current counts
    localStorage.setItem('assignment_counts', JSON.stringify(currentCounts));
  }, [classNotebooks]);

  // Load transcripts from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('meetingTranscripts');
    if (stored) {
      setMeetingTranscripts(JSON.parse(stored));
    }
  }, []);

  // Keyboard shortcut for Quick Access Panel: Ctrl/Cmd + Shift + W
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'W') {
        e.preventDefault();
        setShowQuickAccessPanel(prev => !prev);
      }
      // ESC to close panel
      if (e.key === 'Escape' && showQuickAccessPanel) {
        setShowQuickAccessPanel(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showQuickAccessPanel]);

  // Swipe from right edge to open panel; swipe from left edge to close when open.
  useEffect(() => {
    const edgeMargin = 80;
    const threshold = 60;
    const verticalFactor = 2;

    const handlePointerDown = (e: PointerEvent) => {
      swipeStartRef.current = { x: e.clientX, y: e.clientY };
    };

    const handlePointerUp = (e: PointerEvent) => {
      const start = swipeStartRef.current;
      swipeStartRef.current = null;
      if (!start) return;

      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * verticalFactor) return;

      // Open with right-edge swipe left; close with left-edge swipe right when open
      if (dx < -threshold && start.x > window.innerWidth - edgeMargin) {
        setShowQuickAccessPanel(true);
      } else if (dx > threshold && start.x < edgeMargin && showQuickAccessPanel) {
        setShowQuickAccessPanel(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    window.addEventListener('pointerup', handlePointerUp, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [showQuickAccessPanel]);

  // Listen for storage events to refresh scheduled meetings
  useEffect(() => {
    const handleStorageChange = () => {
      setScheduledMeetingsRefresh(prev => prev + 1);
      // Load scheduled sessions
      const scheduled = JSON.parse(localStorage.getItem('scheduledSessions') || '[]');
      setScheduledSessions([...scheduled]); // Always create new array reference
      // Also reload transcripts
      const stored = localStorage.getItem('meetingTranscripts');
      if (stored) {
        setMeetingTranscripts(JSON.parse(stored));
      }
    };
    
    // Initial load
    handleStorageChange();
    
    // Listen to storage (cross-window) updates
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // REMOVED: Old roomId parameter handler - now using unified 'room' parameter handler below (line ~2744)

  const activeClass =
    classNotebooks.find((notebook) => notebook.id === activeClassId) ||
    classNotebooks[0];
    
  const activeCategory = useMemo(() => {
    return activeClass
      ? activeClass.categories.find((category) => category.id === activeCategoryId) ||
        activeClass.categories[0]
      : undefined;
  }, [activeClass, activeCategoryId, classNotebooks]);

  // Check if there are any upcoming items across all categories
  const hasUpcomingItems = activeClass?.categories.some((cat) => {
    const items = cat.buckets?.upcoming || [];
    return items.some((item) => item.status === "upcoming" && item.dueDate);
  }) || false;

  // Get color for each category
  const getCategoryColor = (categoryId: string) => {
    switch(categoryId) {
      case 'discussion': return 'text-blue-300';
      case 'assignment': return 'text-amber-300';
      case 'experiential': return 'text-emerald-300';
      case 'video': return 'text-cyan-300';
      default: return 'text-amber-100';
    }
  };

  const activeCategoryBuckets = activeCategory?.buckets;
  
  // Get items from timelineOverrides first, fall back to default buckets
  const overrideItems = timelineOverrides[activeClassId]?.[activeCategoryId];
  // CRITICAL FIX: Always use buckets directly (like mobile does), ignore overrides
  // Desktop was checking overrides first which caused empty results
  const activeItems = (activeCategoryBuckets && activeCategoryBuckets[activePhase]) || [];
  
  console.log(`[DEBUG] activeCategoryBuckets structure:`, {
    hasBuckets: !!activeCategoryBuckets,
    phase: activePhase,
    bucketKeys: activeCategoryBuckets ? Object.keys(activeCategoryBuckets) : [],
    upcomingLength: activeCategoryBuckets?.upcoming?.length,
    completedLength: activeCategoryBuckets?.completed?.length,
    upcomingItems: activeCategoryBuckets?.upcoming?.slice(0, 3).map(i => i.title),
    completedItems: activeCategoryBuckets?.completed?.slice(0, 3).map(i => i.title),
  });
  console.log(`[DEBUG] Final activeItems count: ${activeItems.length}`, activeItems.slice(0, 3).map(i => i?.title));
  
  // Filter OUT virtual room notes (they have their own section above)
  const itemsWithoutTranscripts = activeItems.filter(item => {
    const title = item.title?.toLowerCase() || '';
    const description = item.description?.toLowerCase() || '';
    const id = item.id?.toString() || '';
    
    // Exclude items that look like virtual room transcripts or test items
    const isTranscript = title.includes('session transcription') || 
                         title.includes('virtual room') ||
                         title.includes('meeting notes') ||
                         description.includes('session transcription') ||
                         description.includes('virtual room') ||
                         // Filter out test items created from virtual room
                         (title.includes('session') && (title.includes('beta') || title.includes('test'))) ||
                         // Filter by ID pattern (numeric IDs are user-created, not imported)
                         (id && /^\d+$/.test(id));
    return !isTranscript;
  });
  
  // Filter items by type if a specific filter is active
  const filteredItems = activeItemFilter === 'all' 
    ? itemsWithoutTranscripts
    : itemsWithoutTranscripts.filter(item => item.metadata?.type === activeItemFilter);

  const collapsableItems = sortItemsByDueClosestNow(
    (() => {
      const upcomingItems = activeCategoryBuckets?.upcoming || [];
      const completedItems = activeCategoryBuckets?.completed || [];

      const completedIds = new Set(completedItems.map((item: any) => String(item.id)));
      const completedKeys = new Set(
        completedItems.map((item: any) => `${String(item.title || '').toLowerCase()}|${item.dueDate || ''}`)
      );

      const upcomingWithoutCompleted = upcomingItems.filter((item: any) => {
        const itemId = String(item.id);
        const itemKey = `${String(item.title || '').toLowerCase()}|${item.dueDate || ''}`;
        const normalizedStatus = String(item?.status || '').toLowerCase();
        const isMarkedCompleted = item?.completed === true;

        return (
          normalizedStatus !== 'completed' &&
          !isMarkedCompleted &&
          !completedIds.has(itemId) &&
          !completedKeys.has(itemKey)
        );
      });

      const upcomingWithoutTranscripts = upcomingWithoutCompleted.filter(item => {
        const title = item.title?.toLowerCase() || '';
        const description = item.description?.toLowerCase() || '';
        const id = item.id?.toString() || '';

        const isTranscript = title.includes('session transcription') || 
                             title.includes('virtual room') ||
                             title.includes('meeting notes') ||
                             description.includes('session transcription') ||
                             description.includes('virtual room') ||
                             (title.includes('session') && (title.includes('beta') || title.includes('test'))) ||
                             (id && /^\d+$/.test(id));
        return !isTranscript;
      });

      const now = Date.now();
      const upcomingCurrentOrFutureOnly = upcomingWithoutTranscripts.filter((item: any) => {
        if (!item?.dueDate) return true;
        const dueTime = new Date(item.dueDate).getTime();
        if (Number.isNaN(dueTime)) return true;
        return dueTime >= now;
      });

      return activeItemFilter === 'all'
        ? upcomingCurrentOrFutureOnly
        : upcomingCurrentOrFutureOnly.filter(item => item.metadata?.type === activeItemFilter);
    })()
  );
  
  const canLaunchDraft = Boolean(activeClass && activeCategory);

  const formatDueDisplay = (dueDate?: string) => {
    if (!dueDate) return "Date TBD";
    
    // Parse datetime-local string in local timezone (avoid UTC conversion)
    // Format: "2026-01-17T10:00"
    const [datePart, timePart] = dueDate.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart ? timePart.split(':').map(Number) : [0, 0];
    
    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day, hours, minutes);
    
    if (Number.isNaN(date.getTime())) {
      return dueDate;
    }
    const dateFormatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    });
    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
  };

  const formatTimeTo12Hour = (time24?: string) => {
    if (!time24) return "TBD";
    // Format: "15:00" -> "3:00 PM"
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const extractTimeFromDateTime = (dateTime?: string) => {
    if (!dateTime || !dateTime.includes('T')) return undefined;
    const rawTime = dateTime.split('T')[1]?.substring(0, 5);
    return rawTime || undefined;
  };

  const getSessionKey = (session: any, idx: number) => session?.sessionId || session?.id || `session-${idx}`;

  const normalizeSessions = (sessions: any[]) =>
    sessions.map((s, idx) => ({
      sessionId: s.sessionId || s.id || `session-${idx}-${Date.now()}`,
      ...s,
    }));

  const exportMeetingToICS = (session: any) => {
    const { title, date, time, duration, roomId, roomType, participants } = session;
    const now = new Date();
    const start = date && time ? new Date(`${date}T${time}`) : now;

    const parseDurationMinutes = (text?: string) => {
      if (!text) return 60;
      const match = text.match(/([0-9]+\.?[0-9]*)/);
      const value = match ? parseFloat(match[1]) : 60;
      if (/hour/i.test(text) && !/minute/i.test(text)) return Math.round(value * 60);
      return Math.round(value);
    };

    const minutes = parseDurationMinutes(duration);
    const end = new Date(start.getTime() + minutes * 60 * 1000);

    const toIcsDate = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const roomName = roomId || 'Virtual Room';
    const roomUrl = `${window.location.origin}/organizer?room=${roomType || 'discussion'}${activeClass?.id ? `&class=${activeClass.id}` : ''}`;

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//WHIPT//Virtual Meeting//EN',
      'BEGIN:VEVENT',
      `UID:${session.sessionId || Date.now()}@whipt.app`,
      `DTSTAMP:${toIcsDate(now)}`,
      `DTSTART:${toIcsDate(start)}`,
      `DTEND:${toIcsDate(end)}`,
      `SUMMARY:${title || 'Virtual Meeting'}`,
      `DESCRIPTION:Join the virtual meeting.\nRoom Link: ${roomUrl}${participants?.length ? `\nParticipants: ${participants.join(', ')}` : ''}`,
      `LOCATION:${roomUrl}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(title || 'virtual-meeting').replace(/\s+/g, '-')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper: Check if item is archived (completed OR past due by 3+ months)
  const isItemArchived = (item: NotebookItem): boolean => {
    if (item.status === 'completed') {
      // Check if completed more than 3 months ago
      const completedDate = item.completedAt ? new Date(item.completedAt) : null;
      if (completedDate) {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        return completedDate < threeMonthsAgo;
      }
    }
    
    if (item.dueDate && item.status === 'upcoming') {
      // Check if past due by 3+ months
      const dueDate = new Date(item.dueDate);
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      return dueDate < threeMonthsAgo;
    }
    
    return false;
  };

  const getMeetingColors = (roomType?: string) => {
    // Return color classes based on room type
    switch (roomType) {
      case 'discussion':
        return {
          gradient: 'from-cyan-600/15 to-blue-600/15 hover:from-cyan-600/25 hover:to-blue-600/25',
          border: 'border-cyan-400/30',
          glowColor: 'rgba(34, 211, 238, 0.5)', // cyan
          pulseColor: 'rgba(34, 211, 238, 0.3)'
        };
      case 'assignment':
        return {
          gradient: 'from-amber-600/15 to-yellow-600/15 hover:from-amber-600/25 hover:to-yellow-600/25',
          border: 'border-amber-400/30',
          glowColor: 'rgba(251, 191, 36, 0.5)', // amber
          pulseColor: 'rgba(251, 191, 36, 0.3)'
        };
      case 'experiential':
        return {
          gradient: 'from-emerald-600/15 to-green-600/15 hover:from-emerald-600/25 hover:to-green-600/25',
          border: 'border-emerald-400/30',
          glowColor: 'rgba(16, 185, 129, 0.5)', // emerald
          pulseColor: 'rgba(16, 185, 129, 0.3)'
        };
      case 'video':
        return {
          gradient: 'from-purple-600/15 to-violet-600/15 hover:from-purple-600/25 hover:to-violet-600/25',
          border: 'border-purple-400/30',
          glowColor: 'rgba(168, 85, 247, 0.5)', // purple
          pulseColor: 'rgba(168, 85, 247, 0.3)'
        };
      default:
        return {
          gradient: 'from-slate-600/15 to-gray-600/15 hover:from-slate-600/25 hover:to-gray-600/25',
          border: 'border-slate-400/30',
          glowColor: 'rgba(148, 163, 184, 0.5)', // slate
          pulseColor: 'rgba(148, 163, 184, 0.3)'
        };
    }
  };

  const formatDocumentTimestamp = (value?: string) => {
    if (!value) return "Just now";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  const summary = useMemo(() => {
    // Use activeClass data if available
    if (activeClass) {
      const allItems: NotebookItem[] = [];
      activeClass.categories.forEach(cat => {
        if (cat.buckets?.upcoming) {
          allItems.push(...cat.buckets.upcoming.filter(item => item.status === 'upcoming'));
        }
      });
      
      // Sort by due date
      allItems.sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      });
      
      const nextItem = allItems[0];
      
      return {
        course: activeClass.course || activeClass.title || 'No Course',
        instructor: activeClass.instructor || 'No Instructor',
        nextItemLabel: nextItem?.title || 'No upcoming items',
        dueDisplay: nextItem?.dueDate ? formatDueDisplay(nextItem.dueDate) : 'TBD',
        upcomingCount: allItems.length,
      };
    }
    
    if (!organizerItems.length) {
      return FALLBACK_ORGANIZER_SUMMARY;
    }
    const primary = organizerItems[0];
    return {
      course: primary.course || FALLBACK_ORGANIZER_SUMMARY.course,
      instructor: primary.instructor || FALLBACK_ORGANIZER_SUMMARY.instructor,
      nextItemLabel: primary.title || FALLBACK_ORGANIZER_SUMMARY.nextItemLabel,
      dueDisplay:
        formatDueDisplay(primary.dueDate) ||
        FALLBACK_ORGANIZER_SUMMARY.dueDisplay,
      upcomingCount: organizerItems.length,
    };
  }, [organizerItems, activeClass]);

  // Get items from active category's bucket for timeline
  const previewItems = useMemo(() => {
    if (!activeClass || !activeCategory) return organizerItems.length ? organizerItems : FALLBACK_ITEMS;
    
    // Read directly from category buckets (single source of truth)
    const bucket = activeCategory.buckets?.[activePhase] || [];
    return bucket.length > 0 ? bucket : (organizerItems.length ? organizerItems : FALLBACK_ITEMS);
  }, [activeClass, activeCategory, activePhase, organizerItems, classNotebooks]);

  const notebookDocuments = useMemo(() => {
    if (!activeClass || !activeCategoryId) return [] as NotebookDocument[];
    return documents.filter(
      (doc) =>
        doc.notebookId === activeClass.id &&
        doc.notebookTabId === activeCategoryId
    );
  }, [documents, activeClass, activeCategoryId]);

  // All documents for the class (for Documents Hub)
  const allClassDocuments = useMemo(() => {
    if (!activeClass) return [] as NotebookDocument[];
    return documents.filter((doc) => doc.notebookId === activeClass.id);
  }, [documents, activeClass]);

  // Uploaded documents (files with data: or encrypted)
  const uploadedDocuments = useMemo(() => {
    return documents.filter(d => (d.content?.includes('data:')) || d.isProtected);
  }, [documents]);

  // Categorized wallet documents
  const walletDocuments = useMemo(() => {
    return uploadedDocuments
      .filter(d => d.notebookTabId === 'travel')
      .map(d => {
        const categorized = categorizeWalletDocument(d.title);
        const identifiers = extractIdentifiers(d.title);
        return {
          id: d.id,
          title: d.title,
          category: categorized.category,
          brand: categorized.brand,
          brandColor: categorized.brandColor,
          content: d.content,
          isProtected: d.isProtected || false,
          created_at: d.created_at,
          confirmationNumber: identifiers.confirmationNumber,
          flightNumber: identifiers.flightNumber
        };
      });
  }, [uploadedDocuments]);

  const toneBadgeClass = (tone: OrganizerTone) => {
    const base = "px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.2em]";
    switch (tone) {
      case "experiential":
        return `${base} bg-emerald-500/10 text-emerald-200`;
      case "meeting":
        return `${base} bg-cyan-500/10 text-cyan-200`;
      case "assignment":
      default:
        return `${base} bg-fuchsia-500/10 text-fuchsia-200`;
    }
  };
  const findNextDueDate = (items: NotebookItem[]): string | undefined => {
    return items
      .filter((item) => item.dueDate)
      .sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aTime - bTime;
      })[0]?.dueDate;
  };

  const getNotebookNextDue = (notebook: ClassNotebook) => {
    const aggregate = notebook.categories.flatMap(
      (category) => category.buckets.upcoming
    );
    return aggregate.length ? findNextDueDate(aggregate) : undefined;
  };

  const getCategoryNextDue = (category?: NotebookCategory) => {
    if (!category) return undefined;
    return findNextDueDate(category.buckets?.upcoming || []);
  };
  
  // Helper to get due items for overlay (within next 7 days)
  const getDueItemsForCategory = (classId: string, categoryId: string) => {
    const notebook = classNotebooks.find(nb => nb.id === classId);
    const category = notebook?.categories.find(cat => cat.id === categoryId);
    const items = category?.buckets?.upcoming || [];
    
    const now = Date.now();
    const weekFromNow = now + (7 * 24 * 60 * 60 * 1000);
    
    return items.filter(item => {
      if (!item.dueDate) return false;
      const dueTime = new Date(item.dueDate).getTime();
      return dueTime >= now && dueTime <= weekFromNow;
    }).sort((a, b) => {
      const aTime = new Date(a.dueDate!).getTime();
      const bTime = new Date(b.dueDate!).getTime();
      return aTime - bTime;
    });
  };

  useEffect(() => {
    if (!activeClass) return;
    // Only auto-select if user already had a category selected that no longer exists
    // Don't auto-select on initial load (when activeCategoryId is empty)
    if (!activeCategoryId) return;
    
    const exists = activeClass.categories.some(
      (category) => category.id === activeCategoryId
    );
    if (!exists && activeClass.categories[0]) {
      setActiveCategoryId(activeClass.categories[0].id);
    }
  }, [activeClass, activeCategoryId]);

  // Load journal entries and add to upcoming section
  useEffect(() => {
    if (!activeClass) return;
    
    const storageKey = `journal-entries-${activeClass.id}`;
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      try {
        const journalEntries: JournalEntry[] = JSON.parse(stored);
        
        // Filter entries with due dates and convert to NotebookItem format
        const upcomingJournalItems = journalEntries
          .filter(entry => entry.dueDate)
          .map((entry): NotebookItem => {
            // Truncate content to first 150 characters for preview
            const truncatedContent = entry.content.length > 150 
              ? entry.content.substring(0, 150) + '...' 
              : entry.content;
            
            return {
              id: parseInt(entry.id.replace('journal-', '')) || Math.random(),
              title: `${entry.title}`,
              tone: entry.entryType === 'discussion' ? 'discussion' as OrganizerTone : 'meeting' as OrganizerTone,
              course: `${entry.entryType === 'discussion' ? '💬 Discussion' : '📔 Journal'}${entry.moduleNumber ? ' • ' + entry.moduleNumber : ''}`,
              dueDate: entry.dueDate,
              notes: truncatedContent,
              status: 'upcoming',
              created_at: entry.created_at,
            };
          });

        if (upcomingJournalItems.length > 0) {
          // Find the video category and update its upcoming bucket
          const videoCategory = activeClass.categories.find(cat => cat.id === 'video');
          if (videoCategory && videoCategory.buckets && videoCategory.buckets.upcoming) {
            // Remove old journal items (identified by tone = 'discussion' or having emoji indicators)
            const nonJournalItems = videoCategory.buckets.upcoming.filter(
              item => item.tone !== 'discussion' && 
                      !(item.course && (item.course.includes('💬') || item.course.includes('📔')))
            );
            videoCategory.buckets.upcoming = [...nonJournalItems, ...upcomingJournalItems];
          }
        }
      } catch (e) {
        console.error('Failed to load journal entries for upcoming:', e);
      }
    }
  }, [activeClass]);

  const handleClassChange = (classId: string) => {
    setActiveClassId(classId);
    const targetNotebook = classNotebooks.find((notebook) => notebook.id === classId);
    if (targetNotebook?.categories[0]) {
      setActiveCategoryId(targetNotebook.categories[0].id);
    }
  };

  const handleAddClassNotebook = () => {
    const title = window.prompt("Class notebook title?");
    if (!title) return;
    const normalizedTitle = title.trim() || "Untitled Class";
    const categories = createNotebookCategories(organizerMode as 'student' | 'personal');
    const newNotebook: ClassNotebook = {
      id: `class-${Date.now()}`,
      title: normalizedTitle,
      accent: "from-[#7a3c07] to-[#3f1b05]",
      categories,
    };
    setClassNotebooks((prev) => {
      const updated = [...prev, newNotebook];
      if (user?.id) {
        saveNotebooks(user.id, updated);
      }
      return updated;
    });
    setActiveClassId(newNotebook.id);
    setActiveCategoryId(categories[0]?.id || "");
    setActivePhase("upcoming");
  };

  const handleAddClass = (data: { className: string; courseName: string; instructor: string }) => {
    const newNotebook: ClassNotebook = {
      id: `class-${Date.now()}`,
      title: data.className.trim(),
      course: data.courseName.trim(),
      instructor: data.instructor.trim(),
      categories: [
        {
          id: 'discussion',
          label: 'Discussion',
          icon: NotebookPen,
          accent: 'text-blue-200',
          buckets: { upcoming: [], completed: [] }
        },
        {
          id: 'assignment',
          label: 'Assignment',
          icon: CalendarDays,
          accent: 'text-amber-200',
          buckets: { upcoming: [], completed: [] }
        },
        {
          id: 'experiential',
          label: 'Experiential',
          icon: Upload,
          accent: 'text-emerald-200',
          buckets: { upcoming: [], completed: [] }
        },
        {
          id: 'video',
          label: 'Meeting Notes',
          icon: BellRing,
          accent: 'text-cyan-200',
          buckets: { upcoming: [], completed: [] }
        },
        {
          id: 'snack',
          label: 'Snack Dial',
          icon: Utensils,
          accent: 'text-pink-200',
          buckets: { upcoming: [], completed: [] }
        },
        {
          id: 'other',
          label: 'Other',
          icon: FileText,
          accent: 'text-purple-200',
          buckets: { upcoming: [], completed: [] }
        }
      ]
    };
    
    // Add notebook and switch to it atomically
    setClassNotebooks(prev => {
      const updated = [...prev, newNotebook];
      // Persist to database immediately
      if (user?.id) {
        saveNotebooks(user.id, updated);
      }
      return updated;
    });
    setActiveClassId(newNotebook.id);
    setActiveCategoryId(newNotebook.categories[0].id);
  };

  const handleEventsImported = (events: CalendarEvent[]) => {
    setImportedEvents(events);
    setShowCalendarImport(false);
    setShowEventAssignment(true);
  };

  const handleEventAssignments = (assignments: EventAssignment[]) => {
    setClassNotebooks((prev) =>
      prev.map((notebook) => {
        const notebookAssignments = assignments.filter(
          (a) => a.notebookId === notebook.id
        );
        
        if (notebookAssignments.length === 0) return notebook;
        
        return {
          ...notebook,
          categories: notebook.categories.map((category) => {
            const categoryItems = notebookAssignments
              .filter((a) => {
                // Map imported category to notebook category ID
                return a.category === category.id;
              })
              .map((a) => {
                const stableImportUid = String(a.event.id || '').trim();
                const stableItemId = `imported-${stableImportUid}`;
                const item: NotebookItem = {
                  id: stableItemId,
                  title: a.event.title,
                  dueDate: a.event.startDate.toISOString(),
                  status: 'upcoming',
                  description: a.event.description || undefined,
                  notes: a.event.description || undefined,
                  eventType: 'assignment',
                  importUid: stableImportUid,
                  importSource: a.event.source,
                  importedAt: new Date().toISOString(),
                  imported: true,
                };
                return item;
              });
            
            if (categoryItems.length === 0) return category;
            
            const existingUpcoming = [...(category.buckets?.upcoming || [])];
            const existingCompleted = [...(category.buckets?.completed || [])];

            // Upsert imported items by stable ICS UID so re-import refreshes changed dates/titles
            categoryItems.forEach((importedItem: any) => {
              const existingUpcomingIndex = existingUpcoming.findIndex((existingItem: any) => {
                const existingUid = String(existingItem.importUid || '').trim();
                const importedUid = String(importedItem.importUid || '').trim();
                return (
                  (existingUid && importedUid && existingUid === importedUid) ||
                  String(existingItem.id) === String(importedItem.id)
                );
              });

              const existingCompletedIndex = existingCompleted.findIndex((existingItem: any) => {
                const existingUid = String(existingItem.importUid || '').trim();
                const importedUid = String(importedItem.importUid || '').trim();
                return (
                  (existingUid && importedUid && existingUid === importedUid) ||
                  String(existingItem.id) === String(importedItem.id)
                );
              });

              if (existingCompletedIndex >= 0) {
                existingCompleted[existingCompletedIndex] = {
                  ...existingCompleted[existingCompletedIndex],
                  title: importedItem.title,
                  dueDate: importedItem.dueDate,
                  description: importedItem.description,
                  notes: importedItem.notes,
                  importUid: importedItem.importUid,
                  importSource: importedItem.importSource,
                  importedAt: importedItem.importedAt,
                  imported: true,
                  eventType: existingCompleted[existingCompletedIndex].eventType || 'assignment',
                  status: existingCompleted[existingCompletedIndex].status || 'completed',
                };
              } else if (existingUpcomingIndex >= 0) {
                existingUpcoming[existingUpcomingIndex] = {
                  ...existingUpcoming[existingUpcomingIndex],
                  title: importedItem.title,
                  dueDate: importedItem.dueDate,
                  description: importedItem.description,
                  notes: importedItem.notes,
                  importUid: importedItem.importUid,
                  importSource: importedItem.importSource,
                  importedAt: importedItem.importedAt,
                  imported: true,
                  eventType: existingUpcoming[existingUpcomingIndex].eventType || 'assignment',
                };
              } else {
                const hasDuplicateByContent =
                  existingUpcoming.some((existingItem: any) =>
                    existingItem.title === importedItem.title && existingItem.dueDate === importedItem.dueDate
                  ) ||
                  existingCompleted.some((existingItem: any) =>
                    existingItem.title === importedItem.title && existingItem.dueDate === importedItem.dueDate
                  );

                if (!hasDuplicateByContent) {
                  existingUpcoming.push(importedItem);
                }
              }
            });

            return {
              ...category,
              buckets: {
                ...category.buckets,
                upcoming: existingUpcoming,
                completed: existingCompleted,
              },
            };
          }),
        };
      })
    );
    
    setShowEventAssignment(false);
    setImportedEvents([]);
  };

  const handleAddNotebookItem = async (item?: { title: string; dueDate?: string; notes?: string; metadata?: any }) => {
    if (!activeClass || !activeCategory) return;
    
    // If no item passed, use legacy prompt behavior
    if (!item) {
      const title = window.prompt("Reminder title?");
      if (!title) return;
      
      const description = window.prompt("Description/notes (optional):");
      const dueDateInput = window.prompt("Due date (YYYY-MM-DD HH:MM):");
      
      item = {
        title,
        dueDate: dueDateInput || undefined,
        notes: description || undefined
      };
    }
    
    // If this item has a due date, also create a calendar event
    if (item.dueDate && user) {
      try {
        // Use the category explicitly set in the form, or fall back to activeCategory
        const itemCategory = item.metadata?.category || activeCategory.id;
        const isPersonalCategory = ['work', 'home', 'travel'].includes(itemCategory);
        
        // For personal organizer items, use category name as course (WORK, HOME, TRAVEL)
        // For class items, use the class/notebook title
        const courseName = isPersonalCategory 
          ? itemCategory.toUpperCase() 
          : activeClass.title;
        
        await createCalendarEvent({
          user_id: user.id,
          date: item.dueDate,
          title: item.title,
          description: item.notes || undefined,
          category: itemCategory,
          course: courseName, // "WORK", "HOME", "TRAVEL", or actual class name
          metadata: {
            ...item.metadata,
            source: 'desktop-organizer',
            organizer_category: activeCategory.id,
            isPersonal: isPersonalCategory,
            notebookId: activeClass.id
          }
        });
        console.log(`✅ Calendar event created for ${itemCategory}:`, item.title, 'Course:', courseName);
      } catch (error) {
        console.error('Failed to create calendar event:', error);
      }
    }
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== activeClass.id) return notebook;
        const newItem: NotebookItem = {
          id: `${activeClass.id}-${activeCategory.id}-${Date.now()}`,
          title: item!.title,
          dueDate: item!.dueDate,
          status: activePhase,
          description: item!.notes || undefined,
          metadata: item!.metadata || undefined,
        };
        return {
          ...notebook,
          categories: notebook.categories.map((category) => {
            if (category.id !== activeCategory.id) return category;
            return {
              ...category,
              buckets: {
                ...category.buckets,
                [activePhase]: [...category.buckets[activePhase], newItem],
              },
            };
          }),
        };
      });
      
      // Save immediately with the updated state
      if (user?.id) {
        setTimeout(() => saveNotebooks(user.id, updated), 100);
      }
      
      return updated;
    });
  };

  const handleDeleteItem = (notebookId: string, categoryId: string, itemId: string) => {
    if (!window.confirm('Mark this item as completed? It will be hidden from view but available for review.')) return;
    if (!user?.id) return;
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== notebookId) return notebook;
        return {
          ...notebook,
          categories: notebook.categories.map((category) => {
            if (category.id !== categoryId) return category;
            // Find the item and mark as completed
            const allItems = [...category.buckets.upcoming, ...category.buckets.completed];
            const updatedItems = allItems.map(item => 
              String(item.id) === String(itemId)
                ? { ...item, status: 'completed', completed: true, completedAt: new Date().toISOString() }
                : item
            );
            return {
              ...category,
              buckets: {
                upcoming: updatedItems.filter(item => String(item.status || '').toLowerCase() !== 'completed' && item.completed !== true),
                completed: updatedItems.filter(item => String(item.status || '').toLowerCase() === 'completed' || item.completed === true),
              },
            };
          }),
        };
      });
      
      // Save to database after state update
      saveNotebooks(user.id, updated);
      
      // Force re-render by creating new array reference
      return [...updated];
    });
  };

  const handlePermanentDeleteItem = (notebookId: string, categoryId: string, itemId: string, itemTitle: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${itemTitle}"?\n\nThis action cannot be undone.`)) {
      return;
    }
    if (!user?.id) {
      return;
    }
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== notebookId) return notebook;
        return {
          ...notebook,
          categories: notebook.categories.map((category) => {
            if (category.id !== categoryId) return category;
            // Remove item from both buckets
            return {
              ...category,
              buckets: {
                upcoming: category.buckets.upcoming.filter(item => item.id !== itemId),
                completed: category.buckets.completed.filter(item => item.id !== itemId),
              },
            };
          }),
        };
      });
      
      // Save to database after state update
      saveNotebooks(user.id, updated);
      
      return [...updated];
    });
  };

  const handleMoveItem = (notebookId: string, fromCategoryId: string, itemId: string, toCategoryId: string) => {
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== notebookId) return notebook;
        
        // Find the item to move
        let itemToMove: NotebookItem | null = null;
        const fromCategory = notebook.categories.find(cat => cat.id === fromCategoryId);
        if (fromCategory) {
          itemToMove = [...fromCategory.buckets.upcoming, ...fromCategory.buckets.completed]
            .find(item => item.id === itemId) || null;
        }
        
        if (!itemToMove) return notebook;
        
        // Remove from old category and add to new category
        return {
          ...notebook,
          categories: notebook.categories.map((category) => {
            if (category.id === fromCategoryId) {
              // Remove from this category
              return {
                ...category,
                buckets: {
                  upcoming: category.buckets.upcoming.filter(item => item.id !== itemId),
                  completed: category.buckets.completed.filter(item => item.id !== itemId),
                },
              };
            } else if (category.id === toCategoryId) {
              // Add to this category
              return {
                ...category,
                buckets: {
                  ...category.buckets,
                  [itemToMove!.status]: [...category.buckets[itemToMove!.status], itemToMove!],
                },
              };
            }
            return category;
          }),
        };
      });
      
      // Save to database after state update
      if (user?.id) {
        saveNotebooks(user.id, updated);
      }
      return updated;
    });
  };

  const handleRemoveDuplicates = () => {
    if (!window.confirm('Remove all duplicate items? This will keep only one copy of each event based on title and due date.')) return;
    if (!user?.id || !activeClass) return;
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== activeClass.id) return notebook;
        
        return {
          ...notebook,
          categories: notebook.categories.map((category) => {
            const allItems = [
              ...(category.buckets?.upcoming || []),
              ...(category.buckets?.completed || [])
            ];
            
            // Keep only unique items by title+dueDate
            const seen = new Map<string, NotebookItem>();
            allItems.forEach(item => {
              const key = `${item.title}|${item.dueDate}`;
              if (!seen.has(key)) {
                seen.set(key, item);
              }
            });
            
            const uniqueItems = Array.from(seen.values());
            
            // Separate back into upcoming/completed
            return {
              ...category,
              buckets: {
                upcoming: uniqueItems.filter(item => item.status === 'upcoming'),
                completed: uniqueItems.filter(item => item.status === 'completed'),
              },
            };
          }),
        };
      });
      
      saveNotebooks(user.id, updated);
      return updated;
    });
  };

  const handleAddNotebookTab = () => {
    setShowTabTemplateModal(true);
  };

  const handleTemplateSelected = (template: any, customLabel?: string) => {
    if (!activeClass) return;
    
    const label = customLabel || template.defaultLabel || template.name;
    // Initialize metadata based on template type
    const metadata: any = {};
    if (template.id === 'work') {
      metadata.counters = [
        { label: 'PTO Days', used: 0, total: 15, color: 'blue' },
        { label: 'Sick Days', used: 0, total: 10, color: 'red' }
      ];
    }
    
    const newCategory: NotebookCategory = {
      id: `tab-${Date.now()}`,
      label,
      icon: template.icon,
      accent: template.accent,
      buckets: { upcoming: [], completed: [] },
      metadata,
    };
    
    setClassNotebooks((prev) =>
      prev.map((notebook) =>
        notebook.id === activeClass.id
          ? { ...notebook, categories: [...notebook.categories, newCategory] }
          : notebook
      )
    );
    setActiveCategoryId(newCategory.id);
    setActivePhase("upcoming");
    setShowTabTemplateModal(false);
  };

  const handleLaunchNotebookDraft = () => {
    if (!activeClass || !activeCategory) return;
    const params = new URLSearchParams({
      notebookId: activeClass.id,
      notebookTitle: activeClass.title,
      tabId: activeCategory.id,
      tabLabel: activeCategory.label,
      draftId: `${activeClass.id}-${activeCategory.id}-${Date.now()}`,
    });
    navigate(`/editor?${params.toString()}`);
  };

  const handleCreateSnackSpot = useCallback(
    async (payload: SnackSpotDraft) => {
      const label = payload.label.trim();
      if (!label) return;
      await createSnackSpot({
        userId: activeUserId,
        label,
        phone: sanitizeOptionalText(payload.phone),
        deliveryUrl: sanitizeOptionalText(payload.deliveryUrl),
        locationHint: sanitizeOptionalText(payload.locationHint),
        notes: sanitizeOptionalText(payload.notes),
        favorite: payload.favorite ? 1 : 0,
      } as Omit<SnackSpotRecord, "id" | "created_at" | "updated_at">);
    },
    [createSnackSpot, activeUserId]
  );

  const handleUpdateSnackSpot = useCallback(
    async (id: number, changes: Partial<SnackSpotRecord>) => {
      await updateSnackSpot(id, changes);
    },
    [updateSnackSpot]
  );

  const handleDeleteSnackSpot = useCallback(
    async (id: number) => {
      await deleteSnackSpot(id);
    },
    [deleteSnackSpot]
  );

  const handleMarkSnackOrdered = useCallback(
    async (id: number) => {
      await updateSnackSpot(id, { lastOrderedAt: new Date().toISOString() });
    },
    [updateSnackSpot]
  );

  const handleOpenVirtualRoom = async (categoryId: string, classIdOverride?: string) => {
    const targetClass = classIdOverride
      ? classNotebooks.find((nb) => nb.id === classIdOverride) || activeClass
      : activeClass;

    const blueprint = STUDENT_CATEGORY_BLUEPRINTS.find((b) => b.id === categoryId);

    const category = targetClass
      ? targetClass.categories.find((c) => c.id === categoryId)
      : blueprint
      ? {
          id: blueprint.id,
          label: blueprint.label,
          icon: blueprint.icon,
          accent: blueprint.accent,
          buckets: { upcoming: [], completed: [] },
        }
      : null;

    if (!category) return;
    
    // Get agenda from roomAgendas state (if exists)
    const agendaText = roomAgendas[categoryId] || '';
    const agendaArray = agendaText ? agendaText.split('\n').filter(line => line.trim()) : [];
    // Get base profile and override agenda with custom one
    const baseProfile = VIRTUAL_ROOM_PROFILES[categoryId] || {
      id: categoryId,
      tabLabel: category?.label || 'Session',
      roomTitle: `${category?.label || 'Session'} Session`,
      description: `Collaborate on ${category?.label?.toLowerCase() || 'this'} work with your peers.`,
      agenda: [],
      accessibilityNote: "Live captions and screen sharing available",
      participants: [
        { id: "1", name: "You", role: "Participant", initials: "YOU", online: true },
      ],
      resources: [],
    };
    
    // Override agenda with the custom one from roomAgendas
    const profile = {
      ...baseProfile,
      agenda: agendaArray,
    };
    // Clear room notes and chat when opening a new room
    setRoomNotes('');
    
    setVirtualRoomContext(profile);
    setVirtualRoomOpen(true);
  };

  // Deep link handler: ?room={id}&class={classId}&autojoin=1 opens room automatically
  useEffect(() => {
    if (inviteDeepLinkHandled.current) return;

    const params = new URLSearchParams(window.location.search);
    const roomId = params.get('room');
    const classId = params.get('class');
    const autoJoin = params.has('autojoin') || params.get('join') === '1';

    if (!roomId || !autoJoin) return;

    // Wait until notebooks are loaded (or allow blueprint fallback)
    const hasNotebooks = classNotebooks.length > 0;
    if (!hasNotebooks && !guestInviteMode) return;

    inviteDeepLinkHandled.current = true;

    // If classId provided and exists, set active class first
    if (classId && classNotebooks.some((nb) => nb.id === classId)) {
      setActiveClassId(classId);
      const classItem = classNotebooks.find((nb) => nb.id === classId);
      const firstCat = classItem?.categories?.[0]?.id;
      if (firstCat) setActiveCategoryId(firstCat);
    }

    // Open the virtual room
    handleOpenVirtualRoom(roomId, classId || undefined);
  }, [classNotebooks, setActiveClassId, setActiveCategoryId, guestInviteMode]);

  const persistRoomNotes = useCallback(async (topic: string) => {
    if (!roomNotes || !roomNotes.trim() || !virtualRoomContext || !activeClass) return;
    const transcript: MeetingTranscript = {
      id: `transcript-${Date.now()}`,
      roomType: virtualRoomContext.id,
      topic,
      title: topic, // Add title for display
      category: virtualRoomContext.tabLabel, // Add category label (Discussion, Assignment, etc.)
      classId: activeClass.id,
      className: activeClass.title,
      date: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      participants: virtualRoomContext.participants?.map(p => p.name) || ['You'],
      content: roomNotes,
      created_at: new Date().toISOString(),
    };

    const transcripts = [transcript, ...meetingTranscripts];
    setMeetingTranscripts(transcripts);

    if (user?.id) {
      await saveMeetingTranscripts(user.id, transcripts);
      // ALSO save to Transcript entity for Virtual Room Notes page
      try {
        const { createFlexibleRepository } = await import('@/repositories/flexibleEntityRepository');
        const transcriptRepo = createFlexibleRepository({ entityType: 'Transcript' });
        const savedTranscript = await transcriptRepo.create({
          title: topic || `${virtualRoomContext.roomTitle} Notes`,
          roomTitle: virtualRoomContext.roomTitle,
          roomCategory: virtualRoomContext.id, // discussion, assignment, experiential, social
          transcript: roomNotes,
          content: roomNotes,
          notes: roomNotes,
          date: new Date().toISOString(),
          participants: virtualRoomContext.participants?.map(p => p.name) || ['You'],
          classId: activeClass.id,
          className: activeClass.title
        });
      } catch (error) {
        console.error('❌ [VirtualRoom] Failed to save to Transcript entity:', error);
      }
    }

    // Save to the CORRECT category based on which room type was opened
    // discussion → discussion tab, assignment → assignment tab, etc.
    const categoryId = virtualRoomContext.id; // 'discussion', 'assignment', 'experiential', or 'video'
    const newItem: NotebookItem = {
      id: `meeting-${Date.now()}`,
      title: topic || `${virtualRoomContext.roomTitle} Notes`,
      dueDate: new Date().toISOString(),
      status: 'upcoming' as const,
      description: roomNotes,
      location: '',
      classId: activeClass.id
    };

    const updatedOverrides = { ...timelineOverrides };
    if (!updatedOverrides[activeClass.id]) {
      updatedOverrides[activeClass.id] = {};
    }
    if (!updatedOverrides[activeClass.id][categoryId]) {
      updatedOverrides[activeClass.id][categoryId] = [];
    }
    updatedOverrides[activeClass.id][categoryId] = [
      newItem,
      ...updatedOverrides[activeClass.id][categoryId]
    ];
    setTimelineOverrides(updatedOverrides);
    if (user?.id) {
      await saveTimelineOverrides(user.id, updatedOverrides);
      try {
        await createCalendarEvent({
          user_id: user.id,
          title: newItem.title,
          description: newItem.description,
          due_date: newItem.dueDate,
          course: activeClass.course || activeClass.title,
          status: newItem.status,
          category: 'Virtual Room Notes',
          room_category: virtualRoomContext.id, // Store the session type
          notes: newItem.description
        });
      } catch (error) {
        console.error('❌ [VirtualRoom] Failed to sync to calendar:', error);
      }
    }
  }, [roomNotes, virtualRoomContext, activeClass, meetingTranscripts, timelineOverrides, user?.id]);

  const handleCloseVirtualRoom = useCallback(async () => {
    console.log('[Close] Checking roomNotes:', {
      hasNotes: !!roomNotes,
      notesLength: roomNotes?.length || 0,
      notesTrimmed: roomNotes?.trim().length || 0,
      notesPreview: roomNotes?.substring(0, 100),
      hasContext: !!virtualRoomContext
    });
    
    if (roomNotes && roomNotes.trim() && virtualRoomContext) {
      const topic = window.prompt("Enter a topic/title for this session:", `${virtualRoomContext.tabLabel} Session`);
      if (topic) {
        await persistRoomNotes(topic);
      }
    } else {
    }

    setVirtualRoomOpen(false);
    setVirtualRoomContext(null);
    setRoomNotes('');
  }, [roomNotes, virtualRoomContext, persistRoomNotes]);

  const handleMinimizeVirtualRoom = useCallback(() => {
    setVirtualRoomMinimized(true);
  }, []);

  const handleRestoreVirtualRoom = useCallback(() => {
    setVirtualRoomMinimized(false);
  }, []);

  const handleGuestCloseVirtualRoom = useCallback(() => {
    setVirtualRoomOpen(false);
    setVirtualRoomContext(null);
    setRoomNotes('');
    navigate('/guest/goodbye');
  }, [navigate]);

  const handleAgendaChange = (updatedAgenda: string[]) => {
    if (virtualRoomContext) {
      setVirtualRoomContext({
        ...virtualRoomContext,
        agenda: updatedAgenda,
      });
    }
  };

  const handleRenameTimelineItem = (itemId: number) => {
    const nextTitle = window.prompt("Update title?");
    if (!nextTitle) return;
    setTimelineOverrides((prev) => ({
      ...prev,
      [itemId.toString()]: { title: nextTitle },
    }));
  };

  const handleFileUpload = async (files: FileList, password?: string) => {
    if (!activeClass || !uploadTabSelection) {
      alert('Please select a tab first');
      return;
    }

    setUploadingFiles(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Max 10MB per file.`);
        errorCount++;
        continue;
      }

      try {
        let base64Content: string;
        let isProtected = false;
        // If password provided, encrypt the file directly (handles its own FileReader)
        if (password) {
          try {
            base64Content = await encryptFile(file, password);
            isProtected = true;
          } catch (encryptError) {
            console.error('❌ Encryption failed:', encryptError);
            throw new Error(`Encryption failed: ${encryptError instanceof Error ? encryptError.message : 'Unknown error'}`);
          }
        } else {
          // No encryption - just read file as base64
          const reader = new FileReader();
          base64Content = await new Promise((resolve, reject) => {
            reader.onload = () => {
              resolve(reader.result as string);
            };
            reader.onerror = (error) => {
              console.error('❌ FileReader error:', error);
              reject(error);
            };
            reader.readAsDataURL(file);
          });
        }
        
        // Create document in database
        console.log(`📤 Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)...`);
        await create({
          title: file.name,
          content: base64Content,
          paperFormat: 'APA', // Default format for uploaded files
          notebookId: activeClass.id,
          notebookTabId: uploadTabSelection,
          userId: activeUserId,
          isProtected, // Add flag to indicate encryption
        });
        successCount++;
      } catch (error) {
        console.error('❌ Error uploading file:', file.name, error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        alert(`Failed to upload ${file.name}: ${errorMessage}`);
        errorCount++;
        continue; // Continue with next file instead of stopping
      }
    }

    setUploadingFiles(false);
    await reload();
    
    if (successCount > 0) {
      alert(`Successfully uploaded ${successCount} file(s)!${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
    } else {
      alert(`All uploads failed. Please try again.`);
    }
    
    setShowUploadPanel(false);
    setUploadTabSelection('');
    setProtectWithPassword(false);
  };

  const handleFileSelect = (files: FileList) => {
    if (protectWithPassword && files.length > 0) {
      // Show password modal for first file (single file protection for now)
      setPendingFile(files[0]);
      setShowPasswordModal(true);
    } else {
      handleFileUpload(files);
    }
  };

  const handlePasswordSet = async (password: string) => {
    setShowPasswordModal(false);
    if (pendingFile) {
      const fileList = new DataTransfer();
      fileList.items.add(pendingFile);
      await handleFileUpload(fileList.files, password);
      setPendingFile(null);
    } else {
    }
  };

  const handleUnlockDocument = async (password: string): Promise<boolean> => {
    if (!unlockingDocId) return false;
    
    try {
      const doc = documents.find(d => d.id === unlockingDocId);
      if (!doc) return false;
      const decryptedContent = await decryptFile(doc.content, password);
      // Open decrypted content in new window
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(`
          <html>
            <head>
              <title>${doc.title}</title>
              <style>
                body { margin: 20px; font-family: Arial, sans-serif; }
                iframe { width: 100%; height: 90vh; border: none; }
              </style>
            </head>
            <body>
              <h2>🔓 ${doc.title}</h2>
              <iframe src="${decryptedContent}"></iframe>
            </body>
          </html>
        `);
      }
      
      setShowUnlockModal(false);
      setUnlockingDocId(null);
      setUnlockingDocTitle('');
      return true;
    } catch (err) {
      console.error('❌ Decryption failed:', err);
      return false;
    }
  };

  const handleDeleteDocument = (docId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDocConfirm(docId.toString());
  };

  const confirmDeleteDocument = async () => {
    if (deleteDocConfirm) {
      try {
        const docId = parseInt(deleteDocConfirm);
        await deleteDocument(docId);
        await reload();
        setDeleteDocConfirm(null);
      } catch (error) {
        console.error('Error deleting document:', error);
        alert(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const cancelDeleteDocument = () => {
    setDeleteDocConfirm(null);
  };

  if (guestInviteMode) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        {virtualRoomOpen && virtualRoomContext ? (
          <StudentVirtualRoom
            tabLabel={virtualRoomContext.tabLabel}
            roomTitle={virtualRoomContext.roomTitle}
            description={virtualRoomContext.description}
            agenda={virtualRoomContext.agenda}
            accessibilityNote={virtualRoomContext.accessibilityNote}
            participants={virtualRoomContext.participants}
            resources={virtualRoomContext.resources}
            roomId={`${activeClassId || 'guest'}-${virtualRoomContext.id}`}
            onClose={handleGuestCloseVirtualRoom}
            roomNotes={roomNotes}
            onNotesChange={setRoomNotes}
            onAgendaChange={handleAgendaChange}
            snackSpots={snackSpots}
            snackLoading={snackLoading}
            onCreateSnack={handleCreateSnackSpot}
            onUpdateSnack={handleUpdateSnackSpot}
            onDeleteSnack={handleDeleteSnackSpot}
            onMarkSnackOrdered={handleMarkSnackOrdered}
          />
        ) : (
          <div className="text-center space-y-3">
            <p className="text-lg font-semibold">Joining room…</p>
            <p className="text-sm text-slate-300">Please wait while we connect you.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
        {/* Notification Panel */}
        {user?.id && <NotificationPanel userId={String(user.id)} />}
        
        {/* Beta Warning Banner - Shows on all browsers */}
        <BetaWarningBanner />
        
        {/* Download Notification Banner */}
        {showDownloadNotification && (
          <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-auto z-50 animate-slide-in">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-4 md:px-6 py-3 md:py-4 rounded-xl shadow-2xl border-2 border-emerald-400 flex items-center gap-2 md:gap-3 animate-bounce">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <NotebookPen className="w-4 h-4 md:w-5 md:h-5" />
              </div>
              <div>
                <p className="font-bold text-xs md:text-sm">Document Downloaded!</p>
                <p className="text-[10px] md:text-xs text-emerald-100">Find it in Documents Hub below ⬇️</p>
              </div>
            </div>
          </div>
        )}

        {/* Debug notification panel removed */}
        
        <div className="min-h-screen overflow-y-auto bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] pt-4 md:pt-12 pb-4 flex items-start justify-center relative"
             style={{
               backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(251,191,36,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(168,85,247,0.08) 0%, transparent 50%)',
             }}>
          
          {/* QUICK ACTIONS - Side Tabs (positioned OUTSIDE all containers to break through) */}
          <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-full max-w-6xl mx-6 h-full">
              {QUICK_ACTIONS_TABS.map((tab, index) => {
                const positions = [
                  { top: 450, right: -130 },   // Music
                  { top: 510, right: -130 },   // Messages
                  { top: 570, right: -130 },   // AI Assistant
                  { top: 630, right: -130 },   // Snack Dial
                  { top: 690, right: -130 },   // Apps
                  { top: 750, right: -130 },   // Budget
                  { top: 810, right: -130 },   // Suggestions
                  { top: 870, right: -130 },   // WHIPT SUMM!
                ];
                const pos = positions[index] || { top: 450 + (index * 60), right: -130 };
                
                const actionContext = {
                  navigate,
                  setShowTodoList,
                  setShowMusicPlayer,
                  setShowMessaging,
                  setIsMessagingMinimized,
                  setShowAIPanel,
                  setShowQuickAccessPanel,
                  setShowSnackPanel,
                  setShowAppsPanel,
                  setShowBudgetAssistant,
                  setShowSuggestions,
                  setShowWhiptSumm,
                  unreadCount: Object.values(unreadMessages).reduce((a, b) => a + b, 0) + adminUnreadMessages,
                };
                
                const badgeCount = tab.badge ? tab.badge(actionContext) : null;
                
                return (
                  <div key={tab.id} className="absolute" style={{ top: `${pos.top}px`, right: `${pos.right}px`, zIndex: panelZIndexes.quickTabs }}>
                    <button
                      onClick={() => { bringToFront('quickTabs'); tab.onClick(actionContext); }}
                      disabled={tab.disabled}
                      className={`pl-6 pr-5 py-4 text-sm font-bold bg-gradient-to-b ${tab.gradient} backdrop-blur-xl ${tab.border} border-2 ${tab.textColor} ${tab.disabled ? 'opacity-60 cursor-not-allowed' : 'hover:translate-x-[5px] hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] hover:scale-105 cursor-pointer'} transition-all duration-300 shadow-2xl flex items-center gap-2 rounded-r-xl pointer-events-auto ${tab.id === 'apps' || tab.id === 'budget' ? 'justify-center' : 'justify-end'}`}
                      style={{
                        width: '165px',
                        clipPath: 'inset(0 0 0 10px)',
                      }}
                      title={tab.disabled ? 'Coming Soon!' : tab.label}
                    >
                    {tab.icon.startsWith('M') ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                      </svg>
                    ) : tab.icon === 'AI' ? (
                      <span className="text-[11px] font-black tracking-tighter bg-gradient-to-r from-indigo-200 to-blue-200 bg-clip-text text-transparent">AI</span>
                    ) : tab.icon === 'WHIPT' ? (
                      <span className="text-[11px] font-black tracking-tight bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent">WHIPT</span>
                    ) : (
                      <span className="text-sm">{tab.icon}</span>
                    )}
                    {tab.id === 'whipt-summ' ? (
                      <div className="flex flex-col items-end leading-tight">
                        <span className="text-[11px] font-black tracking-tight bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent">SUMM!</span>
                        <span className="text-[8px] font-bold text-amber-300/70 tracking-wide">SOON</span>
                      </div>
                    ) : (
                      <span className="font-bold">{tab.label}</span>
                    )}
                    </button>
                    {badgeCount && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-slate-900 animate-bounce" style={{ zIndex: 10000 }}>
                        {badgeCount}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative w-full max-w-6xl mx-6 h-full overflow-y-auto overflow-x-visible rounded-3xl organizer-scroll">

            <div className="relative rounded-2xl md:rounded-[36px] border-2 border-amber-400/30 bg-gradient-to-br from-[#1a0b2d]/95 via-[#0c0418] to-[#05030a]/98 shadow-[0_50px_150px_rgba(251,191,36,0.25),0_20px_60px_rgba(0,0,0,0.8),inset_0_2px_20px_rgba(251,191,36,0.1)] text-white overflow-visible transform-gpu perspective-1000" style={{ zIndex: 10 }}>
              
              {/* 1950s nightclub metallic rings - hidden on mobile */}
              <div className="hidden md:flex pointer-events-none absolute inset-y-12 left-12 flex-col justify-between z-20">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={`ring-${index}`}
                    className="relative block h-9 w-9 rounded-full"
                    style={{
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(251,191,36,0.3), 0 0 20px rgba(251,191,36,0.2)'
                    }}
                  >
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-600 opacity-90" />
                    <span className="absolute inset-[3px] rounded-full bg-gradient-to-br from-slate-800 via-slate-900 to-black shadow-inner" />
                    <span className="absolute inset-[6px] rounded-full bg-gradient-to-br from-amber-400/20 to-transparent" />
                  </div>
                ))}
              </div>

              <div className="relative z-10 px-4 sm:px-8 lg:pl-24 py-7 space-y-5">
                <style>{`
                  @keyframes pulse-alert {
                    0% { opacity: 1; }
                    50% { opacity: 0.35; }
                    100% { opacity: 1; }
                  }
                  @keyframes sweep {
                    0% { transform: translateX(-100%); }
                    50% { transform: translateX(100%); }
                    100% { transform: translateX(100%); }
                  }
                  @keyframes flash-blue {
                    0%, 100% { box-shadow: 0 0 0 rgba(37,99,235,0); }
                    50% { box-shadow: 0 0 30px rgba(37,99,235,0.8), 0 0 60px rgba(37,99,235,0.5); }
                  }
                  @keyframes flash-amber {
                    0%, 100% { box-shadow: 0 0 0 rgba(251,191,36,0); }
                    50% { box-shadow: 0 0 20px rgba(251,191,36,0.4), 0 0 40px rgba(251,191,36,0.25); }
                  }
                  @keyframes flash-emerald {
                    0%, 100% { box-shadow: 0 0 0 rgba(16,185,129,0); }
                    50% { box-shadow: 0 0 30px rgba(16,185,129,0.8), 0 0 60px rgba(16,185,129,0.5); }
                  }
                  @keyframes flash-cyan {
                    0%, 100% { box-shadow: 0 0 0 rgba(6,182,212,0); }
                    50% { box-shadow: 0 0 30px rgba(6,182,212,0.8), 0 0 60px rgba(6,182,212,0.5); }
                  }
                  @keyframes flash-pink {
                    0%, 100% { box-shadow: 0 0 0 rgba(236,72,153,0); }
                    50% { box-shadow: 0 0 30px rgba(236,72,153,0.8), 0 0 60px rgba(236,72,153,0.5); }
                  }
                  @keyframes ticker-scroll {
                    0% { transform: translateY(0); }
                    20% { transform: translateY(0); }
                    25% { transform: translateY(-100%); }
                    45% { transform: translateY(-100%); }
                    50% { transform: translateY(-200%); }
                    70% { transform: translateY(-200%); }
                    75% { transform: translateY(-300%); }
                    95% { transform: translateY(-300%); }
                    100% { transform: translateY(-400%); }
                  }
                `}</style>

                {/* Fixed Header - Kindle Frame */}
                <div className="flex flex-col min-h-screen items-stretch">
                  {/* FIXED: Compact Header Section - Embedded into organizer background */}
                  <div className="sticky top-0 px-3 sm:px-4 py-2 bg-[#050309] border-b border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.3)] transition-all duration-300 z-50 overflow-visible">
                    {/* Mobile: collapsed bar */}
                    <div className="flex items-center justify-between md:hidden">
                      <div className="flex items-center gap-2">
                        <h1 className="text-xl font-black tracking-tight bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent">
                          WHIPT
                        </h1>
                        <span className="text-[11px] text-purple-200/80">Where'd I Put That?</span>
                      </div>
                      <button
                        onClick={() => setMobileHeaderExpanded((v) => !v)}
                        className="px-3 py-1 rounded-full border border-amber-300/40 text-amber-100 text-xs bg-amber-500/10"
                        aria-expanded={mobileHeaderExpanded}
                      >
                        {mobileHeaderExpanded ? '▲' : '▼'}
                      </button>
                    </div>

                    {/* Expanded header content - only shows on mobile when expanded OR always on desktop */}
                    <div className={`${mobileHeaderExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'} md:max-h-none md:opacity-100 md:pointer-events-auto overflow-hidden transition-all duration-300 md:space-y-3`}>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex flex-col gap-0.5">
                        {/* WHIPT - Main Brand Name (compact) */}
                        <h1 className="text-2xl md:text-3xl font-black tracking-tight bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent drop-shadow-[0_4px_12px_rgba(251,191,36,0.6)]">
                          WHIPT
                        </h1>
                        
                        {/* Where'd I Put That? - Tagline (smaller) */}
                        <p className="text-xs text-purple-200/80 font-medium italic -mt-0.5">Where'd I Put That?</p>
                        
                        {/* Class Name - Compact - Hidden for Home/Work/Travel */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <h2 className={`text-sm md:text-base font-semibold text-white ${activeCategory && ['home', 'work', 'travel'].includes(activeCategory.id) ? 'hidden' : ''}`}>
                            {activeClass?.title || 'Select a Class'}
                          </h2>
                        
                        {/* New Class Button - Compact */}
                        <button
                          onClick={() => setShowAddClassModal(true)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-400/40 bg-gradient-to-r from-emerald-600/20 to-green-600/20 hover:from-emerald-600/40 hover:to-green-600/40 transition-all text-xs"
                        >
                          <Plus className="w-3 h-3 text-emerald-300" />
                          <span className="font-semibold text-emerald-200">New</span>
                        </button>
                        
                        {activeClass && (
                          <button
                            onClick={() => {
                              const newClassName = window.prompt("Edit class name:", activeClass.title);
                              if (!newClassName?.trim()) return;
                              
                              const newCourseName = window.prompt("Edit course name:", activeClass.course);
                              const newInstructor = window.prompt("Edit instructor name:", activeClass.instructor);
                              
                              setClassNotebooks(prev => {
                                const updated = prev.map(notebook => 
                                  notebook.id === activeClass.id
                                    ? {
                                        ...notebook,
                                        title: newClassName.trim(),
                                        course: newCourseName?.trim() || notebook.course,
                                        instructor: newInstructor?.trim() || notebook.instructor
                                      }
                                    : notebook
                                );
                                if (user?.id) {
                                  saveNotebooks(user.id, updated);
                                }
                                return updated;
                              });
                            }}
                            className="text-sm text-slate-400 hover:text-white transition-colors"
                            title="Edit class information"
                          >
                            ✏️
                          </button>
                        )}
                        
                        {activeClass && classNotebooks.length > 1 && (
                          <button
                            onClick={() => {
                              const confirmation = window.prompt(`⚠️ To delete "${activeClass.title}", type DELETE in all caps:`);
                              if (confirmation !== 'DELETE') {
                                if (confirmation !== null) {
                                  alert('Delete cancelled. You must type DELETE exactly.');
                                }
                                return;
                              }
                              
                              const updatedNotebooks = classNotebooks.filter(nb => nb.id !== activeClass.id);
                              setClassNotebooks(updatedNotebooks);
                              if (user?.id) {
                                saveNotebooks(user.id, updatedNotebooks);
                              }
                              
                              // Switch to first remaining class
                              if (updatedNotebooks.length > 0) {
                                setActiveClassId(updatedNotebooks[0].id);
                                setActiveCategoryId(updatedNotebooks[0].categories[0].id);
                              }
                            }}
                            className="text-sm text-red-400 hover:text-red-300 transition-colors"
                            title="Delete this class"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Course Info & Next Milestone - Hide for Home/Work/Travel tabs */}
                      {activeCategory && !['home', 'work', 'travel'].includes(activeCategory.id) && (
                      <div className="flex flex-col gap-1 text-xs md:text-sm text-slate-200">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          <span className="font-semibold text-emerald-200">{summary.course}</span>
                          <span className="text-slate-400 hidden md:inline">•</span>
                          <span className="text-purple-200">{summary.instructor}</span>
                          <span className="text-slate-400 hidden md:inline">•</span>
                          <span className="text-cyan-200 text-xs">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <div className="text-xs text-amber-200/80">
                          Next: <span className="font-semibold">{summary.nextItemLabel}</span> • Due {summary.dueDisplay} • {summary.upcomingCount} items
                        </div>
                      </div>
                      )}
                    </div>
                    
                    {/* Streamlined Header Controls - Desktop Only */}
                    <div className="flex items-center justify-end gap-3">
                      {/* User Avatar - Links to Settings */}
                      {user?.avatar ? (
                        <img 
                          src={user.avatar} 
                          alt={user.name}
                          onClick={() => navigate('/organizer/settings')}
                          className="w-12 h-12 rounded-full object-cover border-2 border-purple-400/50 cursor-pointer hover:border-purple-300 transition-all hover:scale-105"
                          title="Settings"
                        />
                      ) : (
                        <div 
                          onClick={() => navigate('/organizer/settings')}
                          className={`w-12 h-12 rounded-full ${user?.profile_color || 'bg-purple-500'} flex items-center justify-center text-white text-base font-bold border-2 border-purple-400/50 cursor-pointer hover:border-purple-300 transition-all hover:scale-105`}
                          title="Settings"
                        >
                          {user?.name?.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      
                      {/* Online Status Dropdown */}
                      <OnlineStatusDropdown />
                      
                      {/* Refresh Data Button */}
                      <button
                        type="button"
                        onClick={refreshDataFromDatabase}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600/20 border border-green-500/30 hover:bg-green-600/30 transition-all group"
                        title="Refresh all data from database"
                      >
                        <RefreshCcw className="w-4 h-4 text-green-400 group-hover:rotate-180 transition-transform duration-500" />
                        <span className="text-xs text-green-300 font-semibold">Refresh</span>
                      </button>
                      
                      {/* Settings Button */}
                      <button
                        type="button"
                        onClick={() => navigate('/organizer/settings')}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/10 hover:bg-black/40 transition-all"
                        title="Settings"
                      >
                        <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      
                      {/* Auto-Save Indicator */}
                      <AutoSaveIndicator 
                        isSaving={autoSaveStatus.isSaving}
                        lastSaved={autoSaveStatus.lastSaved}
                        error={autoSaveStatus.error}
                        className="px-3 py-2 rounded-lg bg-black/30 border border-white/10"
                      />
                    </div>
                  </div>
                  {/* End: Header top buttons section */}
                </div>
                </div>
                {/* End: Header Section */}

                  {/* SCROLLABLE: Content Area - Kindle Pages */}
                  <div className="flex-1 px-4 sm:px-6">
                    {/* Interactive Calendar Popup - Tailored by Event Type */}
                    {calendarOpen && (
                  <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
                    <div className="rounded-t-3xl md:rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-900/90 to-orange-900/95 p-6 shadow-[0_40px_120px_rgba(251,191,36,0.3)] backdrop-blur-md w-full max-w-2xl h-[90vh] md:h-auto max-h-[90vh] overflow-y-auto" style={{ animation: 'flash-amber 0.5s ease-out 1' }}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-amber-100">Add to Calendar</h3>
                      <button
                        onClick={() => {
                          setCalendarOpen(false);
                          setPrefilledDate(null);
                          setEventType('assignment');
                          setCalendarTypeLocked(false);
                          setMeetingType('in-person');
                          setSelectedCategoryForForm('');
                          setSelectedClassForForm('');
                        }}
                        className="text-amber-200 hover:text-white text-xl"
                      >
                        ✕
                      </button>
                    </div>
                    
                    {/* Event Type Selector */}
                    {!calendarTypeLocked ? (
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <button
                        type="button"
                        onClick={() => setEventType('assignment')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          eventType === 'assignment'
                            ? 'bg-amber-500/20 border-amber-400 shadow-lg'
                            : 'bg-black/20 border-amber-400/20 hover:border-amber-400/40'
                        }`}
                      >
                        <div className="text-3xl mb-2">📝</div>
                        <div className="text-sm font-semibold text-amber-100">Assignment</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEventType('meeting')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          eventType === 'meeting'
                            ? 'bg-blue-500/20 border-blue-400 shadow-lg'
                            : 'bg-black/20 border-amber-400/20 hover:border-amber-400/40'
                        }`}
                      >
                        <div className="text-3xl mb-2">📅</div>
                        <div className="text-sm font-semibold text-amber-100">Meeting</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEventType('reminder')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          eventType === 'reminder'
                            ? 'bg-rose-500/20 border-rose-400 shadow-lg'
                            : 'bg-black/20 border-amber-400/20 hover:border-amber-400/40'
                        }`}
                      >
                        <div className="text-3xl mb-2">⏰</div>
                        <div className="text-sm font-semibold text-amber-100">Reminder</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEventType('event')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          eventType === 'event'
                            ? 'bg-emerald-500/20 border-emerald-400 shadow-lg'
                            : 'bg-black/20 border-amber-400/20 hover:border-amber-400/40'
                        }`}
                      >
                        <div className="text-3xl mb-2">🎉</div>
                        <div className="text-sm font-semibold text-amber-100">Event</div>
                      </button>
                    </div>
                    ) : (
                      <div className="mb-6 rounded-xl border border-amber-400/30 bg-black/20 px-4 py-3 text-sm text-amber-100 font-semibold">
                        Creating: {eventType.charAt(0).toUpperCase() + eventType.slice(1)}
                      </div>
                    )}
                    
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      const categoryId = formData.get('category') as string;
                      const title = formData.get('title') as string;
                      const description = formData.get('description') as string;
                      const dueDate = formData.get('dueDate') as string;
                      const dueTime = formData.get('dueTime') as string;
                      const location = formData.get('location') as string;

                      if (!title.trim() || !dueDate || !categoryId) {
                        alert('Please fill in title, category, and due date');
                        return;
                      }

                      // Combine date + time into ISO string
                      const dateTimeString = dueTime 
                        ? `${dueDate}T${dueTime}:00` 
                        : `${dueDate}T23:59:00`;

                      // Create new item
                      const newItem = {
                        id: Date.now(),
                        title: title.trim(),
                        notes: description.trim(),
                        dueDate: dateTimeString,
                        status: 'upcoming' as const,
                        eventType: eventType, // Add event type
                        created_at: new Date().toISOString()
                      };

                      // NOTE: We don't create a calendar_event row here because the item
                      // is being added to notebooks, which the calendar already displays.
                      // Creating both would cause duplicates.

                      // Add to the appropriate category in notebooks
                      const updatedNotebooks = classNotebooks.map(notebook => {
                        if (notebook.id !== activeClassId) return notebook;
                        
                        return {
                          ...notebook,
                          categories: notebook.categories.map(cat => {
                            if (cat.id !== categoryId) return cat;
                            
                            return {
                              ...cat,
                              buckets: {
                                ...cat.buckets,
                                upcoming: [...(cat.buckets?.upcoming || []), newItem]
                              }
                            };
                          })
                        };
                      });

                      setClassNotebooks(updatedNotebooks);
                      
                      // CRITICAL: Save to database immediately
                      if (user?.id) {
                        try {
                          await saveNotebooks(user.id, updatedNotebooks);
                          console.log('✅ Item saved to database:', title);
                        } catch (error) {
                          console.error('❌ Failed to save item:', error);
                          alert('Failed to save item. Please try again.');
                          return;
                        }
                      }
                      
                      // If eventType is 'meeting', also add to Scheduled Virtual Meetings
                      if (eventType === 'meeting') {
                        const classForEvent = selectedClassForForm && selectedClassForForm !== 'none' 
                          ? classNotebooks.find(nb => nb.id === selectedClassForForm)
                          : classNotebooks.find(nb => nb.id === activeClassId);
                        
                        const newSession = {
                          sessionId: `session-${Date.now()}`,
                          classId: classForEvent?.id || activeClassId,
                          title: title.trim(),
                          date: dueDate, // Store as YYYY-MM-DD
                          time: dueTime || '23:59',
                          duration: '60 min',
                          agenda: description.trim() || '',
                          participants: [],
                          roomType: meetingType === 'virtual' ? 'study' : undefined,
                          attendees: 0
                        };
                        
                        const existingSessions = JSON.parse(localStorage.getItem('scheduledSessions') || '[]');
                        const updated = [...existingSessions, newSession];
                        localStorage.setItem('scheduledSessions', JSON.stringify(updated));
                        setScheduledSessions(updated);
                        
                        // Also save to database
                        if (user?.id) {
                          await saveScheduledSessions(user.id, updated);
                        }
                        
                        console.log('✅ Added meeting to Scheduled Virtual Meetings');
                      }
                      
                      // Switch to the category tab and set to upcoming
                      setActiveCategoryId(categoryId);
                      setActivePhase('upcoming');

                      // Close calendar and clear prefilled date
                      setCalendarOpen(false);
                      setPrefilledDate(null);
                      setEventType('assignment');
                      setCalendarTypeLocked(false);
                      setMeetingType('in-person');
                      setSelectedCategoryForForm('');
                      setSelectedClassForForm('');

                      // Reset form
                      e.currentTarget.reset();
                    }} className="space-y-4">
                      
                      {/* Category Selection */}
                      <div>
                        <label className="block text-sm font-semibold text-amber-200 mb-2">
                          Which tab should this appear in? *
                        </label>
                        <select
                          name="category"
                          required
                          value={selectedCategoryForForm}
                          onChange={(e) => setSelectedCategoryForForm(e.target.value)}
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        >
                          <option value="">Select a tab...</option>
                          {activeClass?.categories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Class Selection - Only show for academic tabs */}
                      {selectedCategoryForForm && ['discussion', 'assignment', 'experiential', 'journal'].includes(selectedCategoryForForm) && (
                        <div>
                          <label className="block text-sm font-semibold text-amber-200 mb-2">
                            Which class is this for? *
                          </label>
                          <select
                            name="classId"
                            required
                            value={selectedClassForForm}
                            onChange={(e) => setSelectedClassForForm(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                          >
                            <option value="">Select a class...</option>
                            {classNotebooks.map(notebook => (
                              <option key={notebook.id} value={notebook.id}>
                                {notebook.courseCode || notebook.title}
                              </option>
                            ))}
                            <option value="none">NONE (Personal)</option>
                          </select>
                        </div>
                      )}

                      {/* Title */}
                      <div>
                        <label className="block text-sm font-semibold text-amber-200 mb-2">
                          Title *
                        </label>
                        <input
                          type="text"
                          name="title"
                          required
                          placeholder="e.g., Chapter 5 Discussion, Final Project, Lab Report"
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-white placeholder-amber-300/40 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-semibold text-amber-200 mb-2">
                          Description / Notes
                        </label>
                        <textarea
                          name="description"
                          rows={3}
                          placeholder="What is this about? Any specific requirements or notes..."
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-white placeholder-amber-300/40 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 resize-none"
                        />
                      </div>

                      {/* Due Date */}
                      <div>
                        <label className="block text-sm font-semibold text-amber-200 mb-2">
                          Due Date *
                        </label>
                        <input
                          type="date"
                          name="dueDate"
                          required
                          defaultValue={prefilledDate || ''}
                          key={prefilledDate || 'no-date'} 
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        />
                      </div>

                      {/* Due Time */}
                      <div>
                        <label className="block text-sm font-semibold text-amber-200 mb-2">
                          Due Time (Optional)
                        </label>
                        <input
                          type="time"
                          name="dueTime"
                          defaultValue="23:59"
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-amber-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        />
                      </div>

                      {/* Location (for meetings) */}
                      <div>
                        <label className="block text-sm font-semibold text-amber-200 mb-2">
                          Location / Link (Optional)
                        </label>
                        <input
                          type="text"
                          name="location"
                          placeholder="e.g., Room 305, Zoom link, etc."
                          className="w-full px-4 py-2 rounded-lg bg-black/40 border border-amber-400/30 text-white placeholder-amber-300/40 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        />
                      </div>

                      {/* Submit Buttons */}
                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCalendarOpen(false);
                            setCalendarTypeLocked(false);
                          }}
                          className="flex-1 px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-bold transition-all shadow-lg hover:shadow-xl"
                        >
                          Add Item
                        </button>
                      </div>
                    </form>
                  </div>
                  </div>
                )}

                {/* Mobile tab toggle / gesture target */}
                <div className="md:hidden relative pb-3">
                  <div
                    className="absolute left-0 right-0 -top-2 h-8 cursor-pointer"
                    onPointerDown={onTabSwipeStart}
                    onPointerMove={onTabSwipeMove}
                    onPointerUp={onTabSwipeEnd}
                  />
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setMobileTabsVisible((v) => !v)}
                      className="px-4 py-2 rounded-full text-xs font-semibold border border-amber-300/40 bg-amber-500/10 text-amber-100 shadow-lg"
                    >
                      {mobileTabsVisible ? 'Hide tabs' : 'Show tabs'}
                    </button>
                  </div>
                </div>

                {/* Class Tabs - Horizontal Scrollable - STICKY */}
                <div
                  data-tutorial="class-tabs"
                  className={`sticky top-0 z-50 bg-gradient-to-b from-[#050309] via-[#050309] to-transparent pt-4 pb-6 -mx-4 px-4 backdrop-blur-sm ${mobileTabsVisible ? '' : 'hidden'} md:block`}
                  onPointerDown={onTabSwipeStart}
                  onPointerMove={onTabSwipeMove}
                  onPointerUp={onTabSwipeEnd}
                >
                <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {/* Show Active Classes button */}
                  <button
                    onClick={() => setShowArchivedClasses(!showArchivedClasses)}
                    className="flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold border border-white/30 bg-white/10 text-white hover:bg-white/20 transition-all"
                    title={showArchivedClasses ? "Hide Archived Classes" : "Show Archived Classes"}
                  >
                    {showArchivedClasses ? '🗃️ Hide Archived' : '📚 Active Only'}
                  </button>
                  
                  {classNotebooks
                    .filter(nb => showArchivedClasses || !nb.archived) // Filter archived classes
                    .map((notebook, index) => {
                    const isActive = notebook.id === activeClassId;
                    
                    // Color rotation for tabs
                    const colors = [
                      { from: 'from-purple-600', to: 'to-indigo-600', shadow: 'shadow-purple-500/50' },
                      { from: 'from-blue-600', to: 'to-cyan-600', shadow: 'shadow-blue-500/50' },
                      { from: 'from-emerald-600', to: 'to-teal-600', shadow: 'shadow-emerald-500/50' },
                      { from: 'from-amber-600', to: 'to-orange-600', shadow: 'shadow-amber-500/50' },
                      { from: 'from-rose-600', to: 'to-pink-600', shadow: 'shadow-rose-500/50' },
                      { from: 'from-violet-600', to: 'to-purple-600', shadow: 'shadow-violet-500/50' },
                    ];
                    const colorSet = colors[index % colors.length];
                    
                    return (
                      <div key={notebook.id} className="relative flex-shrink-0 group">
                        <button
                          onClick={() => {
                            if (notebook.id !== activeClassId) {
                              // Add slide animation
                              const contentArea = document.querySelector('.notebook-content-main');
                              if (contentArea) {
                                contentArea.classList.add('animate-slide-out');
                                setTimeout(() => contentArea.classList.remove('animate-slide-out'), 300);
                              }
                            }
                            
                            // Switch class immediately
                            setActiveClassId(notebook.id);
                            setActiveCategoryId(notebook.categories[0]?.id || '');
                          }}
                          className={`px-6 py-3 text-sm font-semibold border-2 transition-all relative overflow-hidden flex items-center gap-2 ${
                            isActive
                              ? `border-white bg-gradient-to-r ${colorSet.from} ${colorSet.to} text-white shadow-lg ${colorSet.shadow} scale-110`
                              : 'border-white/20 bg-white/5 text-white/60 hover:text-white hover:border-white/40'
                          }`}
                          style={{
                            clipPath: isActive 
                              ? 'polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%)'
                              : 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)'
                          }}
                        >
                          {/* Sweep animation overlay */}
                          <div 
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                            style={{
                              animation: 'sweep 3s ease-in-out infinite',
                              transform: 'translateX(-100%)'
                            }}
                          />
                          {/* Teacher Photo */}
                          {notebook.teacherPhoto ? (
                            <img 
                              src={notebook.teacherPhoto} 
                              alt="Teacher" 
                              className="relative z-10 w-8 h-8 rounded-full object-cover border-2 border-white/30"
                            />
                          ) : (
                            <div className="relative z-10 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg">
                              {notebook.title?.[0] || '📚'}
                            </div>
                          )}
                          <span className="relative z-10">{notebook.title}</span>
                        </button>
                        {/* Archive/Unarchive button - Smaller, less intrusive */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            
                            // Confirmation for archiving
                            if (!notebook.archived && !window.confirm(`Archive class "${notebook.title}"?\n\nYou can restore it anytime by clicking "📚 Active Only" → "🗃️ Hide Archived"`)) {
                              return;
                            }
                            
                            const updated = classNotebooks.map(nb => 
                              nb.id === notebook.id ? { ...nb, archived: !nb.archived } : nb
                            );
                            setClassNotebooks(updated);
                            
                            // Auto-show archived classes when archiving so user can see where it went
                            if (!notebook.archived) {
                              setShowArchivedClasses(true);
                            }
                            
                            if (user?.id) {
                              saveNotebooks(user.id, updated);
                            }
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500/80 hover:bg-amber-600 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-50"
                          title={notebook.archived ? "Unarchive class" : "Archive class"}
                        >
                          {notebook.archived ? '↑' : '↓'}
                        </button>
                        {/* Delete class button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete class "${notebook.title}" and all its items?`)) {
                              const updated = classNotebooks.filter(nb => nb.id !== notebook.id);
                              setClassNotebooks(updated);
                              if (user?.id) {
                                saveNotebooks(user.id, updated);
                              }
                              // Switch to first remaining class or empty
                              if (updated.length > 0) {
                                setActiveClassId(updated[0].id);
                                setActiveCategoryId(updated[0].categories[0]?.id || '');
                              }
                            }
                          }}
                          className="absolute -top-1 right-3 w-4 h-4 rounded-full bg-red-500/80 hover:bg-red-600 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-50"
                          title="Delete class"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
                </div>

                {/* Notebook interface with animation container */}
                <section className="notebook-content-main rounded-[36px] border-2 border-amber-500/20 bg-gradient-to-br from-[#2a1204]/90 via-[#1a0a02]/95 to-[#0a0501]/98 shadow-[0_40px_100px_rgba(251,191,36,0.15),0_20px_50px_rgba(0,0,0,0.6),inset_0_1px_2px_rgba(251,191,36,0.2)] px-4 sm:px-6 py-6 space-y-6 transform-gpu transition-all duration-300">
                
                <style>{`
                  @keyframes slideOut {
                    0% { transform: translateX(0); opacity: 1; }
                    100% { transform: translateX(-100px); opacity: 0; }
                  }
                  @keyframes slideIn {
                    0% { transform: translateX(100px); opacity: 0; }
                    100% { transform: translateX(0); opacity: 1; }
                  }
                  .animate-slide-out {
                    animation: slideOut 0.3s ease-out forwards;
                  }
                  .animate-slide-in {
                    animation: slideIn 0.5s ease-out forwards;
                  }
                `}</style>

          {/* Vertical Scrolling Ticker */}
          <div className="relative h-16 overflow-hidden rounded-[30px] border border-amber-300/30 bg-gradient-to-b from-[#5b1111]/40 via-[#3a0a0a]/30 to-[#1f0505]/45 backdrop-blur-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-10px_24px_rgba(0,0,0,0.35),0_10px_30px_rgba(0,0,0,0.4)]">
            <div className="pointer-events-none absolute inset-0 rounded-[30px] ring-1 ring-white/20" />
            <div className="pointer-events-none absolute inset-x-4 top-1 h-4 rounded-full bg-white/25 blur-md opacity-70" />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#120606]/85 via-[#120606]/35 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#120606]/85 via-[#120606]/35 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-black/35 to-transparent" />
            <div
              className="absolute inset-0 flex flex-col"
              style={{
                animation: hasUpcomingItems ? 'ticker-scroll 20s linear infinite' : 'none',
                willChange: hasUpcomingItems ? 'transform' : undefined,
                transform: 'perspective(900px) rotateX(1.5deg)',
                transformOrigin: 'center center'
              }}
            >
              {/* Create 5 copies for smooth loop (4 categories + 1 repeat) */}
              {[...Array(5)].map((_, cycleIndex) => (
                <div key={cycleIndex}>
                  {hasUpcomingItems ? activeClass?.categories.filter(c => c.id !== 'snack').map((category) => {
                    const items = timelineOverrides[activeClassId]?.[category.id] || category.buckets?.upcoming || [];
                    const upcomingItem = items.find(item => item.status === "upcoming" && item.dueDate);
                    const colorClass = getCategoryColor(category.id);
                    return (
                      <div
                        key={`${cycleIndex}-${category.id}`}
                        className="relative h-16 flex items-center justify-between px-6 cursor-pointer hover:bg-amber-600/20 transition-colors"
                        onClick={() => {
                          setActiveCategoryId(category.id);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-base font-bold tracking-wide ${colorClass}`}>
                            {category.label}
                          </span>
                          {upcomingItem && (
                            <span className={`text-sm ${colorClass} opacity-80 flex items-center gap-1`}>
                              <span className="text-base">{getEventTypeIcon(upcomingItem.eventType)}</span>
                              • {upcomingItem.title}
                            </span>
                          )}
                        </div>
                        {upcomingItem?.dueDate && (
                          <div className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4 text-amber-300" />
                            <span className="text-sm text-amber-200 font-semibold">
                              {formatDueDisplay(upcomingItem.dueDate)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    <div className="h-16 flex items-center justify-between px-6">
                      <div className="flex items-center gap-3">
                        <span className="text-base font-bold tracking-wide text-amber-100">
                          {activeClass?.title || 'No Class Selected'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-amber-300" />
                        <span className="text-sm text-amber-200 font-semibold">
                          {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-white/10 space-y-4">
            {/* Add Schoolwork & Import Calendar Buttons - Positioned Above Tabs */}
            <div className="flex justify-center items-center gap-4 relative">
              {/* Pulsing glowing background for Add Schoolwork */}
              <div className="absolute left-1/2 transform -translate-x-16">
                <div 
                  className="w-48 h-12 bg-amber-500/15 rounded-full blur-xl"
                  style={{
                    animation: 'pulse-glow 2s ease-in-out infinite'
                  }}
                ></div>
              </div>
              
              {/* Pulsing glowing background for Import Calendar */}
              <div className="absolute left-1/2 transform translate-x-16">
                <div 
                  className="w-48 h-12 bg-cyan-500/15 rounded-full blur-xl"
                  style={{
                    animation: 'pulse-glow 2s ease-in-out infinite'
                  }}
                ></div>
              </div>
              
              {/* Add Schoolwork Button */}
              <button
                type="button"
                onClick={() => setCalendarOpen(!calendarOpen)}
                className="relative flex items-center gap-2 px-6 py-3 rounded-full border-2 border-amber-400/40 bg-gradient-to-r from-amber-600/20 to-orange-600/20 hover:from-amber-600/40 hover:to-orange-600/40 transition-all shadow-lg hover:shadow-amber-500/50"
                style={{
                  animation: (hasUpcomingItems && !calendarOpen) ? 'flash-amber 2s ease-in-out infinite' : 'none'
                }}
              >
                <CalendarDays className="w-5 h-5 text-amber-300" />
                <span className="text-sm font-semibold text-amber-200">Add Schoolwork</span>
              </button>
              
              {/* Import Calendar Button */}
              <button
                onClick={() => setShowCalendarImport(true)}
                className="relative flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-cyan-400/40 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 hover:from-cyan-600/40 hover:to-blue-600/40 transition-all shadow-lg hover:shadow-cyan-500/50"
                title="Import events from Google Calendar or ICS file"
                style={{
                  animation: 'flash-cyan 2s ease-in-out infinite'
                }}
              >
                <Calendar className="w-4 h-4 text-cyan-300" />
                <span className="text-xs font-semibold text-cyan-200">Import Calendar</span>
              </button>
            </div>

            {/* Category Tabs */}
            <div className="space-y-3">
              <div className={`${mobileTabsVisible ? 'flex' : 'hidden'} md:flex flex-wrap items-center gap-3`}>
              {activeClass?.categories
                .map((category) => {
                const isActive = activeCategory?.id === category.id;
                const nextDue = getCategoryNextDue(category);
                const Icon = category.icon;
                const isSnack = category.id === "snack";
                
                // Define distinct colors for each tab
                const tabColors = {
                  discussion: {
                    active: "bg-gradient-to-br from-blue-600 to-blue-800 border-blue-400 shadow-[0_8px_24px_rgba(37,99,235,0.4)]",
                    inactive: "bg-blue-900/20 border-blue-700/30 hover:border-blue-500/50",
                    text: "text-blue-100"
                  },
                  assignment: {
                    active: "bg-gradient-to-br from-amber-500 to-orange-700 border-amber-400 shadow-[0_8px_24px_rgba(251,191,36,0.4)]",
                    inactive: "bg-amber-900/20 border-amber-700/30 hover:border-amber-500/50",
                    text: "text-amber-100"
                  },
                  experiential: {
                    active: "bg-gradient-to-br from-emerald-600 to-emerald-800 border-emerald-400 shadow-[0_8px_24px_rgba(16,185,129,0.4)]",
                    inactive: "bg-emerald-900/20 border-emerald-700/30 hover:border-emerald-500/50",
                    text: "text-emerald-100"
                  },
                  journal: {
                    active: "bg-gradient-to-br from-purple-600 to-purple-800 border-purple-400 shadow-[0_8px_24px_rgba(168,85,247,0.4)]",
                    inactive: "bg-purple-900/20 border-purple-700/30 hover:border-purple-500/50",
                    text: "text-purple-100"
                  },
                  video: {
                    active: "bg-gradient-to-br from-pink-500 to-pink-700 border-pink-400 shadow-[0_8px_24px_rgba(236,72,153,0.4)]",
                    inactive: "bg-pink-900/20 border-pink-700/30 hover:border-pink-500/50",
                    text: "text-pink-100"
                  },
                  home: {
                    active: "bg-gradient-to-br from-rose-500 to-rose-700 border-rose-400 shadow-[0_8px_24px_rgba(244,63,94,0.4)]",
                    inactive: "bg-rose-900/20 border-rose-700/30 hover:border-rose-500/50",
                    text: "text-rose-100"
                  },
                  work: {
                    active: "bg-gradient-to-br from-indigo-600 to-indigo-800 border-indigo-400 shadow-[0_8px_24px_rgba(99,102,241,0.4)]",
                    inactive: "bg-indigo-900/20 border-indigo-700/30 hover:border-indigo-500/50",
                    text: "text-indigo-100"
                  },
                  travel: {
                    active: "bg-gradient-to-br from-sky-500 to-sky-700 border-sky-400 shadow-[0_8px_24px_rgba(14,165,233,0.4)]",
                    inactive: "bg-sky-900/20 border-sky-700/30 hover:border-sky-500/50",
                    text: "text-sky-100"
                  },
                  snack: {
                    active: "bg-gradient-to-br from-pink-500 to-rose-600 border-pink-400 shadow-[0_8px_24px_rgba(236,72,153,0.4)]",
                    inactive: "bg-pink-900/20 border-pink-700/30 hover:border-pink-500/50",
                    text: "text-pink-100"
                  }
                };
                
                const colors = tabColors[category.id] || tabColors.discussion;
                const opacity = isActive || isSnack ? "opacity-100" : "opacity-40 hover:opacity-70";
                
                // Check if this is a default tab (can't delete)
                const isDefaultTab = ['discussion', 'assignment', 'experiential', 'journal', 'video', 'home', 'work', 'travel', 'snack'].includes(category.id);
                
                return (
                  <div key={category.id} className="relative group">
                    <button
                      type="button"
                      onClick={() => {
                        // Home/Work/Travel go directly to category view (no overlay)
                        if (['home', 'work', 'travel'].includes(category.id)) {
                          setActiveCategoryId(category.id);
                          setShowEverythingMode(false);
                          setActivePhase("upcoming");
                          setCategoryExpanded(prev => ({ ...prev, [category.id]: true }));
                        }
                        // Open overlay for class tabs
                        else if (['discussion', 'assignment', 'experiential', 'journal', 'video'].includes(category.id)) {
                          setOverlayTabData({
                            type: category.id as any,
                            className: activeClass?.title || activeClass?.name || 'Class',
                            classId: activeClassId,
                            categoryId: category.id
                          });
                          setShowTabOverlay(true);
                        } else {
                          // For other tabs (like snack), use old behavior
                          setActiveCategoryId(category.id);
                          setShowEverythingMode(false);
                          setActivePhase("upcoming");
                          setCategoryExpanded(prev => ({ ...prev, [category.id]: true }));
                        }
                      }}
                      className={`flex items-center gap-3 px-5 py-3 text-left transition-all duration-300 ${
                        isActive ? colors.active : colors.inactive
                      } ${opacity} ${isActive ? 'scale-105 z-10' : 'scale-100'}`}
                      style={{
                        clipPath: isActive 
                          ? 'polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%)'
                          : 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)'
                      }}
                    >
                      <Icon className={`w-5 h-5 ${colors.text} ${isActive ? 'drop-shadow-lg' : ''}`} />
                      <div>
                        <p className={`text-sm font-bold ${colors.text}`}>{category.label}</p>
                        {/* Hide class code for Home/Work/Travel tabs */}
                        {!['home', 'work', 'travel'].includes(category.id) && (
                          <p className={`text-[10px] ${colors.text} opacity-60 mt-0.5`}>
                            {activeClass?.courseCode || activeClass?.code || activeClass?.title?.split(' - ')[0] || activeClass?.title || ''}
                          </p>
                        )}
                        <p className={`text-[10px] ${colors.text} opacity-80`}>
                          {nextDue ? formatDueDisplay(nextDue) : "No due dates"}
                        </p>
                      </div>
                    </button>
                    {!isDefaultTab && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newLabel = window.prompt(`Rename "${category.label}" tab:`, category.label);
                            if (newLabel && newLabel.trim() && newLabel !== category.label) {
                              setClassNotebooks(prev =>
                                prev.map(notebook =>
                                  notebook.id === activeClass?.id
                                    ? {
                                        ...notebook,
                                        categories: notebook.categories.map(cat =>
                                          cat.id === category.id
                                            ? { ...cat, label: newLabel.trim() }
                                            : cat
                                        )
                                      }
                                    : notebook
                                )
                              );
                            }
                          }}
                          className="absolute -top-1 -right-7 w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-blue-600"
                          title="Rename tab"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const confirmation = window.prompt(`⚠️ To delete "${category.label}" tab, type DELETE in all caps:`);
                            if (confirmation !== 'DELETE') {
                              if (confirmation !== null) {
                                alert('Delete cancelled. You must type DELETE exactly.');
                              }
                              return;
                            }
                            const updated = classNotebooks.map(notebook =>
                              notebook.id === activeClass?.id
                                ? {
                                    ...notebook,
                                    categories: notebook.categories.filter(cat => cat.id !== category.id)
                                  }
                                : notebook
                            );
                            setClassNotebooks(updated);
                            // Save to database
                            if (user) {
                              saveNotebooks(user.id, updated);
                            }
                            // Switch to first category if deleting active one
                            if (isActive && updated.find(n => n.id === activeClass?.id)?.categories[0]) {
                              setActiveCategoryId(updated.find(n => n.id === activeClass?.id)!.categories[0].id);
                            }
                          }}
                          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                          title="Delete tab"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                onClick={handleAddNotebookTab}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] border border-dashed border-white/30 rounded-full px-3 py-1 text-amber-100 hover:text-white"
              >
                <Plus className="w-3 h-3" /> Add Tab
              </button>
              </div>
            </div>

            {/* Main Upcoming/Completed Panel - MOVED HERE FOR VISIBILITY */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2">
                {["upcoming", "completed"].map((phase) => (
                  <button
                    key={phase}
                    type="button"
                    onClick={() => setActivePhase(phase as "upcoming" | "completed")}
                    className={
                      "px-4 py-1.5 rounded-full text-xs font-semibold border transition-all shadow-lg " +
                      (activePhase === phase
                        ? phase === "upcoming"
                          ? "border-cyan-400/60 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 text-cyan-200 shadow-cyan-500/30"
                          : "border-emerald-400/60 bg-gradient-to-r from-emerald-500/30 to-green-500/30 text-emerald-200 shadow-emerald-500/30"
                        : "border-white/15 text-amber-100/60 hover:text-amber-100")
                    }
                  >
                    {phase === "upcoming" ? "Upcoming" : "Completed"}
                  </button>
                ))}
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                {activeCategory?.id !== 'snack' && activeCategory?.id !== 'video' && activeCategory?.id !== 'journal' && (
                  <button
                    type="button"
                    onClick={() => {
                      setUploadTabSelection(activeCategoryId);
                      setShowUploadPanel(true);
                    }}
                    className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-fuchsia-200 border border-fuchsia-400/40 rounded-full px-2.5 py-1.5 md:px-3 md:py-1 hover:bg-fuchsia-500/10 transition-all"
                  >
                    <Upload className="w-3.5 h-3.5 md:w-3 md:h-3" /> <span className="hidden sm:inline">Upload file</span><span className="sm:hidden">Upload</span>
                  </button>
                )}
                {/* WHIPT WALLET Button for Travel tab */}
                {activeCategory?.id === 'travel' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowDigitalWallet(true)}
                      className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-bold bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-full px-3 py-1.5 md:px-4 md:py-2 hover:from-amber-600 hover:to-yellow-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <span className="text-base">💳</span> 
                      <span className="hidden sm:inline tracking-wider">WHIPT WALLET</span>
                      <span className="sm:hidden tracking-wider">WALLET</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowGmailImport(true)}
                      className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full px-3 py-1.5 md:px-4 md:py-2 hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
                    >
                      <span className="text-base">📧</span> 
                      <span className="hidden sm:inline">Import from Gmail</span>
                      <span className="sm:hidden">Gmail</span>
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleLaunchNotebookDraft}
                  disabled={true}
                  className="inline-flex items-center gap-2 text-sm border rounded-full px-3 py-1 transition-all text-slate-400 border-white/10 cursor-not-allowed opacity-50"
                >
                  <ArrowUpRight className="w-3 h-3" /> Start writing in ThesiSribe (Coming Soon)
                </button>
              </div>
            </div>
          </div>

          {/* Day Counter Widget - Show for Work tabs with counter metadata */}
          {activeCategory?.metadata?.counters && activeCategory.metadata.counters.length > 0 && (
            <div className="flex items-start gap-2 mb-4">
              <div className="flex-1">
                <DayCounterWidget
                  counters={activeCategory.metadata.counters}
                  onUpdate={(index, newUsed) => {
                    setClassNotebooks((prev) =>
                      prev.map((notebook) =>
                        notebook.id === activeClassId
                          ? {
                              ...notebook,
                              categories: notebook.categories.map((cat) =>
                                cat.id === activeCategoryId && cat.metadata?.counters
                                  ? {
                                      ...cat,
                                      metadata: {
                                        ...cat.metadata,
                                        counters: cat.metadata.counters.map((counter: any, i: number) =>
                                          i === index ? { ...counter, used: newUsed } : counter
                                        ),
                                      },
                                    }
                                  : cat
                              ),
                            }
                          : notebook
                      )
                    );
                    // Auto-save after counter update
                    if (user?.id) {
                      setTimeout(() => {
                        const updated = classNotebooks.map((notebook) =>
                          notebook.id === activeClassId
                            ? {
                                ...notebook,
                                categories: notebook.categories.map((cat) =>
                                  cat.id === activeCategoryId && cat.metadata?.counters
                                    ? {
                                        ...cat,
                                        metadata: {
                                          ...cat.metadata,
                                          counters: cat.metadata.counters.map((counter: any, i: number) =>
                                            i === index ? { ...counter, used: newUsed } : counter
                                          ),
                                        },
                                      }
                                    : cat
                                ),
                              }
                            : notebook
                        );
                        saveNotebooks(user.id, updated);
                      }, 500);
                    }
                  }}
                />
              </div>
              <PTOSettingsEditor
                counters={activeCategory.metadata.counters}
                onSave={(updatedCounters) => {
                  setClassNotebooks((prev) =>
                    prev.map((notebook) =>
                      notebook.id === activeClassId
                        ? {
                            ...notebook,
                            categories: notebook.categories.map((cat) =>
                              cat.id === activeCategoryId
                                ? {
                                    ...cat,
                                    metadata: {
                                      ...cat.metadata,
                                      counters: updatedCounters,
                                    },
                                  }
                                : cat
                            ),
                          }
                        : notebook
                    )
                  );
                  // Auto-save
                  if (user?.id) {
                    setTimeout(() => {
                      const updated = classNotebooks.map((notebook) =>
                        notebook.id === activeClassId
                          ? {
                              ...notebook,
                              categories: notebook.categories.map((cat) =>
                                cat.id === activeCategoryId
                                  ? {
                                      ...cat,
                                      metadata: {
                                        ...cat.metadata,
                                        counters: updatedCounters,
                                      },
                                    }
                                  : cat
                              ),
                            }
                          : notebook
                      );
                      saveNotebooks(user.id, updated);
                    }, 500);
                  }
                }}
              />
            </div>
          )}

          {/* Tab-specific item type filters and forms */}
          {/* REMOVED redundant filter buttons - only "+ Add" buttons needed */}
          
          {/* Render form components (modals) - They control their own open/close state */}
          {/* Display form trigger buttons horizontally */}
          <div className="flex flex-wrap gap-3 mb-4">
          {activeCategory?.id === 'home' && (
            <>
              <ShoppingListForm onAdd={handleAddNotebookItem} />
              <BillsTrackerForm onAdd={handleAddNotebookItem} />
              <RecipeForm onAdd={handleAddNotebookItem} />
              <HomeImprovementForm onAdd={handleAddNotebookItem} />
              <MaintenanceReminderForm onAdd={handleAddNotebookItem} />
            </>
          )}
          
          {activeCategory?.id === 'work' && (
            <>
              <MeetingForm onAdd={handleAddNotebookItem} />
              <DeadlineForm onAdd={handleAddNotebookItem} />
            </>
          )}
          
          {activeCategory?.id === 'travel' && (
            <>
              <PackingListForm onAdd={handleAddNotebookItem} />
              <ItineraryForm onAdd={handleAddNotebookItem} />
              <TravelBudgetForm onAdd={handleAddNotebookItem} />
            </>
          )}
          </div>

          {activeCategory?.id === "snack" ? (
            <LateNightSnackPanel
              spots={snackSpots}
              loading={snackLoading}
              onCreate={handleCreateSnackSpot}
              onUpdate={handleUpdateSnackSpot}
              onDelete={handleDeleteSnackSpot}
              onMarkOrdered={handleMarkSnackOrdered}
            />
          ) : (
            <>
              {/* Meeting Notes tab shows saved virtual room notes */}
              {activeCategory?.id === "video" && (
                <div className="mb-6">
                  {meetingTranscripts.length === 0 ? (
                    <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-cyan-400/30 rounded-2xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                          <NotebookPen className="w-6 h-6 text-cyan-300" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-cyan-200 mb-2">Virtual Room Notes</h3>
                          <p className="text-sm text-slate-300 leading-relaxed mb-3">
                            Open a virtual room from any tab and take notes during your session. When you end the session and save your notes, they'll appear here automatically!
                          </p>
                          <p className="text-xs text-cyan-300/80">
                            💡 Tip: Virtual room notes are also saved to Documents Hub for download
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-cyan-300/80 mb-3">
                        <NotebookPen className="w-4 h-4" />
                        <span className="font-medium">Saved Virtual Room Notes</span>
                        <span className="text-slate-400">({meetingTranscripts.length})</span>
                      </div>
                      {meetingTranscripts.map((transcript, idx) => (
                        <div 
                          key={`transcript-${idx}`}
                          className="bg-gradient-to-br from-purple-500/5 to-cyan-500/5 border border-cyan-400/20 rounded-xl p-4 hover:border-cyan-400/40 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium text-cyan-200">{transcript.title}</h4>
                              {transcript.category && (
                                <p className="text-xs text-slate-400 mt-1">
                                  Category: {transcript.category}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-slate-400 whitespace-nowrap ml-4">
                              {new Date(transcript.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="text-sm text-slate-300 bg-slate-900/50 rounded-lg p-3 max-h-32 overflow-y-auto">
                            {transcript.content || <span className="text-slate-500 italic">No content</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-3">
                {/* Journal Tab - Full JournalPage embedded */}
                {activeCategory?.id === "journal" && (
                  <div className="-mx-8 -mt-6">
                    <JournalPage />
                  </div>
                )}
                
                {/* Other Tabs - Show regular items or empty state */}
                {activeCategory?.id !== "journal" && collapsableItems.length === 0 && activeCategory?.id !== "video" && (
                  <div className="rounded-2xl border border-dashed border-white/15 text-center py-8 text-sm text-amber-100/70">
                    No entries yet. Add one to start tracking.
                  </div>
                )}

                {/* Regular List View */}
                {activeCategory?.id !== "journal" && collapsableItems && collapsableItems.length > 0 && (
                  <div className="rounded-2xl border border-white/15 bg-black/20">
                    {/* Header with expand/collapse */}
                    <button
                      onClick={() => setCategoryExpanded(prev => ({
                        ...prev,
                        [activeCategoryId]: !prev[activeCategoryId]
                      }))}
                      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base font-semibold text-white">
                          {collapsableItems.length} {collapsableItems.length === 1 ? 'Item' : 'Items'}
                        </span>
                        <span className="text-xs text-amber-200/70">
                          Click to {categoryExpanded[activeCategoryId] ? 'collapse' : 'expand'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Print Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            printFilteredList(
                              activeCategory?.label || '',
                              activeItemFilter,
                              collapsableItems
                            );
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border border-purple-400/50 transition-all flex items-center gap-1.5"
                          title="Print list with WHIPT header"
                        >
                          <Printer className="w-3.5 h-3.5" />
                          Print
                        </button>
                        {/* Download Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadFilteredList(
                              activeCategory?.label || '',
                              activeItemFilter,
                              collapsableItems
                            );
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-400/50 transition-all flex items-center gap-1.5"
                          title="Download as text file"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </button>
                        {/* Remove Duplicates Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Remove duplicate items from this category? (Keeps one copy of each)')) {
                              setClassNotebooks((prev) => {
                                const updated = prev.map((notebook) => {
                                  if (notebook.id !== activeClassId) return notebook;
                                  
                                  return {
                                    ...notebook,
                                    categories: notebook.categories.map((category) => {
                                      if (category.id !== activeCategoryId) return category;
                                      
                                      // Get all items
                                      const allItems = [
                                        ...(category.buckets?.upcoming || []),
                                        ...(category.buckets?.completed || [])
                                      ];
                                      
                                      // Find duplicates - keep first occurrence of each title+date combo
                                      const seen = new Set();
                                      const uniqueItems = allItems.filter(item => {
                                        const key = `${item.title}|${item.dueDate}`;
                                        if (seen.has(key)) {
                                          return false; // Duplicate - skip it
                                        }
                                        seen.add(key);
                                        return true; // Keep it
                                      });
                                      
                                      // Separate back into upcoming/completed
                                      const uniqueUpcoming = uniqueItems.filter(i => i.status === 'upcoming');
                                      const uniqueCompleted = uniqueItems.filter(i => i.status === 'completed');
                                      
                                      const removed = allItems.length - uniqueItems.length;
                                      if (removed > 0) {
                                        alert(`Removed ${removed} duplicate item${removed === 1 ? '' : 's'}`);
                                      } else {
                                        alert('No duplicates found!');
                                      }
                                      
                                      return {
                                        ...category,
                                        buckets: {
                                          upcoming: uniqueUpcoming,
                                          completed: uniqueCompleted
                                        }
                                      };
                                    })
                                  };
                                });
                                
                                // Save to database
                                if (user?.id) {
                                  saveNotebooks(user.id, updated);
                                }
                                return updated;
                              });
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 border border-blue-400/50 transition-all"
                          title="Remove duplicate items (keeps one copy of each)"
                        >
                          Remove Duplicates
                        </button>
                        {/* Archive Toggle */}
                        {filteredItems.some(item => isItemArchived(item)) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowArchivedItems(!showArchivedItems);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              showArchivedItems
                                ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-400/50'
                                : 'bg-slate-700/50 text-slate-300 border border-slate-600/50 hover:bg-slate-600/50'
                            }`}
                            title={showArchivedItems ? 'Hide archived items (3+ months old)' : 'Show archived items (3+ months old)'}
                          >
                            {showArchivedItems ? '✓ Showing Archived' : 'Show Archived'}
                          </button>
                        )}
                        {/* Animated dropdown arrow with pulsing glow */}
                        <div 
                          className={`relative transform transition-transform ${categoryExpanded[activeCategoryId] ? 'rotate-180' : ''}`}
                          style={{
                            animation: categoryExpanded[activeCategoryId] ? 'none' : 'pulse-glow 2s ease-in-out infinite'
                          }}
                        >
                          <div 
                            className="absolute inset-0 bg-amber-400/40 blur-md rounded-full"
                            style={{
                              animation: categoryExpanded[activeCategoryId] ? 'none' : 'pulse-glow 2s ease-in-out infinite'
                            }}
                          />
                          <span className="relative text-amber-400 font-bold text-lg">▼</span>
                        </div>
                      </div>
                    </button>

                    {/* Collapsible list */}
                    {categoryExpanded[activeCategoryId] && (
                      <div className="max-h-[500px] overflow-y-auto space-y-3 p-4 border-t border-white/10">
                        {(collapsableItems || [])
                          .filter(item => showArchivedItems || !isItemArchived(item))
                          .map((item) => {
              const dueSoon =
                item.status === "upcoming" &&
                item.dueDate &&
                new Date(item.dueDate).getTime() - Date.now() < 48 * 60 * 60 * 1000;
              const archived = isItemArchived(item);
              const cleanItemDescription = sanitizeItemDescription(item.description || item.notes);
              return (
                <div
                  key={item.id}
                  className={`rounded-2xl border p-4 space-y-2 ${
                    archived 
                      ? 'border-slate-600/50 bg-slate-900/40 opacity-70' 
                      : item.status === 'completed'
                      ? 'border-emerald-600/50 bg-emerald-900/20'
                      : 'border-white/15 bg-black/20'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.status === 'completed' 
                          ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/50' 
                          : item.status === 'inProgress'
                          ? 'bg-amber-500/20 text-amber-200 border border-amber-400/50'
                          : 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/50'
                      }`}>
                        {item.status === 'completed' ? '✓ Completed' : item.status === 'inProgress' ? '⏳ In Progress' : '📋 Upcoming'}
                      </span>
                      <span className="text-lg mr-1">{getEventTypeIcon(item.eventType)}</span>
                      <p className={`text-base font-semibold ${item.status === 'completed' ? 'line-through text-emerald-300/70' : ''}`}>{item.title}</p>
                      {archived && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-slate-700/50 text-slate-400 border border-slate-600/50">
                          Archived
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        {activeClass!.categories.filter(cat => cat.id !== activeCategoryId).length > 0 && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMoveMenuId(openMoveMenuId === item.id ? null : item.id);
                              }}
                              className="px-2 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 text-xs font-semibold border border-purple-400/50 transition-colors"
                              title="Move to different category"
                              aria-expanded={openMoveMenuId === item.id}
                            >
                              Move →
                            </button>
                            {openMoveMenuId === item.id && (
                              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-gray-900 border border-purple-400/50 rounded-lg shadow-2xl z-50 min-w-[180px] max-w-[260px] overflow-hidden">
                                {activeClass!.categories
                                  .filter(cat => cat.id !== activeCategoryId)
                                  .map(cat => (
                                    <button
                                      key={cat.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMoveItem(activeClass!.id, activeCategoryId, item.id, cat.id);
                                        setOpenMoveMenuId(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-xs text-purple-200 hover:bg-purple-500/20 transition-colors first:rounded-t-lg last:rounded-b-lg whitespace-nowrap"
                                    >
                                      → {cat.label}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}

                        <button
                          onClick={() => handleDeleteItem(activeClass!.id, activeCategory!.id, item.id)}
                          className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 transition-colors"
                          title="Mark as complete"
                        >
                          <CheckSquare className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            openItemEditor(item, activeClass!.id, activeCategory!.id);
                          }}
                          className="p-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 transition-colors"
                          title="Edit item"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePermanentDeleteItem(activeClass!.id, activeCategory!.id, item.id, item.title)}
                          className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
                          title="Delete permanently"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-xs min-w-0 max-w-full">
                        <CalendarDays className="w-3.5 h-3.5 text-amber-200" />
                        <span className="whitespace-nowrap">{formatDueDisplay(item.dueDate)}</span>
                        {dueSoon && !archived && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.3em] bg-red-500/30 text-red-100 border border-red-400/60"
                            style={{ animation: "pulse-alert 1.2s ease-in-out infinite" }}
                          >
                            <BellRing className="w-3 h-3" /> Due soon
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
                      </div>
                    )}
                  </div>
                )}
              </div>

          {/* Uploaded Documents Section for this tab */}
          {activeCategory && activeCategory.id !== 'snack' && activeCategory.id !== 'video' && activeCategory.id !== 'journal' && (
            <div className="rounded-3xl border border-white/10 bg-black/20 p-4 space-y-3 mt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">📄 Uploaded Documents</p>
                <span className="text-xs text-amber-100/70">
                  {uploadedDocuments.length} total files
                </span>
              </div>
              {uploadedDocuments.length === 0 ? (
                <p className="text-sm text-amber-100/70 text-center py-4">
                  No uploaded documents yet. Click "Upload file" above to add files.
                </p>
              ) : (
                <div className="space-y-2">
                  {uploadedDocuments
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between rounded-xl border border-white/15 bg-white/5 px-4 py-3 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">{doc.title}</p>
                            {doc.isProtected && (
                              <span className="text-amber-400" title="Password Protected">
                                <Lock className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Tab: {doc.notebookTabId || 'unknown'} • {new Date(doc.created_at).toLocaleDateString()}
                            {doc.isProtected && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">🔒 Protected</span>}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (doc.isProtected) {
                                setUnlockingDocId(doc.id);
                                setUnlockingDocTitle(doc.title);
                                setShowUnlockModal(true);
                              } else {
                                // Open preview in new window
                                const previewWindow = window.open('', '_blank');
                                if (previewWindow) {
                                  previewWindow.document.write(`
                                    <html>
                                      <head>
                                        <title>${doc.title}</title>
                                        <style>
                                          body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #1a1a1a; }
                                          iframe, embed { width: 90vw; height: 90vh; border: none; }
                                          img { max-width: 90vw; max-height: 90vh; }
                                        </style>
                                      </head>
                                      <body>
                                        ${doc.content.startsWith('data:application/pdf') 
                                          ? `<embed src="${doc.content}" type="application/pdf" />` 
                                          : doc.content.startsWith('data:image') 
                                          ? `<img src="${doc.content}" alt="${doc.title}" />`
                                          : `<iframe src="${doc.content}"></iframe>`
                                        }
                                      </body>
                                    </html>
                                  `);
                                  previewWindow.document.close();
                                }
                              }
                            }}
                            className="text-xs px-3 py-1 rounded-full bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 transition-colors"
                            title="Preview file"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => {
                              if (doc.isProtected) {
                                setUnlockingDocId(doc.id);
                                setUnlockingDocTitle(doc.title);
                                setShowUnlockModal(true);
                              } else {
                                const link = document.createElement('a');
                                link.href = doc.content;
                                link.download = doc.title;
                                link.click();
                              }
                            }}
                            className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 transition-colors"
                            title="Download file"
                          >
                            ⬇️
                          </button>
                          <button
                            onClick={(e) => handleDeleteDocument(doc.id, e)}
                            className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                        ))}
                </div>
              )}
            </div>
          )}

            </>
          )}
        </section>

            {/* WHIPT Transcripts Section - Enabled */}
            <div className="mt-4">
              <div 
                className="relative overflow-hidden rounded-3xl border-2 border-purple-400/60 bg-gradient-to-br from-purple-900/70 to-pink-900/60 p-6 shadow-[0_20px_60px_rgba(147,51,234,0.35)]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg ring-2 ring-white/30">
                      <span className="text-3xl">🎤</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">WHIPT Transcripts</h3>
                      <p className="text-sm text-purple-100">Upload audio/video, extract answers, and save transcripts to your workspace.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTranscriptsPanel(true)}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-lg hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white/40"
                  >
                    Open Transcripts
                  </button>
                </div>
              </div>
            </div>

            {/* Virtual Room Hero Button - directly above Scheduled Meetings */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowVirtualRoomsPanel(true)}
                className="w-full text-left relative overflow-hidden rounded-3xl border-2 border-cyan-400/60 bg-gradient-to-br from-cyan-900/70 to-blue-900/60 p-6 shadow-[0_20px_60px_rgba(34,211,238,0.30)] hover:brightness-110 transition-all"
                style={{ animation: 'pulse-glow 3s ease-in-out infinite' }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg ring-2 ring-white/30">
                      <span className="text-3xl">🌐</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        Virtual Room <span className="text-cyan-200 text-base font-semibold">(7 categories)</span>
                      </h3>
                      <p className="text-sm text-cyan-100">Open room categories here, then schedule and review meetings directly below at a glance.</p>
                    </div>
                  </div>
                  <div className="px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold shadow-lg">
                    Open Rooms Panel
                  </div>
                </div>
              </button>
            </div>

            {/* Session Recordings Hero Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowRecordingsPanel(true)}
                className="w-full text-left relative overflow-hidden rounded-3xl border-2 border-rose-400/60 bg-gradient-to-br from-rose-900/70 to-pink-900/60 p-6 shadow-[0_20px_60px_rgba(244,63,94,0.30)] hover:brightness-110 transition-all"
                style={{ animation: 'pulse-glow 3s ease-in-out infinite' }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg ring-2 ring-white/30">
                      <span className="text-3xl">📹</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        Session Recordings <span className="text-rose-200 text-base font-semibold">(video, audio, transcripts)</span>
                      </h3>
                      <p className="text-sm text-rose-100">View, play, download, and share recorded Chime video sessions — just like Zoom.</p>
                    </div>
                  </div>
                  <div className="px-6 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 text-white font-bold shadow-lg">
                    Open Recordings
                  </div>
                </div>
              </button>
            </div>

            {showVirtualRoomsPanel && (
              <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowVirtualRoomsPanel(false)} />
                <div className="relative w-full max-w-5xl max-h-[88vh] overflow-y-auto rounded-3xl border-2 border-purple-400/60 bg-gradient-to-br from-purple-900/70 to-pink-900/60 p-6 shadow-[0_20px_60px_rgba(147,51,234,0.35)]">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg ring-2 ring-white/30">
                        <span className="text-2xl">🌐</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Virtual Rooms</h3>
                        <p className="text-sm text-purple-100">Launch room categories and schedule actions</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowVirtualRoomsPanel(false)}
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white font-semibold shadow-lg hover:brightness-110"
                    >
                      Close ✕
                    </button>
                  </div>

                  <div className="space-y-5">
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                      {activeClass?.categories.filter(cat => !['snack', 'transcripts', 'virtual-room-notes'].includes(cat.id)).map((category) => {
                        const blueprint = CATEGORY_BLUEPRINTS.find(b => b.id === category.id);
                        const profile = VIRTUAL_ROOM_PROFILES[category.id];
                        const Icon = blueprint?.icon || category.icon || NotebookPen;
                        const roomProfile = profile || {
                          id: category.id,
                          tabLabel: category?.label || 'Session',
                          roomTitle: `${category?.label || 'Session'} Session`,
                          description: `Collaborate on ${category?.label?.toLowerCase() || 'this'} work with your peers.`,
                          agenda: [],
                          accessibilityNote: "Live captions and screen sharing available",
                          participants: [{ id: "1", name: "You", role: "Participant", initials: "YOU", online: true }],
                          resources: [],
                        };

                        return (
                          <div key={category.id} className="rounded-2xl border border-white/20 bg-black/25 p-4 flex flex-col gap-2 backdrop-blur-md">
                            <div className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 ${category.accent}`} />
                              <p className="text-sm font-semibold text-white">{category.id === 'video' ? 'Social/Family Room' : `${category.label} Room`}</p>
                            </div>
                            <p className="text-xs text-white/80 leading-relaxed">{roomProfile.description}</p>

                            <button
                              type="button"
                              onClick={() => handleOpenVirtualRoom(category.id)}
                              className="inline-flex items-center justify-center rounded-full border border-slate-500/40 text-slate-300 hover:bg-slate-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] transition-all"
                            >
                              Launch Room
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const link = `${window.location.origin}/organizer?room=${category.id}&class=${activeClass?.id}&autojoin=1`;
                                try {
                                  await navigator.clipboard.writeText(link);
                                  setCopiedLink(link);
                                  setCopiedRoomName(category.label || 'Virtual Room');
                                  setShowLinkCopiedModal(true);
                                } catch (err) {
                                  alert(`Copy failed. Please copy manually:\n\n${link}`);
                                }
                              }}
                              className="inline-flex items-center gap-1 justify-center rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] transition-all"
                            >
                              <span>🔗</span> Copy Invite Link
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setAgendaForRoom(category.id);
                                setShowAgendaModal(true);
                              }}
                              className={`inline-flex items-center gap-1 justify-center rounded-full border ${roomAgendas[category.id] ? 'border-blue-500/60 text-blue-300 bg-blue-500/10' : 'border-blue-500/40 text-blue-300'} hover:bg-blue-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] transition-all`}
                            >
                              <span>📋</span> {roomAgendas[category.id] ? 'Edit Agenda' : 'Add Agenda'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setScheduleForRoom(category.id);
                                setShowScheduleModal(true);
                              }}
                              className="inline-flex items-center gap-1 justify-center rounded-full border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] transition-all"
                            >
                              <Calendar className="h-3 w-3" />
                              Schedule
                            </button>
                          </div>
                        );
                      })}
                    </div>

                  </div>
                </div>
              </div>
            )}

            {/* Scheduled Meetings Section - Between Virtual Rooms and Calendar */}
            <style>{`
              @keyframes gentle-pulse-cyan {
                0%, 100% { 
                  box-shadow: 0 0 0 rgba(34, 211, 238, 0);
                  transform: scale(1);
                }
                50% { 
                  box-shadow: 0 0 20px rgba(34, 211, 238, 0.5), 0 0 40px rgba(34, 211, 238, 0.3);
                  transform: scale(1.005);
                }
              }
              @keyframes gentle-pulse-amber {
                0%, 100% { 
                  box-shadow: 0 0 0 rgba(251, 191, 36, 0);
                  transform: scale(1);
                }
                50% { 
                  box-shadow: 0 0 20px rgba(251, 191, 36, 0.5), 0 0 40px rgba(251, 191, 36, 0.3);
                  transform: scale(1.005);
                }
              }
              @keyframes gentle-pulse-emerald {
                0%, 100% { 
                  box-shadow: 0 0 0 rgba(16, 185, 129, 0);
                  transform: scale(1);
                }
                50% { 
                  box-shadow: 0 0 20px rgba(16, 185, 129, 0.5), 0 0 40px rgba(16, 185, 129, 0.3);
                  transform: scale(1.005);
                }
              }
              @keyframes gentle-pulse-purple {
                0%, 100% { 
                  box-shadow: 0 0 0 rgba(168, 85, 247, 0);
                  transform: scale(1);
                }
                50% { 
                  box-shadow: 0 0 20px rgba(168, 85, 247, 0.5), 0 0 40px rgba(168, 85, 247, 0.3);
                  transform: scale(1.005);
                }
              }
              @keyframes gentle-pulse-slate {
                0%, 100% { 
                  box-shadow: 0 0 0 rgba(148, 163, 184, 0);
                  transform: scale(1);
                }
                50% { 
                  box-shadow: 0 0 20px rgba(148, 163, 184, 0.5), 0 0 40px rgba(148, 163, 184, 0.3);
                  transform: scale(1.005);
                }
              }
              .meeting-discussion:hover { animation: gentle-pulse-cyan 3s ease-in-out infinite; }
              .meeting-assignment:hover { animation: gentle-pulse-amber 3s ease-in-out infinite; }
              .meeting-experiential:hover { animation: gentle-pulse-emerald 3s ease-in-out infinite; }
              .meeting-video:hover { animation: gentle-pulse-purple 3s ease-in-out infinite; }
              .meeting-default:hover { animation: gentle-pulse-slate 3s ease-in-out infinite; }
              .scheduled-meetings-panel {
                box-shadow: 0 0 60px rgba(34, 211, 238, 0.2), 0 0 100px rgba(34, 211, 238, 0.1);
                backdrop-filter: blur(20px);
                -webkit-backdrop-filter: blur(20px);
              }
            `}</style>
            <div key={scheduledSessions.length} className="scheduled-meetings-panel rounded-2xl border-2 border-rose-400/40 bg-gradient-to-br from-rose-950/30 to-red-950/30 p-5 mt-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold uppercase tracking-[0.3em] text-rose-100 drop-shadow-lg">
                  📅 Scheduled Virtual Meetings
                </h3>
                <span className="text-sm font-semibold text-rose-200 px-3 py-1 rounded-full bg-rose-500/30 border border-rose-400/40 backdrop-blur-sm">
                  {scheduledSessions.length} scheduled
                </span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-rose-500/30 scrollbar-track-transparent">
                {scheduledSessions.length === 0 ? (
                  <p className="text-sm text-rose-200/70 italic text-center py-6 bg-rose-900/20 rounded-lg border border-rose-400/20 backdrop-blur-sm">
                    No meetings scheduled yet. Click "Schedule" on any virtual room card above to create one.
                  </p>
                ) : (
                  scheduledSessions.map((session: any, idx: number) => {
                    const colors = getMeetingColors(session.roomType);
                    const animationClass = session.roomType ? `meeting-${session.roomType}` : 'meeting-default';
                    const sessionKey = getSessionKey(session, idx);
                    return (
                    <div key={sessionKey} className={`${animationClass} rounded-xl bg-gradient-to-r ${colors.gradient} px-4 py-3 border-2 ${colors.border} transition-all backdrop-blur-md`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-bold text-base text-white truncate drop-shadow-md">{session.title}</p>
                            {session.recurring && (
                              <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-white/20 text-white border border-white/30 whitespace-nowrap font-semibold backdrop-blur-sm">
                                🔄 {session.recurring}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-white/90 drop-shadow">
                            <span className="flex items-center gap-1">📅 <strong>{session.date}</strong></span>
                            <span className="flex items-center gap-1">🕐 <strong>{formatTimeTo12Hour(session.time)}{session.timezone ? ` (${session.timezone})` : ''}</strong></span>
                            <span className="flex items-center gap-1">⏱️ <strong>{session.duration}</strong></span>
                          </div>
                          {session.participants && session.participants.length > 0 && (
                            <p className="text-xs text-white/70 mt-2 truncate drop-shadow">
                              👥 {session.participants.slice(0, 3).join(', ')}{session.participants.length > 3 ? ` +${session.participants.length - 3} more` : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              const newTitle = window.prompt('Edit meeting title:', session.title);
                              if (newTitle && newTitle.trim()) {
                                const updated = scheduledSessions.map((s, i) =>
                                  getSessionKey(s, i) === sessionKey ? { ...s, title: newTitle.trim() } : s
                                );
                                setScheduledSessions(updated);
                              }
                            }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/20 text-base transition-colors p-2 rounded-lg backdrop-blur-sm"
                            title="Edit meeting"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete "${session.title}"?`)) {
                                const updated = scheduledSessions.filter((s, i) => getSessionKey(s, i) !== sessionKey);
                                setScheduledSessions(updated);
                              }
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/20 text-base transition-colors p-2 rounded-lg backdrop-blur-sm"
                            title="Delete meeting"
                          >
                            🗑️
                          </button>
                          <button
                            onClick={() => exportMeetingToICS(session)}
                            className="text-amber-300 hover:text-amber-200 hover:bg-amber-400/20 text-base transition-colors p-2 rounded-lg backdrop-blur-sm"
                            title="Add to calendar (.ics)"
                          >
                            📆
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                  })
                )}
              </div>
            </div>

            {/* Virtual Room Notes Panel - All Classes (Virtual Rooms only) */}
            <div className="mt-4 rounded-2xl border-2 border-purple-400/40 bg-gradient-to-br from-purple-950/35 to-indigo-950/30 p-5 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold uppercase tracking-[0.28em] text-purple-100 drop-shadow">📝 Virtual Room Notes</h3>
                  <p className="text-xs text-purple-200/75 mt-1">Transcripts from virtual rooms across all classes</p>
                </div>
                <button
                  onClick={() => setShowTranscriptsPanel(true)}
                  className="px-3 py-1.5 rounded-full bg-purple-500/20 border border-purple-400/50 text-purple-100 text-xs font-semibold hover:bg-purple-500/30 transition-all"
                >
                  Open All Notes
                </button>
              </div>

              {(() => {
                const virtualRoomOnlyTranscripts = meetingTranscripts.filter((transcript: any) => {
                  const roomType = String(transcript.roomType || transcript.room_category || '').toLowerCase();
                  const category = String(transcript.category || '').toLowerCase();
                  const title = String(transcript.title || '').toLowerCase();
                  const content = String(transcript.content || '').toLowerCase();

                  const hasVirtualRoomMarker = Boolean(
                    roomType ||
                    category.includes('discussion') ||
                    category.includes('assignment') ||
                    category.includes('experiential') ||
                    category.includes('social') ||
                    category.includes('virtual')
                  );

                  const looksLikeTranscript = content.length > 0 || title.includes('session') || title.includes('virtual room');

                  return hasVirtualRoomMarker && looksLikeTranscript;
                });

                const sorted = [...virtualRoomOnlyTranscripts].sort((a: any, b: any) => {
                  const timeA = new Date(a.createdAt || a.created_at || a.date || 0).getTime();
                  const timeB = new Date(b.createdAt || b.created_at || b.date || 0).getTime();
                  return timeB - timeA;
                });

                if (sorted.length === 0) {
                  return (
                    <div className="text-sm text-purple-200/75 italic text-center py-6 bg-purple-900/20 rounded-lg border border-purple-400/20">
                      No virtual room transcripts yet. Start a room and save notes to populate this panel.
                    </div>
                  );
                }

                return (
                  <div className="space-y-2 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-purple-500/30 scrollbar-track-transparent">
                    {sorted.slice(0, 8).map((transcript: any, idx: number) => {
                      const lines = (transcript.content || '').split('\n').filter((line: string) => line.trim());
                      const preview = lines.slice(0, 2).join(' ');
                      const roomLabel = transcript.category || transcript.roomType || 'Virtual Room';
                      const classLabel = transcript.className || classNotebooks.find(nb => nb.id === transcript.classId)?.title || 'Unknown Class';
                      const createdAt = transcript.createdAt || transcript.created_at || transcript.date;

                      return (
                        <div key={`global-vr-transcript-${transcript.id || idx}`} className="rounded-xl border border-purple-400/25 bg-black/25 px-4 py-3 backdrop-blur-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                <span className="text-sm font-semibold text-purple-100 truncate">{transcript.title || 'Virtual Room Session'}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-400/30">{roomLabel}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/30">{classLabel}</span>
                              </div>
                              <p className="text-xs text-purple-200/80 line-clamp-2">{preview || 'No transcript preview available.'}</p>
                            </div>
                            <span className="text-[11px] text-slate-300 whitespace-nowrap">
                              {createdAt ? new Date(createdAt).toLocaleDateString() : 'Unknown date'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

        {/* Calendar View - Replaces Timeline */}
        <section data-tutorial="timeline" className="mt-4">
          <CalendarView 
            key={`calendar-${activeClassId}-${classNotebooks.length}-${calendarEvents.length}`}
            items={(() => {
              const notebookItems = classNotebooks.flatMap(notebook =>
                notebook.categories.flatMap(cat => 
                  (cat.buckets?.upcoming || [])
                    .filter(item => item.dueDate) // Only items with due dates
                    .map(item => ({
                      id: String(item.id),
                      title: item.title,
                      dueDate: item.dueDate,
                      description: item.description || item.notes || '',
                      notes: item.notes || '',
                      course: notebook.title, // Add course name
                      status: item.status,
                      tone: cat.id, // Category for color coding
                      eventType: item.eventType // Pass event type for icons
                    }))
                )
              );
              
              // Create a Set of notebook item identifiers (title + date) for deduplication
              const notebookItemKeys = new Set(
                notebookItems.map(item => `${item.title}|${item.dueDate?.split('T')[0]}`)
              );
              
              // Filter out calendar events that match notebook items
              const calendarOnlyEvents = calendarEvents
                .filter(event => {
                  const key = `${event.title}|${event.due_date?.split('T')[0]}`;
                  return !notebookItemKeys.has(key); // Only include if NOT in notebooks
                })
                .map(event => ({
                  id: event.id,
                  title: event.title,
                  dueDate: event.due_date,
                  description: event.description || '',
                  notes: event.notes || '',
                  course: event.course || 'Calendar',
                  status: event.status || 'upcoming',
                  tone: event.category || 'event',
                  eventType: 'calendar-event'
                }));
              
              return [
                ...calendarOnlyEvents,
                ...notebookItems,
                ...scheduledSessions.map(session => ({
                  id: `session-${session.sessionId}`,
                  sessionId: session.sessionId,
                  title: `🎥 ${session.title || 'Virtual Meeting'}`,
                  dueDate: session.date && session.time ? `${session.date}T${session.time}` : session.date,
                  time: session.time,
                  timezone: session.timezone,
                  description: session.agenda || 'Virtual meeting session',
                  notes: `${session.attendees || 0} attendees`,
                  course: classNotebooks.find(nb => nb.id === session.classId)?.title || 'Meeting',
                  status: 'upcoming',
                  tone: 'video' // Mark as video/meeting for color coding
                }))
              ];
            })()}
            onItemClick={(item: any) => {
              // Check if it's an empty day click (no items)
              if (item?.isEmpty && item?.dateObj) {
                const dateStr = `${item.dateObj.getFullYear()}-${String(item.dateObj.getMonth() + 1).padStart(2, '0')}-${String(item.dateObj.getDate()).padStart(2, '0')}`;
                setSelectedCalendarDate(dateStr);
                setShowQuickAddMenu(true);
                // Position menu in center of screen
                setQuickAddMenuPosition({ 
                  x: window.innerWidth / 2, 
                  y: window.innerHeight / 2 
                });
                return;
              }
              
              // If item has date and items array, it's a day click with existing items
              if (item?.date && item?.items) {
                setSelectedDateEvents(item);
              } else {
                // Single item click
              }
            }}
            onItemUpdate={async (id, updates) => {
              // Handle delete
              if ((updates as any)._delete) {
                // Find which category this item belongs to and delete it
                setClassNotebooks(prev => {
                  const updated = prev.map(notebook => {
                    return {
                      ...notebook,
                      categories: notebook.categories.map(cat => ({
                        ...cat,
                        buckets: {
                          upcoming: cat.buckets.upcoming.filter(item => String(item.id) !== String(id)),
                          completed: cat.buckets.completed.filter(item => String(item.id) !== String(id)),
                        }
                      }))
                    };
                  });
                  
                  if (user?.id) {
                    saveNotebooks(user.id, updated);
                  }
                  
                  return updated;
                });
                return;
              }
              
              // Find and update the item in the timeline
              const updatedOverrides = { ...timelineOverrides };
              
              if (!updatedOverrides[activeClassId]) {
                updatedOverrides[activeClassId] = {};
              }

              // Find the item in categories and update it (check both buckets)
              activeClass?.categories.forEach(cat => {
                ['upcoming', 'completed'].forEach((bucket) => {
                  const itemIndex = cat.buckets[bucket].findIndex(item => item.id === id);
                  if (itemIndex !== -1) {
                    if (!updatedOverrides[activeClassId][cat.id]) {
                      updatedOverrides[activeClassId][cat.id] = [...cat.buckets[bucket]];
                    }
                    updatedOverrides[activeClassId][cat.id][itemIndex] = {
                      ...updatedOverrides[activeClassId][cat.id][itemIndex],
                      ...updates
                    };
                  }
                });
              });

              setTimelineOverrides(updatedOverrides);
              // Save to database
              if (user) {
                await saveTimelineOverrides(user.id, updatedOverrides);
              }
            }}
          />
        </section>

        <section className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2">
          {UPCOMING_BLOCKS.map((block, index) => (
            <button
              key={block.id}
              data-tutorial={block.id === 'documents' ? 'documents' : undefined}
              ref={block.id === 'documents' ? documentsHubRef : undefined}
              onClick={() => {
                if (block.id === 'documents') setShowDocumentsPanel(true);
                if (block.id === 'uploads') setShowUploadPanel(true);
                if (block.id === 'transcripts') setShowTranscriptsPanel(true);
                if (block.id === 'health') setShowHealthPanel(true);
              }}
              style={{
                animation: `pulse-glow 3s ease-in-out infinite ${index * 0.5}s`,
              }}
              className={`glass-panel rounded-2xl p-5 border-2 transition-all text-left relative overflow-hidden group ${
                `cursor-pointer ${
                  block.id === 'documents' && showDownloadNotification
                    ? 'border-emerald-400/60 ring-4 ring-emerald-400/30'
                    : block.id === 'health'
                    ? 'border-amber-400/40 hover:border-amber-400/60'
                    : 'border-white/20 hover:border-white/40'
                }`
              }`}
            >
              {/* Coming Soon Banner for Health */}
              {block.id === 'health' && (
                <div className="absolute top-2 right-2 z-20">
                  <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/40 rounded-full shadow-lg">
                    Coming Soon
                  </span>
                </div>
              )}
              
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 via-purple-600/20 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" 
                   style={{
                     animation: 'shimmer 2s ease-in-out infinite',
                   }}
              />
              
              {/* Attention badge */}
              {block.id !== 'health' && (
                <>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-ping" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full" />
                </>
              )}
              
              <div className="flex items-start gap-3 relative z-10">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center ring-2 ring-white/20 group-hover:ring-white/40 transition-all">
                  <block.icon className={`w-5 h-5 ${block.accent} group-hover:scale-110 transition-transform`} />
                </div>
                <div className="flex-1">
                  <p className="text-xs md:text-sm font-bold text-white group-hover:text-purple-200 transition-colors">{block.label}</p>
                  <p className="text-[11px] md:text-xs text-slate-300 leading-relaxed">{block.description}</p>
                </div>
              </div>
            </button>
          ))}
        </section>

        {/* Add keyframe animations to the page */}
        <style>{`
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 0 10px rgba(168, 85, 247, 0.3);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 20px rgba(168, 85, 247, 0.6), 0 0 30px rgba(168, 85, 247, 0.4);
              transform: scale(1.02);
            }
          }
          
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}</style>

              </div>
            </div>
          </div>
        </div>
                  </div>
                </div>

      {virtualRoomOpen && virtualRoomContext && (
        <StudentVirtualRoom
          tabLabel={virtualRoomContext.tabLabel}
          roomTitle={virtualRoomContext.roomTitle}
          description={virtualRoomContext.description}
          agenda={virtualRoomContext.agenda}
          accessibilityNote={virtualRoomContext.accessibilityNote}
          participants={virtualRoomContext.participants}
          resources={virtualRoomContext.resources}
          roomId={`${activeClassId || 'class'}-${virtualRoomContext.id}`}
          onClose={handleCloseVirtualRoom}
          onMinimize={virtualRoomMinimized ? handleRestoreVirtualRoom : handleMinimizeVirtualRoom}
          minimized={virtualRoomMinimized}
          roomNotes={roomNotes}
          onNotesChange={setRoomNotes}
          onAgendaChange={handleAgendaChange}
          snackSpots={snackSpots}
          snackLoading={snackLoading}
          onCreateSnack={handleCreateSnackSpot}
          onUpdateSnack={handleUpdateSnackSpot}
          onDeleteSnack={handleDeleteSnackSpot}
          onMarkSnackOrdered={handleMarkSnackOrdered}
        />
      )}
      
      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-purple-400/30 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Schedule Meeting</h3>
              <button onClick={() => setShowScheduleModal(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Meeting Title</label>
                <input
                  id="organizer-schedule-title"
                  type="text"
                  defaultValue={scheduleForRoom ? (scheduleForRoom === 'video' ? 'Social/Family Room Session' : CATEGORY_BLUEPRINTS.find(b => b.id === scheduleForRoom)?.label + " Session") : "Study Session"}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Date</label>
                  <input
                    id="organizer-schedule-date"
                    type="date"
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Time</label>
                  <input
                    id="organizer-schedule-time"
                    type="time"
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Duration</label>
                <select id="organizer-schedule-duration" className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400">
                  <option>30 minutes</option>
                  <option>1 hour</option>
                  <option>1.5 hours</option>
                  <option>2 hours</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Timezone</label>
                <select
                  id="organizer-schedule-timezone"
                  defaultValue={defaultScheduleTimezone}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                >
                  <option value={defaultScheduleTimezone}>{defaultScheduleTimezone} (Local)</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Chicago">America/Chicago</option>
                  <option value="America/Denver">America/Denver</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Recurring</label>
                <select 
                  id="organizer-schedule-recurring" 
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                  onChange={(e) => {
                    const customOptions = document.getElementById('organizer-schedule-custom-options');
                    if (customOptions) {
                      customOptions.style.display = e.target.value === 'custom' ? 'block' : 'none';
                    }
                  }}
                >
                  <option value="none">One-time meeting</option>
                  <option value="daily">Every Day</option>
                  <option value="weekly">Every Week</option>
                  <option value="biweekly">Every 2 Weeks</option>
                  <option value="monthly">Every Month</option>
                  <option value="custom">Custom...</option>
                </select>
              </div>
              <div id="organizer-schedule-custom-options" style={{ display: 'none' }} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-300 mb-2 block">Repeat every</label>
                    <input
                      id="organizer-schedule-custom-interval"
                      type="number"
                      min="1"
                      defaultValue="1"
                      className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 mb-2 block">Period</label>
                    <select id="organizer-schedule-custom-period" className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400">
                      <option value="days">Day(s)</option>
                      <option value="weeks">Week(s)</option>
                      <option value="months">Month(s)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Number of occurrences</label>
                  <input
                    id="organizer-schedule-custom-occurrences"
                    type="number"
                    min="1"
                    max="52"
                    defaultValue="4"
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Invite participants (comma separated emails)</label>
                <textarea
                  id="organizer-schedule-participants"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-400 h-20 resize-none"
                  placeholder="student1@university.edu, student2@university.edu"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const title = (document.getElementById('organizer-schedule-title') as HTMLInputElement)?.value;
                    const date = (document.getElementById('organizer-schedule-date') as HTMLInputElement)?.value;
                    const time = (document.getElementById('organizer-schedule-time') as HTMLInputElement)?.value;
                    const duration = (document.getElementById('organizer-schedule-duration') as HTMLSelectElement)?.value;
                    const timezone = (document.getElementById('organizer-schedule-timezone') as HTMLSelectElement)?.value || defaultScheduleTimezone;
                    const recurring = (document.getElementById('organizer-schedule-recurring') as HTMLSelectElement)?.value;
                    const participants = (document.getElementById('organizer-schedule-participants') as HTMLTextAreaElement)?.value;
                    
                    if (date && time && title) {
                      // Generate recurring sessions
                      const generateRecurringSessions = () => {
                        const sessions = [];
                        const baseDate = new Date(date + 'T' + time);
                        const formatLocalDate = (localDate: Date) => {
                          const year = localDate.getFullYear();
                          const month = String(localDate.getMonth() + 1).padStart(2, '0');
                          const day = String(localDate.getDate()).padStart(2, '0');
                          return `${year}-${month}-${day}`;
                        };
                        let occurrences = 1;
                        let intervalDays = 0;
                        
                        if (recurring === 'none') {
                          occurrences = 1;
                        } else if (recurring === 'custom') {
                          const customInterval = parseInt((document.getElementById('organizer-schedule-custom-interval') as HTMLInputElement)?.value || '1');
                          const customPeriod = (document.getElementById('organizer-schedule-custom-period') as HTMLSelectElement)?.value;
                          occurrences = parseInt((document.getElementById('organizer-schedule-custom-occurrences') as HTMLInputElement)?.value || '4');
                          
                          if (customPeriod === 'days') intervalDays = customInterval;
                          else if (customPeriod === 'weeks') intervalDays = customInterval * 7;
                          else if (customPeriod === 'months') intervalDays = customInterval * 30;
                        } else {
                          // Preset recurring patterns
                          occurrences = 10; // Default 10 occurrences for preset patterns
                          if (recurring === 'daily') intervalDays = 1;
                          else if (recurring === 'weekly') intervalDays = 7;
                          else if (recurring === 'biweekly') intervalDays = 14;
                          else if (recurring === 'monthly') intervalDays = 30;
                        }
                        
                        const seriesId = `series-${Date.now()}-${Math.random().toString(16).slice(2)}`;
                        
                        for (let i = 0; i < occurrences; i++) {
                          const sessionDate = new Date(baseDate);
                          sessionDate.setDate(sessionDate.getDate() + (i * intervalDays));
                          
                          sessions.push({
                            sessionId: `session-${Date.now()}-${i}-${Math.random().toString(16).slice(2)}`,
                            title: i === 0 ? title : `${title} (${i + 1}/${occurrences})`,
                            date: formatLocalDate(sessionDate),
                            time,
                            timezone,
                            duration,
                            recurring: recurring !== 'none' ? recurring : undefined,
                            seriesId: recurring !== 'none' ? seriesId : undefined,
                            seriesIndex: i + 1,
                            seriesTotal: occurrences,
                            participants: participants.split(',').map(p => p.trim()).filter(p => p),
                            roomId: scheduleForRoom || 'general',
                            roomType: scheduleForRoom,
                            classId: activeClassId,
                            createdAt: new Date().toISOString(),
                          });
                        }
                        
                        return sessions;
                      };
                      
                      const newSessions = generateRecurringSessions();
                      const updated = [...scheduledSessions, ...newSessions];
                      setScheduledSessions(updated);
                      
                      setShowScheduleModal(false);
                      setScheduleForRoom(null);
                    } else {
                      alert('Please fill in title, date, and time');
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 text-sm font-semibold transition-colors"
                >
                  Schedule Meeting
                </button>
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleForRoom(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health & Fitness Modal */}
      {showHealthPanel && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowHealthPanel(false)}>
          <div className="bg-[#050309] border border-emerald-400/30 rounded-2xl p-6 max-w-xl w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Health & Fitness</h3>
              <button onClick={() => setShowHealthPanel(false)} className="text-slate-400 hover:text-white text-xl">
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-300">
                Track steps, sleep, and vitals, and (optionally) sync from wearables. External connections are feature-flagged.
              </p>

              {!healthTrackerEnabled && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 text-amber-100 px-4 py-3 text-sm">
                  Coming soon: Connect Fitbit / Oura. Enable with `VITE_FEATURE_HEALTH_TRACKER=true` after credentials are ready.
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  disabled={!healthTrackerEnabled}
                  className={`w-full rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${
                    healthTrackerEnabled
                      ? 'border-emerald-300/50 text-emerald-100 hover:bg-emerald-500/15'
                      : 'border-slate-600 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  Connect Fitbit (OAuth)
                </button>
                <button
                  disabled={!healthTrackerEnabled}
                  className={`w-full rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${
                    healthTrackerEnabled
                      ? 'border-amber-300/50 text-amber-100 hover:bg-amber-500/15'
                      : 'border-slate-600 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  Connect Oura (OAuth)
                </button>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                <p className="font-semibold mb-1">What you'll see here</p>
                <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">
                  <li>Daily steps, sleep duration, and resting HR</li>
                  <li>7-day trend mini-charts</li>
                  <li>Manual entry (if device not connected)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Agenda Creation Modal */}
      {showAgendaModal && agendaForRoom && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-blue-400/30 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📋 Meeting Agenda</h3>
              <button onClick={() => {
                setShowAgendaModal(false);
                setAgendaForRoom(null);
              }} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">
                  Room: <span className="text-blue-400 font-semibold">
                    {agendaForRoom === 'video' ? 'Social/Family Room' : (CATEGORY_BLUEPRINTS.find(b => b.id === agendaForRoom)?.label || 'Virtual Room')}
                  </span>
                </label>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Agenda Topics</label>
                <textarea
                  id="agenda-textarea"
                  defaultValue={roomAgendas[agendaForRoom] || ''}
                  placeholder={`Example:\n\n1. Welcome & Introductions (5 min)\n2. Review last session's action items (10 min)\n3. Main discussion topic (30 min)\n4. Q&A (10 min)\n5. Next steps & closing (5 min)`}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-blue-400 h-64 resize-none font-mono"
                />
                <p className="text-xs text-slate-500 mt-2">
                  💡 Tip: This agenda will be included when you share the invite link
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const agendaText = (document.getElementById('agenda-textarea') as HTMLTextAreaElement)?.value?.trim();
                    
                    if (agendaText) {
                      setRoomAgendas(prev => ({
                        ...prev,
                        [agendaForRoom]: agendaText
                      }));
                      alert('✅ Agenda saved! It will be included when you share the invite link.');
                    } else {
                      // Remove agenda if empty
                      setRoomAgendas(prev => {
                        const updated = { ...prev };
                        delete updated[agendaForRoom];
                        return updated;
                      });
                      alert('Agenda cleared.');
                    }
                    
                    setShowAgendaModal(false);
                    setAgendaForRoom(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 text-sm font-semibold transition-colors"
                >
                  Save Agenda
                </button>
                <button
                  onClick={() => {
                    setShowAgendaModal(false);
                    setAgendaForRoom(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Documents Hub Modal */}
      {showDocumentsPanel && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-emerald-400/30 rounded-2xl p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📄 Documents Hub</h3>
              <button onClick={() => setShowDocumentsPanel(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              All your uploaded documents from Discussion, Assignment, and Experiential tabs.
            </p>
            <div className="space-y-3">
              {allClassDocuments.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No documents yet. Upload files from any tab to see them here.</p>
              ) : (
                allClassDocuments.map((doc) => {
                  // Check if this is an uploaded file (has base64 data) vs a ThesiSribe document
                  const isUploadedFile = doc.content?.startsWith('data:');
                  
                  return (
                  <div key={doc.id} className="rounded-xl bg-white/5 hover:bg-white/10 px-4 py-3 border border-emerald-300/20 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm text-emerald-100">{doc.title}</p>
                          {doc.isProtected && (
                            <span className="text-amber-400" title="Password Protected">
                              <Lock className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {activeClass?.categories.find(c => c.id === doc.notebookTabId)?.label || 'Unknown'} • 
                          {new Date(doc.created_at).toLocaleDateString()}
                          {isUploadedFile && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200">Uploaded File</span>}
                          {doc.isProtected && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">🔒 Protected</span>}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {isUploadedFile ? (
                          // For uploaded files, show download and delete
                          <>
                            <button
                              onClick={() => {
                                // Check if document is password protected
                                if (doc.isProtected) {
                                  setUnlockingDocId(doc.id);
                                  setUnlockingDocTitle(doc.title);
                                  setShowUnlockModal(true);
                                  return;
                                }
                                
                                const link = document.createElement('a');
                                link.href = doc.content;
                                link.download = doc.title;
                                link.click();
                                
                                // Show notification and highlight Documents Hub
                                setShowDownloadNotification(true);
                                setTimeout(() => setShowDownloadNotification(false), 5000);
                                
                                // Scroll to Documents Hub button with smooth animation
                                setTimeout(() => {
                                  documentsHubRef.current?.scrollIntoView({ 
                                    behavior: 'smooth', 
                                    block: 'center' 
                                  });
                                  // Flash the Documents Hub button
                                  documentsHubRef.current?.classList.add('animate-bounce');
                                  setTimeout(() => {
                                    documentsHubRef.current?.classList.remove('animate-bounce');
                                  }, 2000);
                                }, 500);
                              }}
                              className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 transition-colors"
                              title="Download file"
                            >
                              ⬇️ Download
                            </button>
                            <button
                              onClick={(e) => handleDeleteDocument(doc.id, e)}
                              className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors"
                              title="Delete file"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        ) : (
                          // For ThesiScribe documents and journal entries, show all three
                          <>
                            <button
                              onClick={() => {
                                // Create a downloadable text file from the content
                                const blob = new Blob([doc.content], { type: 'text/plain' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `${doc.title}.txt`;
                                link.click();
                                URL.revokeObjectURL(url);
                              }}
                              className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 transition-colors"
                              title="Download as text file"
                            >
                              ⬇️
                            </button>
                            <button
                              onClick={() => navigate(`/editor?documentId=${doc.id}`)}
                              className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 transition-colors"
                            >
                              Open
                            </button>
                            <button
                              onClick={(e) => handleDeleteDocument(doc.id, e)}
                              className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Document Confirmation Modal */}
      {deleteDocConfirm && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#050309] border-2 border-red-500/30 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center text-2xl">
                🗑️
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Delete Document</h3>
                <p className="text-sm text-slate-400">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-6">
              Are you sure you want to delete "<strong>{allClassDocuments.find(d => d.id.toString() === deleteDocConfirm)?.title}</strong>"? 
              All content will be permanently removed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={cancelDeleteDocument}
                className="flex-1 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 
                           text-white font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDocument}
                className="flex-1 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 
                           text-white font-semibold transition-all shadow-lg shadow-red-500/30"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Center Modal */}
      {showUploadPanel && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-fuchsia-400/30 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📤 Upload Center</h3>
              <button onClick={() => setShowUploadPanel(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Upload PDFs, Word documents (DOC/DOCX), or text files to any tab.
            </p>
            
            {/* Tab Selection */}
            <div className="mb-4">
              <label className="text-sm text-slate-300 mb-2 block">Upload to which tab?</label>
              <select 
                value={uploadTabSelection}
                onChange={(e) => setUploadTabSelection(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-fuchsia-400"
              >
                <option value="">Select a tab...</option>
                {activeClass?.categories.filter(c => c.id !== 'snack').map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Password Protection Checkbox */}
            <div className="mb-4 flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <input
                type="checkbox"
                id="protect-password"
                checked={protectWithPassword}
                onChange={(e) => setProtectWithPassword(e.target.checked)}
                className="w-4 h-4 rounded border-amber-500 text-amber-500 focus:ring-amber-500"
              />
              <label htmlFor="protect-password" className="text-sm text-amber-200 cursor-pointer flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span>🔒 Protect with password (recommended for sensitive documents)</span>
              </label>
            </div>

            <div 
              className="border-2 border-dashed border-fuchsia-400/30 rounded-xl p-8 text-center bg-fuchsia-500/5"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files && uploadTabSelection) {
                  handleFileSelect(e.dataTransfer.files);
                }
              }}
            >
              <Upload className="w-12 h-12 text-fuchsia-300 mx-auto mb-3" />
              <p className="text-fuchsia-200 mb-2">Drag & drop files here</p>
              <p className="text-xs text-slate-400 mb-4">or click to browse</p>
              <input
                type="file"
                multiple={!protectWithPassword}
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                id="file-upload"
                onChange={(e) => {
                  if (e.target.files && uploadTabSelection) {
                    handleFileSelect(e.target.files);
                  } else if (!uploadTabSelection) {
                    alert('Please select a tab first');
                  }
                }}
                disabled={uploadingFiles}
              />
              <label
                htmlFor="file-upload"
                className={`inline-block px-4 py-2 rounded-lg ${
                  uploadingFiles 
                    ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed' 
                    : 'bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/30 cursor-pointer'
                } transition-colors`}
              >
                {uploadingFiles ? 'Uploading...' : 'Choose Files'}
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-4 text-center">
              Supported formats: PDF, DOC, DOCX, TXT • Max 10MB per file
            </p>
          </div>
        </div>
      )}
      
      {/* WHIPT Transcripts Modal */}
      {showTranscriptsPanel && !transcriptsPanelMinimized && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-t-3xl md:rounded-3xl w-full max-w-7xl h-[90vh] md:h-auto max-h-[90vh] flex flex-col shadow-2xl p-6 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] overflow-hidden">
            <WhiptTranscriptsPanel 
              onClose={() => setShowTranscriptsPanel(false)} 
              embedded={true}
              onMinimize={(minimized) => setTranscriptsPanelMinimized(minimized)}
            />
          </div>
        </div>
      )}
      
      {/* Minimized Transcripts Floating Button */}
      {showTranscriptsPanel && transcriptsPanelMinimized && (
        <button
          onClick={() => setTranscriptsPanelMinimized(false)}
          className="fixed bottom-24 left-4 z-[10000] px-4 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-2xl hover:shadow-purple-500/50 hover:scale-105 transition-all flex items-center gap-2"
          title="Restore Transcription Panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-sm">Transcription</span>
        </button>
      )}

      {/* Session Recordings Modal */}
      {showRecordingsPanel && !recordingsPanelMinimized && (
        <SessionRecordingsPanel
          onClose={() => setShowRecordingsPanel(false)}
          onMinimize={(minimized) => setRecordingsPanelMinimized(minimized)}
        />
      )}
      {showRecordingsPanel && recordingsPanelMinimized && (
        <button
          onClick={() => setRecordingsPanelMinimized(false)}
          className="fixed bottom-4 right-4 z-[10000] px-4 py-3 rounded-2xl bg-gradient-to-r from-rose-600 to-pink-700 text-white font-bold shadow-2xl border border-rose-400/50 hover:brightness-110 transition-all flex items-center gap-3"
        >
          <Video className="w-5 h-5" />
          <span className="text-sm">Recordings</span>
        </button>
      )}

      {/* WHIPT SUMM Panel */}
      {showWhiptSumm && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-amber-400/30 rounded-t-3xl md:rounded-3xl w-full max-w-7xl h-[90vh] md:h-auto max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <WhiptSummPanel onClose={() => setShowWhiptSumm(false)} embedded={true} />
          </div>
        </div>
      )}
      
      {/* Edit Item Modal */}
      {editingItemId && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-amber-400/30 rounded-3xl w-full max-w-2xl shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-amber-400/30">
              <h2 className="text-2xl font-bold text-amber-100">✏️ Edit Item</h2>
              <button
                onClick={() => {
                  setEditingItemId(null);
                  setEditingItemContext(null);
                  setEditFormData({ title: '', description: '', dueDate: '', location: '' });
                }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-amber-200 mb-2">Title *</label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-amber-400/30 text-white focus:outline-none focus:border-amber-400 transition-colors"
                  placeholder="Item title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-amber-200 mb-2">Due Date & Time *</label>
                <input
                  type="datetime-local"
                  value={editFormData.dueDate ? editFormData.dueDate.slice(0, 16) : ''}
                  onChange={(e) => setEditFormData({ ...editFormData, dueDate: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-amber-400/30 text-white focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-amber-200 mb-2">Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-amber-400/30 text-white focus:outline-none focus:border-amber-400 transition-colors resize-none"
                  placeholder="Add notes or details"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-amber-200 mb-2">Location (optional)</label>
                <input
                  type="text"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-amber-400/30 text-white focus:outline-none focus:border-amber-400 transition-colors"
                  placeholder="Meeting location or link"
                />
              </div>
            </div>

            <div className="p-6 border-t border-amber-400/30 flex gap-3">
              <button
                onClick={() => {
                  setEditingItemId(null);
                  setEditingItemContext(null);
                  setEditFormData({ title: '', description: '', dueDate: '', location: '' });
                }}
                className="flex-1 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!editFormData.title.trim() || !editFormData.dueDate) {
                    alert('Please fill in title and due date');
                    return;
                  }

                  const targetClassId = editingItemContext?.classId || activeClassId;
                  const targetCategoryId = editingItemContext?.categoryId || activeCategoryId;

                  const updatedNotebooks = classNotebooks.map((notebook) => {
                    if (notebook.id !== targetClassId) return notebook;

                    return {
                      ...notebook,
                      categories: notebook.categories.map((category: any) => {
                        if (category.id !== targetCategoryId) return category;

                        const updateBucketItems = (bucketItems: any[] = []) =>
                          bucketItems.map((item: any) =>
                            String(item.id) === String(editingItemId)
                              ? {
                                  ...item,
                                  title: editFormData.title.trim(),
                                  description: editFormData.description.trim(),
                                  notes: editFormData.description.trim(),
                                  dueDate: editFormData.dueDate,
                                  location: editFormData.location.trim(),
                                }
                              : item
                          );

                        return {
                          ...category,
                          buckets: {
                            upcoming: updateBucketItems(category.buckets?.upcoming || []),
                            completed: updateBucketItems(category.buckets?.completed || []),
                          },
                        };
                      }),
                    };
                  });

                  setClassNotebooks(updatedNotebooks);
                  if (user?.id) {
                    await saveNotebooks(user.id, updatedNotebooks);
                  }
                  
                  setEditingItemId(null);
                  setEditingItemContext(null);
                  setEditFormData({ title: '', description: '', dueDate: '', location: '' });
                }}
                className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold transition-all flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Calendar Panel Modal */}
      {showCalendarPanel && (() => {
        const allCalendarItems = [
          ...classNotebooks.flatMap(notebook =>
            notebook.categories.flatMap(cat => 
              (cat.buckets?.upcoming || []).concat(cat.buckets?.completed || [])
                .filter(item => item.dueDate)
                .map(item => ({
                  type: 'assignment',
                  title: item.title,
                  date: item.dueDate?.split('T')[0],
                  time: item.dueDate?.split('T')[1]?.substring(0, 5),
                  course: notebook.title,
                  status: item.status,
                  description: item.description || item.notes
                }))
            )
          ),
          ...scheduledSessions.map(session => ({
            type: 'meeting',
            title: session.title,
            sessionId: session.sessionId,
            date: session.date,
            time: session.time ? `${formatTimeTo12Hour(session.time)}${session.timezone ? ` (${session.timezone})` : ''}` : undefined,
            timezone: session.timezone,
            duration: session.duration,
            course: classNotebooks.find(nb => nb.id === session.classId)?.title,
            recurring: session.recurring,
            status: 'upcoming'
          }))
        ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const filteredItems = allCalendarItems.filter(item => {
          if (item.type === 'meeting' && !calendarFilters.meetings) return false;
          if (item.type === 'assignment' && !calendarFilters.assignments) return false;
          if (item.status === 'completed' && !calendarFilters.completed) return false;
          return true;
        });

        return (
          <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-[#050309] border border-amber-400/30 rounded-2xl p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">📅 Calendar Events</h3>
                <button onClick={() => setShowCalendarPanel(false)} className="text-slate-400 hover:text-white text-xl">
                  ✕
                </button>
              </div>

              {/* Filter Options */}
              <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white/5 rounded-lg border border-white/10">
                <span className="text-xs text-slate-300 font-medium">Show:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={calendarFilters.meetings}
                    onChange={(e) => setCalendarFilters(prev => ({ ...prev, meetings: e.target.checked }))}
                    className="w-4 h-4 rounded border-amber-400/50 bg-white/10 text-amber-500 focus:ring-2 focus:ring-amber-500"
                  />
                  <span className="text-xs text-slate-200">🎥 Virtual Meetings</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={calendarFilters.assignments}
                    onChange={(e) => setCalendarFilters(prev => ({ ...prev, assignments: e.target.checked }))}
                    className="w-4 h-4 rounded border-purple-400/50 bg-white/10 text-purple-500 focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-xs text-slate-200">📚 Assignments</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={calendarFilters.completed}
                    onChange={(e) => setCalendarFilters(prev => ({ ...prev, completed: e.target.checked }))}
                    className="w-4 h-4 rounded border-emerald-400/50 bg-white/10 text-emerald-500 focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-xs text-slate-200">✅ Completed</span>
                </label>
              </div>

              {/* Month View Calendar Grid */}
              <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
                {(() => {
                  const today = new Date();
                  const year = calendarDate.getFullYear();
                  const month = calendarDate.getMonth();
                  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  
                  const days = [];
                  for (let i = 0; i < firstDay; i++) {
                    days.push(<div key={`empty-${i}`} className="aspect-square" />);
                  }
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dayEvents = allCalendarItems.filter(item => {
                      // Debug: Log comparison for March dates
                      if (month === 2 && day >= 1 && day <= 10) {
                        allCalendarItems.forEach(item => {
                          if (item.date && item.date.startsWith('2026-03')) {
                          }
                        });
                      }
                      return item.date === dateStr;
                    });
                    const hasEvents = dayEvents.length > 0;
                    const isToday = isCurrentMonth && day === today.getDate();
                    const isSelectedDay = !!(
                      selectedDayDate &&
                      selectedDayDate.getFullYear() === year &&
                      selectedDayDate.getMonth() === month &&
                      selectedDayDate.getDate() === day
                    );
                    
                    days.push(
                      <button
                        key={day}
                        onClick={() => {
                          const clickedDate = new Date(year, month, day);
                          setSelectedDayDate(clickedDate);
                          setSelectedDayEvents(dayEvents);
                          setShowDayEventsPopup(true);
                        }}
                        className={`aspect-square p-2 rounded-lg text-sm transition-all relative text-left ${
                          isSelectedDay
                            ? 'ring-2 ring-amber-300/70 shadow-[0_0_18px_rgba(251,191,36,0.45)]'
                            : ''
                        } ${
                          isToday 
                            ? 'bg-amber-500/20 border-2 border-amber-400 text-white font-bold' 
                            : 'hover:bg-white/10 text-slate-300'
                        } ${hasEvents ? 'font-semibold' : ''}`}
                      >
                        <div className="font-semibold">{day}</div>
                        {hasEvents && (
                          <div
                            className={`mt-1 space-y-1 pr-0.5 ${dayEvents.length > 2 ? 'max-h-10 overflow-y-auto' : 'overflow-y-hidden'}`}
                          >
                            {dayEvents.map((event, idx) => (
                              <div
                                key={`${dateStr}-${idx}`}
                                className="w-full text-[10px] leading-tight px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-100 truncate"
                                title={event.title}
                              >
                                {event.title}
                              </div>
                            ))}
                          </div>
                        )}
                        {hasEvents && (
                          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full bg-amber-400" />
                        )}
                      </button>
                    );
                  }
                  
                  return (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <button 
                          onClick={goToPreviousMonth}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-amber-200 hover:text-white hover:bg-amber-500/20 transition-all"
                          title="Previous month"
                        >
                          ‹
                        </button>
                        <button
                          onClick={goToToday}
                          className="flex-1 mx-2 text-lg font-bold text-white hover:text-amber-200 transition-colors"
                          title="Jump to today"
                        >
                          {getMonthYearString()}
                        </button>
                        <button 
                          onClick={goToNextMonth}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-amber-200 hover:text-white hover:bg-amber-500/20 transition-all"
                          title="Next month"
                        >
                          ›
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 text-center mb-3">Click any date to view events</p>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="text-center text-xs font-semibold text-slate-400 py-2">
                            {day}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {days}
                      </div>
                    </>
                  );
                })()}
              </div>

              <p className="text-sm text-slate-300 mb-4">
                {filteredItems.length} {filteredItems.length === 1 ? 'event' : 'events'} shown
              </p>

              <div className="space-y-3">
                {filteredItems.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">
                    No events match your filters.
                  </p>
                ) : (
                  filteredItems.map((item: any, idx: number) => (
                    <div
                      key={idx}
                      className={`rounded-xl px-4 py-3 border transition-colors ${
                        item.type === 'meeting'
                          ? 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-300/20'
                          : 'bg-purple-500/10 hover:bg-purple-500/20 border-purple-300/20'
                      } ${
                        item.status === 'completed' ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className={`font-semibold text-sm ${
                            item.type === 'meeting' ? 'text-amber-100' : 'text-purple-100'
                          } ${
                            item.status === 'completed' ? 'line-through' : ''
                          }`}>
                            {item.type === 'meeting' ? '🎥' : '📚'} {item.title}
                          </p>
                          {item.course && (
                            <p className="text-xs text-slate-400 mt-1">Class: {item.course}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                            <span>📅 {item.date}</span>
                            {item.time && <span>🕐 {item.time}</span>}
                            {item.duration && <span>⏱️ {item.duration}</span>}
                            {item.status === 'completed' && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-[10px]">
                                ✅ Completed
                              </span>
                            )}
                          </div>
                          {item.recurring && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 inline-block mt-2">
                              🔄 {item.recurring}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Quick Add Menu - Appears when clicking a calendar date */}
      {showQuickAddMenu && selectedCalendarDate && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[10001]"
            onClick={() => {
              setShowQuickAddMenu(false);
              setSelectedCalendarDate(null);
            }}
          />
          {/* Menu */}
          <div 
            className="fixed z-[10002] bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-400/40 rounded-xl shadow-2xl p-4 min-w-[200px]"
            style={{ 
              left: `${quickAddMenuPosition.x}px`, 
              top: `${quickAddMenuPosition.y}px`,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="text-xs text-amber-300 font-semibold mb-2">
              Add to {selectedCalendarDate}
            </div>
            <div className="space-y-1">
              <button
                onClick={() => {
                  setShowQuickAddMenu(false);
                  openCalendarFormForType('assignment', selectedCalendarDate, true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 text-white text-sm transition-colors flex items-center gap-2"
              >
                <span>📚</span>
                <span>Assignment</span>
              </button>
              <button
                onClick={() => {
                  setShowQuickAddMenu(false);
                  openCalendarFormForType('meeting', selectedCalendarDate, true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-white text-sm transition-colors flex items-center gap-2"
              >
                <span>🎥</span>
                <span>Meeting</span>
              </button>
              <button
                onClick={() => {
                  setShowQuickAddMenu(false);
                  openCalendarFormForType('event', selectedCalendarDate, true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 text-white text-sm transition-colors flex items-center gap-2"
              >
                <span>📅</span>
                <span>Event</span>
              </button>
              <button
                onClick={() => {
                  setShowQuickAddMenu(false);
                  openCalendarFormForType('reminder', selectedCalendarDate, true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/30 text-white text-sm transition-colors flex items-center gap-2"
              >
                <span>⏰</span>
                <span>Reminder</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Date Events Popup Modal */}
      {selectedDateEvents && (
        <div 
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedDateEvents(null)}
        >
          <div 
            className="bg-[#050309] border border-purple-400/30 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📅 {selectedDateEvents.date}</h3>
              <button 
                onClick={() => setSelectedDateEvents(null)} 
                className="text-slate-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-white/10 transition-colors"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              {selectedDateEvents.items?.length || 0} {selectedDateEvents.items?.length === 1 ? 'item' : 'items'} due on this date
            </p>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
              {(selectedDateEvents.items || []).map((event: any, idx: number) => (
                <div key={idx} className="rounded-xl bg-white/5 hover:bg-white/10 px-4 py-3 border border-purple-300/20 transition-colors group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-purple-100">{event.title}</p>
                      {event.description && (
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">{event.description}</p>
                      )}
                      {event.course && (
                        <p className="text-xs text-amber-300 mt-1">📚 {event.course}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                        {event.dueDate && (
                          <span>🕐 {event.time ? `${formatTimeTo12Hour(event.time)}${event.timezone ? ` (${event.timezone})` : ''}` : (extractTimeFromDateTime(event.dueDate) ? formatTimeTo12Hour(extractTimeFromDateTime(event.dueDate)) : 'TBD')}</span>
                        )}
                        {event.status && (
                          <span className={`px-2 py-0.5 rounded-full ${
                            event.status === 'completed' ? 'bg-emerald-500/20 text-emerald-200' : 
                            event.status === 'inProgress' ? 'bg-amber-500/20 text-amber-200' : 
                            'bg-cyan-500/20 text-cyan-200'
                          }`}>
                            {event.status}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Edit/Delete buttons */}
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={async () => {
                          const sessionId = typeof event.id === 'string' && event.id.startsWith('session-')
                            ? event.id.replace('session-', '')
                            : null;

                          if (sessionId) {
                            const newTitle = window.prompt('Edit meeting title:', event.title?.replace(/^🎥\s*/, '') || event.title);
                            if (!newTitle || !newTitle.trim()) return;

                            const newDate = window.prompt('Edit meeting date (YYYY-MM-DD):', event.date || '');
                            if (!newDate || !newDate.trim()) return;

                            const defaultTime = event.time && event.time.includes(':') ? event.time.substring(0, 5) : '';
                            const newTime = window.prompt('Edit meeting time (HH:MM, 24-hour):', defaultTime);
                            if (!newTime || !newTime.trim()) return;

                            const newTimezone = window.prompt('Edit meeting timezone (IANA, e.g. America/New_York):', event.timezone || defaultScheduleTimezone) || defaultScheduleTimezone;

                            const updatedSessions = scheduledSessions.map((session: any) =>
                              session.sessionId === sessionId
                                ? {
                                    ...session,
                                    title: newTitle.trim(),
                                    date: newDate.trim(),
                                    time: newTime.trim(),
                                    timezone: newTimezone.trim(),
                                  }
                                : session
                            );
                            setScheduledSessions(updatedSessions);
                            setSelectedDateEvents(null);
                            return;
                          }

                          const newTitle = window.prompt('Edit event title:', event.title);
                          if (newTitle && newTitle.trim()) {
                            // Update in notebooks
                            const updated = classNotebooks.map(notebook => ({
                              ...notebook,
                              categories: notebook.categories.map(cat => ({
                                ...cat,
                                buckets: {
                                  ...cat.buckets,
                                  upcoming: (cat.buckets?.upcoming || []).map(item =>
                                    item.id === event.id ? { ...item, title: newTitle.trim() } : item
                                  )
                                }
                              }))
                            }));
                            setClassNotebooks(updated);
                            if (user?.id) await saveNotebooks(user.id, updated);
                            setSelectedDateEvents(null); // Close modal
                          }
                        }}
                        className="w-7 h-7 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 flex items-center justify-center transition-all"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={async () => {
                          console.log('[Delete Button] Clicked, event:', event);
                          if (window.confirm(`Delete "${event.title}"?`)) {
                            console.log('[Delete Button] Confirmed, deleting event ID:', event.id);
                            try {
                              const sessionId = typeof event.id === 'string' && event.id.startsWith('session-')
                                ? event.id.replace('session-', '')
                                : null;

                              if (sessionId) {
                                const updatedSessions = scheduledSessions.filter((session: any) => session.sessionId !== sessionId);
                                setScheduledSessions(updatedSessions);
                                setSelectedDateEvents(null);
                                return;
                              }

                              // Delete from notebooks
                              const updated = classNotebooks.map(notebook => ({
                                ...notebook,
                                categories: notebook.categories.map(cat => ({
                                  ...cat,
                                  buckets: {
                                    ...cat.buckets,
                                    upcoming: (cat.buckets?.upcoming || []).filter(item => item.id !== event.id)
                                  }
                                }))
                              }));
                              setClassNotebooks(updated);
                              console.log('[Delete Button] Updated notebooks, saving...');
                              if (user?.id) await saveNotebooks(user.id, updated);
                              console.log('[Delete Button] Notebooks saved');
                              
                              // Also delete from calendar_events table if it exists there
                              try {
                                console.log('[Delete Button] Attempting to delete from calendar_events table...');
                                await deleteCalendarEvent(event.id);
                                console.log('[Delete Button] Deleted from calendar_events table');
                              } catch (err) {
                                console.log('[Delete Button] Calendar event not found in DB (may only exist in notebooks):', err);
                              }
                              
                              console.log('[Delete Button] Closing modal');
                              setSelectedDateEvents(null); // Close modal
                            } catch (error) {
                              console.error('[Delete Button] Error deleting event:', error);
                              alert('Error deleting event. Please try again.');
                            }
                          }
                        }}
                        className="w-7 h-7 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 flex items-center justify-center transition-all"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Add New Event Button */}
            <div className="mt-4 pt-4 border-t border-purple-400/20">
              <button
                onClick={() => {
                  // Extract date from selectedDateEvents and prefill the form
                  let inferredType: 'assignment' | 'meeting' | 'reminder' | 'event' = 'assignment';
                  let shouldLockType = false;

                  const typedItems = (selectedDateEvents?.items || [])
                    .map((item: any) => normalizeCalendarEventType(item?.eventType || item?.type))
                    .filter(Boolean) as Array<'assignment' | 'meeting' | 'reminder' | 'event'>;

                  if (typedItems.length > 0) {
                    const uniqueTypes = Array.from(new Set(typedItems));
                    if (uniqueTypes.length === 1) {
                      inferredType = uniqueTypes[0];
                      shouldLockType = true;
                    }
                  }

                  if (selectedDateEvents?.dateObj) {
                    const dateStr = `${selectedDateEvents.dateObj.getFullYear()}-${String(selectedDateEvents.dateObj.getMonth() + 1).padStart(2, '0')}-${String(selectedDateEvents.dateObj.getDate()).padStart(2, '0')}`;
                    openCalendarFormForType(inferredType, dateStr, shouldLockType);
                  } else {
                    openCalendarFormForType(inferredType, undefined, shouldLockType);
                  }
                  setSelectedDateEvents(null);
                }}
                className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
              >
                <span className="text-xl">+</span>
                Add New Item to This Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Events Popup - NEW Interactive Modal */}
      {showDayEventsPopup && selectedDayDate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[10000] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-400/60 rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-b border-amber-400/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-amber-100">
                  📅 {selectedDayDate.toDateString() === new Date().toDateString() ? "Today's Schedule" : "Schedule"}
                </h3>
                <button
                  onClick={() => setShowDayEventsPopup(false)}
                  className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-amber-200/80">
                {selectedDayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>

            {/* Items List - Scrollable with visible scrollbar */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[60vh] scrollbar-thin scrollbar-thumb-amber-500/40 scrollbar-track-transparent">
              {selectedDayEvents.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-3">🎉</div>
                  <p className="text-lg font-semibold text-white mb-2">
                    {selectedDayDate.toDateString() === new Date().toDateString() 
                      ? "No items due today!" 
                      : "No items due on this date!"}
                  </p>
                  <p className="text-sm text-slate-400">This day is clear</p>
                </div>
              ) : (
                selectedDayEvents.map((item: any, idx: number) => {
                  const isCompleted = item.status === 'completed';
                  const isAssignment = item.type === 'assignment';
                  const gradient = isAssignment ? 'from-purple-500 to-blue-500' : 'from-amber-500 to-orange-500';

                  return (
                    <div
                      key={idx}
                      className={`bg-black/40 border border-white/20 rounded-xl p-4 ${isCompleted ? 'opacity-60' : ''} hover:border-white/40 transition-all cursor-pointer`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white text-lg">
                            {isAssignment ? '📚' : '🎥'}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold text-base mb-1 ${isCompleted ? 'line-through text-emerald-300/70' : 'text-white'}`}>
                            {item.title}
                          </h4>
                          
                          {item.course && (
                            <p className="text-xs text-purple-300 mb-1">📁 {item.course}</p>
                          )}

                          <div className="flex items-center gap-2 text-xs flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full bg-gradient-to-r ${gradient} text-white font-semibold`}>
                              {item.type === 'assignment' ? 'Assignment' : 'Meeting'}
                            </span>
                            {item.time && <span className="text-slate-400">🕐 {item.time}</span>}
                            {isCompleted && (
                              <span className="text-emerald-400">✓ Completed</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Button */}
            <div className="border-t border-white/10 p-4">
              <button
                onClick={() => setShowDayEventsPopup(false)}
                className="w-full px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && inviteRoomId && (
        <div 
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            setShowInviteModal(false);
            setInviteRoomId(null);
            setInviteEmails('');
            setInviteMessage('');
          }}
        >
          <div 
            className="bg-[#050309] border border-emerald-400/30 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📧 Send Room Invitation</h3>
              <button 
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteRoomId(null);
                  setInviteEmails('');
                  setInviteMessage('');
                }} 
                className="text-slate-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-white/10 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Room Info */}
              <div className="rounded-lg bg-purple-500/10 border border-purple-400/30 p-3">
                <p className="text-sm text-purple-200">
                  <span className="font-semibold">Room:</span> {inviteRoomId === 'video' ? 'Social/Family Room' : (CATEGORY_BLUEPRINTS.find(b => b.id === inviteRoomId)?.label || 'Virtual Room')}
                </p>
                <p className="text-xs text-purple-300 mt-1">
                  <span className="font-semibold">Class:</span> {activeClass?.title || 'No class selected'}
                </p>
              </div>

              {/* Invitation Link - Read-only */}
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Invitation Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/organizer?room=${inviteRoomId}&class=${activeClass?.id}&autojoin=1`}
                    readOnly
                    className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-400"
                  />
                  <button
                    onClick={async () => {
                      const link = `${window.location.origin}/organizer?room=${inviteRoomId}&class=${activeClass?.id}&autojoin=1`;
                      try {
                        await navigator.clipboard.writeText(link);
                        alert('✅ Link copied to clipboard!\n\nYou can now paste it in any email, text message, or chat app.');
                      } catch (err) {
                        alert(`Copy this link:\n\n${link}`);
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 text-sm font-semibold transition-colors whitespace-nowrap"
                  >
                    📋 Copy Link
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  💡 Tip: Copy the link to share via text, chat apps, or social media
                </p>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-xs text-slate-400 uppercase tracking-wider">Or send via email</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              {/* Email Addresses */}
              <div>
                <label className="text-sm text-slate-300 mb-2 block">
                  Recipient Email Addresses
                  <span className="text-slate-500 ml-2">(comma or line separated)</span>
                </label>
                <textarea
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="student1@university.edu, student2@university.edu&#10;professor@university.edu"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-emerald-400 h-24 resize-none"
                />
                
                {/* Recipient Counter & Warnings */}
                {(() => {
                  const emails = inviteEmails
                    .split(/[,\n]/)
                    .map(e => e.trim())
                    .filter(e => e.length > 0);
                  
                  const count = emails.length;
                  
                  if (count === 0) return null;
                  
                  return (
                    <div className="mt-2 space-y-2">
                      {/* Counter */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-2 py-1 rounded-full ${
                          count <= 50 ? 'bg-emerald-500/20 text-emerald-200' :
                          count <= 100 ? 'bg-amber-500/20 text-amber-200' :
                          'bg-red-500/20 text-red-200'
                        }`}>
                          ✓ {count} recipient{count !== 1 ? 's' : ''} ready
                        </span>
                      </div>
                      
                      {/* Warning for 50-100 */}
                      {count > 50 && count <= 100 && (
                        <div className="rounded-lg bg-amber-500/10 border border-amber-400/30 p-2">
                          <p className="text-xs text-amber-200 flex items-start gap-2">
                            <span>⚠️</span>
                            <span>
                              <strong>Large group detected.</strong> Some email clients may have recipient limits. 
                              Consider splitting into 2 batches if you experience issues.
                            </span>
                          </p>
                        </div>
                      )}
                      
                      {/* Warning for >100 */}
                      {count > 100 && (
                        <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-2">
                          <p className="text-xs text-red-200 flex items-start gap-2">
                            <span>🚨</span>
                            <span>
                              <strong>Very large group!</strong> Most email clients limit to 50-100 recipients per message. 
                              <strong className="block mt-1">Recommendation:</strong> Split into batches of 50 recipients and send multiple times.
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Custom Message */}
              <div>
                <label className="text-sm text-slate-300 mb-2 block">
                  Personal Message (Optional)
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Add a personal message to your invitation..."
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-emerald-400 h-20 resize-none"
                />
              </div>

              {/* Agenda Preview (if exists) */}
              {roomAgendas[inviteRoomId] && (
                <div className="rounded-lg bg-blue-500/10 border border-blue-400/30 p-3">
                  <p className="text-sm text-blue-200 font-semibold mb-2">📋 Meeting Agenda:</p>
                  <pre className="text-xs text-blue-300 whitespace-pre-wrap font-mono">{roomAgendas[inviteRoomId]}</pre>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteRoomId(null);
                    setInviteEmails('');
                    setInviteMessage('');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Parse emails
                    const emails = inviteEmails
                      .split(/[,\n]/)
                      .map(e => e.trim())
                      .filter(e => e.length > 0);

                    if (emails.length === 0) {
                      alert('❌ Please enter at least one email address');
                      return;
                    }

                    // Validate emails
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const invalidEmails = emails.filter(e => !emailRegex.test(e));
                    if (invalidEmails.length > 0) {
                      alert(`❌ Invalid email addresses:\n${invalidEmails.join('\n')}`);
                      return;
                    }

                    // Warning for large groups
                    if (emails.length > 100) {
                      const confirmed = window.confirm(
                        `⚠️ You're sending to ${emails.length} recipients.\n\n` +
                        `Resend has a limit of 100 recipients per email.\n\n` +
                        `Click OK to send to first 100 recipients now, or Cancel to adjust your list.`
                      );
                      if (!confirmed) return;
                      
                      // Limit to first 100 if proceeding
                      emails.splice(100);
                      alert(`✓ Sending to first 100 recipients. Send remaining ${inviteEmails.split(/[,\n]/).filter(e => e.trim()).length - 100} in a second batch.`);
                    }

                    // Build invitation data
                    const roomName = inviteRoomId === 'video' ? 'Social/Family Room' : (CATEGORY_BLUEPRINTS.find(b => b.id === inviteRoomId)?.label || 'Virtual Room');
                    const link = `${window.location.origin}/organizer?room=${inviteRoomId}&class=${activeClass?.id}&autojoin=1`;
                    
                    // Show sending state
                    const button = event.currentTarget as HTMLButtonElement;
                    const originalText = button.innerHTML;
                    button.disabled = true;
                    button.innerHTML = '⏳ Sending...';
                    
                    try {
                      // TODO: Re-enable email sending via Supabase edge function once SendGrid creds are fixed/verified.
                      // Temporary behavior: just copy the invite link for manual sharing.
                      await navigator.clipboard.writeText(link);
                      alert(`Link copied. Paste it into email/text to invite participants.\n\n${link}`);

                      setShowInviteModal(false);
                      setInviteRoomId(null);
                      setInviteEmails('');
                      setInviteMessage('');
                    } catch (error) {
                      console.error('Error copying invitations link:', error);
                      alert(`Copy failed. Please copy this link manually:\n\n${link}`);

                      // Restore button
                      button.disabled = false;
                      button.innerHTML = originalText;
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 text-sm font-semibold transition-colors"
                >
                  📧 Send Invitations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integrated Messaging */}
      {showMessaging && (() => {
        const userData = localStorage.getItem('whipt_user');
        const currentUser = userData ? JSON.parse(userData) : null;
        
        if (!currentUser) {
          return (
            <div className="fixed bottom-4 right-4 z-50 bg-red-500/20 border-2 border-red-500 rounded-2xl p-6 text-white">
              <p className="font-bold">Error: Not logged in</p>
              <p className="text-sm">Please refresh and sign in again.</p>
            </div>
          );
        }
        
        return (
          <IntegratedMessaging
            currentUser={currentUser}
            onClose={() => setShowMessaging(false)}
            isMinimized={isMessagingMinimized}
            onMinimize={() => setIsMessagingMinimized(!isMessagingMinimized)}
          />
        );
      })()}

      {/* Group Chat Window */}
      {selectedRoom && (
        <GroupChatWindow
          room={selectedRoom}
          onClose={() => setSelectedRoom(null)}
          isMinimized={isRoomChatMinimized}
          onMinimize={() => setIsRoomChatMinimized(!isRoomChatMinimized)}
        />
      )}

      {/* Feature Tour */}
      <FeatureTour />

      {/* Terms of Service */}
      {(() => {
        return showTerms && (
          <TermsOfService
            onAccept={() => {
              console.log('About to call acceptTerms()...');
              acceptTerms();
              console.log('✅ acceptTerms() called successfully');
              console.log('About to setShowTerms(false)...');
              setShowTerms(false);
              console.log('✅ setShowTerms(false) called - should hide terms now');
            }}
            onDecline={() => {
              // Log out user if they decline
              navigate('/');
            }}
          />
        );
      })()}

      {/* Shared Files Viewer */}
      {showSharedFiles && (
        <SharedFilesViewer onClose={() => setShowSharedFiles(false)} />
      )}

      {/* Snack Dial Modal */}
      {showSnackPanel && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-pink-400/30 rounded-2xl p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">🍴 Snack Dial</h3>
              <button onClick={() => setShowSnackPanel(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            <LateNightSnackPanel
              spots={snackSpots}
              loading={snackLoading}
              onCreate={handleCreateSnackSpot}
              onUpdate={handleUpdateSnackSpot}
              onDelete={handleDeleteSnackSpot}
              onMarkOrdered={handleMarkSnackOrdered}
            />
          </div>
        </div>
      )}

      {/* Apps Integration Panel */}
      {showAppsPanel && (
        <AppsIntegrationPanel onClose={() => setShowAppsPanel(false)} />
      )}

      {/* Budget Assistant */}
      {showBudgetAssistant && (
        <BudgetAssistant onClose={() => setShowBudgetAssistant(false)} />
      )}

      {/* Age Verification Modal */}
      {showAgeVerification && !ageRestrictions && (
        <AgeVerification
          onVerified={(restrictions) => {
            setAgeRestrictions(restrictions);
            setShowAgeVerification(false);
          }}
        />
      )}

      {/* Advertisement Banner (desktop) */}
      <div className="hidden md:block">
        <AdvertisementBanner />
      </div>

      {/* Mobile Ad handle and sheet */}
      <div className="md:hidden">
        {showMobileAds && (
          <div className="fixed inset-0 z-[9800] bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileAds(false)} />
        )}
        <div
          className={`md:hidden fixed bottom-4 right-4 z-[9900] w-[90vw] max-w-sm transition-transform duration-300 ${showMobileAds ? 'translate-x-0' : 'translate-x-[110%]'}`}
        >
          <AdvertisementBanner />
        </div>
        <button
          onClick={() => setShowMobileAds((v) => !v)}
          className="md:hidden fixed right-4 z-[9950] px-3 py-2 rounded-full bg-emerald-600 text-white text-xs shadow-lg"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          {showMobileAds ? 'Hide Ads' : 'Ads'}
        </button>
      </div>

      {/* Footer */}
      <footer className="bg-[#0a0118] border-t border-white/10 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 space-y-6">
          {/* Footer Links */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-amber-300 transition-colors">
                Terms of Service
              </a>
              <span className="text-slate-600">•</span>
              <a href="/privacy" className="hover:text-amber-300 transition-colors">
                Privacy Policy
              </a>
              <span className="text-slate-600">•</span>
              <a href="mailto:support@theblackbettermentquest.org" className="hover:text-amber-300 transition-colors">
                Support
              </a>
              {localStorage.getItem('whipt_dev_mode') === 'true' && (
                <>
                  <span className="text-slate-600">•</span>
                  <a href="/admin/activate" className="hover:text-amber-300 transition-colors text-amber-400">
                    🔧 Admin
                  </a>
                </>
              )}
            </div>
            <p className="text-slate-500 text-sm">Built for students, by students</p>
            <div className="text-xs text-slate-600 space-y-1">
              <p>© 2026 Monica L. James. All rights reserved.</p>
              <p>Where'd I Put That? (WHIPT) is the intellectual property of Monica L. James and Pronoia Wellness LLC.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Add Class Modal */}
      <AddClassModal
        isOpen={showAddClassModal}
        onClose={() => setShowAddClassModal(false)}
        onSubmit={handleAddClass}
      />

      {/* Calendar Import Modal */}
      <CalendarImportModal
        isOpen={showCalendarImport}
        onClose={() => setShowCalendarImport(false)}
        onEventsImported={handleEventsImported}
        mode="student"
      />

      {/* Event Assignment Modal */}
      <EventAssignmentModal
        isOpen={showEventAssignment}
        onClose={() => setShowEventAssignment(false)}
        events={importedEvents}
        notebooks={classNotebooks.map(nb => ({ id: nb.id, name: nb.title }))}
        onAssign={handleEventAssignments}
      />



      {/* AI Chat & Search handle - desktop/tablet */}
      <QuickTooltip content="AI Chat, Web Search, Scratch Pad, Calendar, Messages & Tasks" side="left">
        <div
          className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-[9500] w-8 h-40 rounded-l-2xl bg-purple-500/30 border border-purple-400/50 backdrop-blur items-center justify-center shadow-lg cursor-pointer hover:w-10 transition-all duration-200"
          onPointerDown={onQuickAccessSwipeStart}
          onPointerMove={onQuickAccessSwipeMove}
          onPointerUp={onQuickAccessSwipeEnd}
          onPointerCancel={onQuickAccessSwipeEnd}
          onClick={() => setShowQuickAccessPanel(true)}
        >
          <div className="-rotate-90 select-none flex flex-col items-center gap-0.5">
            <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 animate-pulse" style={{ textShadow: '0 0 10px rgba(251, 191, 36, 0.6), 0 0 20px rgba(251, 191, 36, 0.3)' }}>AI</span>
            <span className="text-[11px] font-bold text-white tracking-wide leading-tight whitespace-nowrap" style={{ textShadow: '0 0 8px rgba(255, 255, 255, 0.8), 0 0 15px rgba(255, 255, 255, 0.5)' }}>CHAT & SEARCH</span>
          </div>
        </div>
      </QuickTooltip>

      {/* Mobile AI Chat & Search button */}
      <QuickTooltip content="AI Chat, Web Search, Scratch Pad & More" side="right">
        <button
          className="md:hidden fixed left-4 z-[9950] px-5 py-3 rounded-full bg-purple-600 text-white text-sm font-bold shadow-lg flex items-center gap-2"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
          onClick={() => setShowQuickAccessPanel(true)}
        >
          <span className="text-base">🤖</span>
          AI Chat & Search
        </button>
      </QuickTooltip>

      {/* Quick Access Panel - Slide in from right */}
      <QuickAccessPanel
        isOpen={showQuickAccessPanel}
        onClose={() => setShowQuickAccessPanel(false)}
        classNotebooks={classNotebooks}
        unreadMessages={Object.values(unreadMessages).reduce((a, b) => a + b, 0)}
        onOpenCalendar={() => setShowCalendarPanel(true)}
        onOpenMessages={() => setShowMessaging(true)}
        onOpenAI={() => setShowAIPanel(true)}
        onOpenScratchPad={() => setShowScratchPadPanel(true)}
        onItemClick={(event) => {
          setShowQuickAccessPanel(false);
        }}
        onMarkComplete={(itemId) => {
          // Find the item across all notebooks/categories and mark complete
          classNotebooks.forEach(notebook => {
            notebook.categories.forEach(category => {
              const hasItem = category.buckets?.upcoming?.some(item => item.id === itemId);
              if (hasItem) {
                handleCompleteItem(notebook.id, category.id, itemId);
              }
            });
          });
        }}
      />

      {/* AI Assistant Panel */}
      <OrganizerAIPanel
        isOpen={showAIPanel}
        onClose={() => setShowAIPanel(false)}
        onSaveToScratchPad={(content, title) => {
          setShowAIPanel(false);
          setShowScratchPadPanel(true);
          // TODO: Pass content to scratch pad
        }}
      />

      {/* Scratch Pad Panel */}
      <OrganizerScratchPadPanel
        isOpen={showScratchPadPanel}
        onClose={() => setShowScratchPadPanel(false)}
      />

      {/* Feature Tutorial */}
      <FeatureTutorial />

      {/* To-Do List PIP Widget */}
      <TodoListPIP 
        isOpen={showTodoList} 
        onClose={() => setShowTodoList(false)} 
        zIndex={panelZIndexes.todoList}
        onFocus={() => bringToFront('todoList')}
      />

      {/* Suggestion Box - desktop floating */}
      <div className="hidden md:block">
        <SuggestionBoxButton />
      </div>

      {/* Suggestion Box - mobile handle + modal */}
      <div className="md:hidden">
        <button
          onClick={() => setShowMobileSuggestions(true)}
          className="fixed left-4 z-[9950] px-3 py-2 rounded-full bg-amber-600 text-white text-xs shadow-lg"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 3rem)' }}
        >
          Suggestions
        </button>
        <SuggestionBox isOpen={showMobileSuggestions} onClose={() => setShowMobileSuggestions(false)} />
      </div>

      {/* Suggestions Box - controlled by quick tab */}
      <SuggestionBox isOpen={showSuggestions} onClose={() => setShowSuggestions(false)} />

      {/* Music Player Dock - Always rendered at bottom, controlled by quick tab */}
      <MusicPlayerDock 
        forceOpenSignal={musicPlayerSignal} 
        zIndex={panelZIndexes.musicPlayer}
        onFocus={() => bringToFront('musicPlayer')}
      />

      {/* Tab Template Selection Modal */}
      {showTabTemplateModal && (
        <TabTemplateModal
          isOpen={showTabTemplateModal}
          onClose={() => setShowTabTemplateModal(false)}
          onSelect={handleTemplateSelected}
        />
      )}

      {/* Password Protection Modal */}
      <PasswordProtectionModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingFile(null);
        }}
        onConfirm={handlePasswordSet}
        fileName={pendingFile?.name || ''}
      />

      {/* WHIPT WALLET - Digital Wallet Panel */}
      {showDigitalWallet && (
        <DigitalWalletPanel
          documents={walletDocuments}
          onClose={() => setShowDigitalWallet(false)}
          onUnlock={async (docId: string, password: string) => {
            const doc = documents.find(d => d.id === docId);
            if (!doc) return false;
            
            try {
              const { decryptFile } = await import('@/lib/encryption');
              const decryptedContent = await decryptFile(doc.content, password);
              // Open decrypted content in new window
              const win = window.open('', '_blank');
              if (win) {
                win.document.write(`
                  <html>
                    <head>
                      <title>${doc.title}</title>
                      <style>
                        body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #1a1a1a; }
                        iframe, embed { width: 90vw; height: 90vh; border: none; }
                        img { max-width: 90vw; max-height: 90vh; }
                      </style>
                    </head>
                    <body>
                      ${decryptedContent.startsWith('data:application/pdf') 
                        ? `<embed src="${decryptedContent}" type="application/pdf" />` 
                        : decryptedContent.startsWith('data:image') 
                        ? `<img src="${decryptedContent}" alt="${doc.title}" />`
                        : `<iframe src="${decryptedContent}"></iframe>`
                      }
                    </body>
                  </html>
                `);
                win.document.close();
              }
              
              return true;
            } catch (error) {
              console.error('❌ Wallet: Decryption failed:', error);
              alert('Incorrect password or decryption failed. Please try again.');
              return false;
            }
          }}
          onPreview={(doc) => {
            const docData = documents.find(d => d.id === doc.id);
            if (docData && !docData.isProtected) {
              const previewWindow = window.open('', '_blank');
              if (previewWindow) {
                previewWindow.document.write(`
                  <html>
                    <head>
                      <title>${doc.title}</title>
                      <style>
                        body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #1a1a1a; }
                        iframe, embed { width: 90vw; height: 90vh; border: none; }
                        img { max-width: 90vw; max-height: 90vh; }
                      </style>
                    </head>
                    <body>
                      ${doc.content.startsWith('data:application/pdf') 
                        ? `<embed src="${doc.content}" type="application/pdf" />` 
                        : doc.content.startsWith('data:image') 
                        ? `<img src="${doc.content}" alt="${doc.title}" />`
                        : `<iframe src="${doc.content}"></iframe>`
                      }
                    </body>
                  </html>
                `);
                previewWindow.document.close();
              }
            }
          }}
          onDownload={async (doc) => {
            const docData = documents.find(d => d.id === doc.id);
            if (docData && !docData.isProtected) {
              const link = document.createElement('a');
              link.href = doc.content;
              link.download = doc.title;
              link.click();
            }
          }}
        />
      )}

      {/* Gmail Import Modal */}
      {showGmailImport && (
        <GmailImportModal
          isOpen={showGmailImport}
          onClose={() => setShowGmailImport(false)}
          accessToken={session?.provider_token || null}
          onImport={async (filename, content, password) => {
            try {
              // Convert base64 data URL to Blob
              const response = await fetch(content);
              const blob = await response.blob();
              const file = new File([blob], filename, { type: blob.type || 'application/pdf' });
              
              // Import document
              await handleFileUpload(file, password);
            } catch (error) {
              console.error('Failed to import from Gmail:', error);
              alert('Failed to import document. Please try again.');
            }
          }}
        />
      )}

      {/* Password Unlock Modal */}
      <PasswordUnlockModal
        isOpen={showUnlockModal}
        onClose={() => {
          setShowUnlockModal(false);
          setUnlockingDocId(null);
          setUnlockingDocTitle('');
        }}
        onUnlock={handleUnlockDocument}
        fileName={unlockingDocTitle}
      />

      {/* Link Copied Modal - WHIPT Styled */}
      {showLinkCopiedModal && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-gradient-to-br from-purple-900/95 via-indigo-900/95 to-purple-900/95 border-2 border-purple-400/50 rounded-3xl p-8 max-w-lg w-full mx-4 shadow-[0_25px_80px_rgba(168,85,247,0.5)] animate-in zoom-in-95 duration-300">
            {/* Logo/Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-xl animate-pulse">
                <span className="text-4xl">🔗</span>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-white mb-2 tracking-wide">
                Link Copied!
              </h2>
              <p className="text-sm text-purple-200 uppercase tracking-[0.3em] font-semibold">
                WHIPT • {copiedRoomName}
              </p>
            </div>

            {/* Message */}
            <div className="bg-black/30 rounded-2xl p-6 mb-6 border border-purple-400/20">
              <p className="text-amber-200 text-center text-sm leading-relaxed mb-4 font-medium">
                ✨ Paste into <span className="font-bold text-amber-100">email</span> or <span className="font-bold text-amber-100">text</span> to invite participants!
              </p>
              
              {/* Link Display */}
              <div className="bg-slate-950/80 rounded-xl p-4 border border-white/10">
                <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-semibold">Your Invite Link:</p>
                <p className="text-sm text-slate-200 break-all font-mono leading-relaxed">
                  {copiedLink}
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => {
                  window.location.href = `sms:?&body=Join my WHIPT session: ${copiedLink}`;
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-400/30 hover:from-green-600/30 hover:to-emerald-600/30 transition-all"
              >
                <span className="text-2xl">💬</span>
                <span className="text-xs text-green-200 font-semibold">Text</span>
              </button>
              <button
                onClick={() => {
                  window.location.href = `mailto:?subject=Join my WHIPT Session&body=Join me: ${copiedLink}`;
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-400/30 hover:from-blue-600/30 hover:to-cyan-600/30 transition-all"
              >
                <span className="text-2xl">📧</span>
                <span className="text-xs text-blue-200 font-semibold">Email</span>
              </button>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(copiedLink);
                  } catch (err) {
                    // Already copied
                  }
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-400/30 hover:from-purple-600/30 hover:to-pink-600/30 transition-all"
              >
                <span className="text-2xl">📋</span>
                <span className="text-xs text-purple-200 font-semibold">Copy</span>
              </button>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowLinkCopiedModal(false);
                setCopiedLink('');
              }}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-sm uppercase tracking-wider transition-all shadow-lg"
            >
              Got It!
            </button>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <InstallPWAPrompt />
      
      {/* NEW: Tab Overlay Panel for Focused View */}
      {showTabOverlay && overlayTabData.type && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-md"
            onClick={() => {
              setShowTabOverlay(false);
              // Keep the tab selected when closing via backdrop
              setActiveCategoryId(overlayTabData.categoryId);
              setShowEverythingMode(false);
              setActivePhase("upcoming");
              setCategoryExpanded(prev => ({ ...prev, [overlayTabData.categoryId]: true }));
            }}
          />
          
          {/* Overlay Panel */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90%] max-w-[1000px] max-h-[85vh] bg-gradient-to-br from-slate-900/40 via-purple-900/10 to-slate-900/40 backdrop-blur-3xl border border-white/10 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
            {/* Header - Matches tab color with see-through glass effect */}
            <div className={`${(() => {
              const tabColors = {
                discussion: 'bg-blue-900/20 border-b-blue-400/20',
                assignment: 'bg-amber-900/20 border-b-amber-400/20',
                experiential: 'bg-emerald-900/20 border-b-emerald-400/20',
                journal: 'bg-purple-900/20 border-b-purple-400/20',
                video: 'bg-pink-900/20 border-b-pink-400/20',
                home: 'bg-rose-900/20 border-b-rose-400/20',
                work: 'bg-indigo-900/20 border-b-indigo-400/20',
                travel: 'bg-sky-900/20 border-b-sky-400/20'
              };
              return tabColors[overlayTabData.type] || tabColors.discussion;
            })()} backdrop-blur-2xl border-b px-8 py-6 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-white capitalize">
                    {overlayTabData.type === 'video' ? 'Social/Family' : overlayTabData.type}
                  </h2>
                  <p className="text-sm text-white/70 mt-1">{overlayTabData.className}</p>
                </div>
                {(() => {
                  const dueCount = classNotebooks
                    .find(c => c.id === overlayTabData.classId)
                    ?.categories?.find(cat => cat.id === overlayTabData.categoryId)
                    ?.buckets?.upcoming?.length || 0;
                  return dueCount > 0 ? (
                    <span className="ml-3 bg-red-500/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-bold border border-red-400/30">
                      {dueCount} Due
                    </span>
                  ) : null;
                })()}
              </div>
              <button
                onClick={() => {
                  setShowTabOverlay(false);
                  // Keep the tab selected when closing overlay
                  setActiveCategoryId(overlayTabData.categoryId);
                  setShowEverythingMode(false);
                  setActivePhase("upcoming");
                  setCategoryExpanded(prev => ({ ...prev, [overlayTabData.categoryId]: true }));
                }}
                className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg flex items-center justify-center text-white text-2xl transition-all hover:rotate-90"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {(() => {
                const activeClass = classNotebooks.find(c => c.id === overlayTabData.classId);
                const category = activeClass?.categories?.find(cat => cat.id === overlayTabData.categoryId);
                
                // Filter out virtual room notes/transcripts - they belong in Virtual Room Notes section only
                const filterOutVirtualRoomItems = (items: any[]) => {
                  return items.filter(item => {
                    // Only exclude if title contains virtual room keywords (not just eventType)
                    const title = (item.title || '').toLowerCase();
                    if (title.includes('session') || title.includes('virtual room') || title.includes('transcript')) return false;
                    return true;
                  });
                };
                
                const upcomingItems = sortItemsByDueClosestNow(filterOutVirtualRoomItems(category?.buckets?.upcoming || []));
                const completedItems = sortItemsByDueClosestNow(filterOutVirtualRoomItems(category?.buckets?.completed || []));
                
                // Separate upcoming and completed for display
                const allItems = [...upcomingItems, ...completedItems];
                const upcomingCount = upcomingItems.length;
                const completedCount = completedItems.length;
                
                return (
                  <>
                    {/* Due Dates Section */}
                    {upcomingItems.length > 0 && (
                      <div className="bg-gradient-to-r from-amber-500/15 to-red-500/15 border-2 border-amber-500 rounded-xl p-6 mb-8">
                        <h3 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
                          <span>📅</span> Due This Week
                        </h3>
                        <div className="space-y-3">
                          {upcomingItems.map((item) => {
                            const daysUntilDue = item.dueDate 
                              ? Math.ceil((new Date(item.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                              : null;
                            const isUrgent = daysUntilDue !== null && daysUntilDue <= 3;
                            
                            return (
                              <div 
                                key={item.id}
                                className={`bg-black/30 border-l-4 ${isUrgent ? 'border-red-600' : 'border-amber-500'} p-4 rounded-lg hover:bg-black/40 transition-all`}
                              >
                                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                                <div className="flex gap-4 text-sm text-slate-300">
                                  {item.dueDate && (
                                    <span className={`font-bold ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>
                                      ⏰ Due {daysUntilDue === 0 ? 'today' : daysUntilDue === 1 ? 'tomorrow' : `in ${daysUntilDue} days`}
                                      {' '}({new Date(item.dueDate).toLocaleDateString()})
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* All Items Grid - Upcoming First, Then Completed */}
                    <h3 className="text-xl font-bold text-amber-400 mb-2 flex items-center gap-2">
                      📝 In Progress {upcomingCount > 0 && <span className="text-sm text-amber-300">({upcomingCount})</span>}
                    </h3>
                    
                    {upcomingItems.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {upcomingItems.map((item) => {
                          return (
                          <div 
                            key={item.id}
                            className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/8 hover:border-purple-400 transition-all group"
                          >
                            <h4 className="text-amber-400 font-semibold mb-2">{item.title}</h4>
                            <div className="flex flex-col gap-2 text-sm">
                              <div className="flex flex-wrap items-center gap-2 min-w-0">
                                <span className="text-amber-400 flex items-center gap-1.5">
                                  <span className="flex items-center gap-0.5 h-4">
                                    <span className="w-0.5 bg-current rounded-full animate-pulse" style={{ height: '40%', animationDelay: '0ms', animationDuration: '1s' }}></span>
                                    <span className="w-0.5 bg-current rounded-full animate-pulse" style={{ height: '70%', animationDelay: '150ms', animationDuration: '1.2s' }}></span>
                                    <span className="w-0.5 bg-current rounded-full animate-pulse" style={{ height: '100%', animationDelay: '300ms', animationDuration: '0.9s' }}></span>
                                    <span className="w-0.5 bg-current rounded-full animate-pulse" style={{ height: '60%', animationDelay: '450ms', animationDuration: '1.1s' }}></span>
                                  </span>
                                  In Progress
                                </span>
                                {item.dueDate && (
                                  <span className="text-slate-400">
                                    • {new Date(item.dueDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-2 opacity-0 group-hover:opacity-100 transition-all self-end max-w-full">
                                <button
                                  onClick={() => openItemEditor(item, overlayTabData.classId, overlayTabData.categoryId)}
                                  className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-300 text-xs rounded-lg transition-all font-semibold"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handlePermanentDeleteItem(overlayTabData.classId, overlayTabData.categoryId, item.id, item.title)}
                                  className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-300 text-xs rounded-lg transition-all font-semibold"
                                >
                                  🗑️ Delete
                                </button>
                                <button
                                  onClick={() => {
                                    const updatedNotebooks = classNotebooks.map(notebook => {
                                      if (notebook.id === overlayTabData.classId) {
                                        return {
                                          ...notebook,
                                          categories: notebook.categories.map(cat => {
                                            if (cat.id === overlayTabData.categoryId) {
                                              return {
                                                ...cat,
                                                buckets: {
                                                  upcoming: (cat.buckets?.upcoming || []).filter(i => String(i.id) !== String(item.id)),
                                                  completed: [
                                                    ...(cat.buckets?.completed || []),
                                                    { ...item, completed: true, status: 'completed' }
                                                  ]
                                                }
                                              };
                                            }
                                            return cat;
                                          })
                                        };
                                      }
                                      return notebook;
                                    });

                                    setClassNotebooks(updatedNotebooks);
                                    if (user) {
                                      saveNotebooks(user.id, updatedNotebooks);
                                    }

                                    playSound('success');
                                  }}
                                  className="px-3 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 text-emerald-300 text-xs rounded-lg transition-all font-semibold"
                                >
                                  ✓ Complete
                                </button>
                              </div>
                            </div>
                          </div>
                        );})}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm mb-8">No items in progress</p>
                    )}
                    
                    {/* Completed Items Section */}
                    {completedItems.length > 0 && (
                      <>
                        <h3 className="text-xl font-bold text-green-400 mb-2 flex items-center gap-2">
                          ✅ Completed <span className="text-sm text-green-300">({completedCount})</span>
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                          {completedItems.map((item) => {
                            return (
                            <div 
                              key={item.id}
                              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/8 transition-all opacity-60"
                            >
                              <h4 className="text-green-400 font-semibold mb-2 line-through">{item.title}</h4>
                              <div className="flex items-center justify-between gap-2 text-sm">
                                <div className="flex items-center gap-2">
                                <span className="text-green-400 flex items-center gap-1">
                                  <span>✅</span> Completed
                                </span>
                                {item.dueDate && (
                                  <span className="text-slate-500">
                                    • {new Date(item.dueDate).toLocaleDateString()}
                                  </span>
                                )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 opacity-0 group-hover:opacity-100 transition-all max-w-full">
                                  <button
                                    onClick={() => openItemEditor(item, overlayTabData.classId, overlayTabData.categoryId)}
                                    className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-300 text-xs rounded-lg transition-all font-semibold"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => handlePermanentDeleteItem(overlayTabData.classId, overlayTabData.categoryId, item.id, item.title)}
                                    className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-300 text-xs rounded-lg transition-all font-semibold"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          );})}
                        </div>
                      </>
                    )}
                    
                    {allItems.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <p className="text-lg">No items yet in this section.</p>
                        <p className="text-sm mt-2">Add your first item using the button below!</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            {/* Action Bar */}
            <div className="flex gap-3 px-8 py-5 bg-black/30 border-t border-amber-400/20">
              <button 
                onClick={() => {
                  // Set the category and class for the calendar form
                  setSelectedCategoryForForm(overlayTabData.categoryId);
                  setSelectedClassForForm(overlayTabData.classId);

                  // Assignment tab should open assignment form only
                  if (overlayTabData.categoryId === 'assignment') {
                    openCalendarFormForType('assignment', undefined, true);
                  } else {
                    setCalendarTypeLocked(false);
                    setCalendarOpen(true);
                  }
                  setShowTabOverlay(false);
                }}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold rounded-lg transition-all shadow-lg"
              >
                + Add New Item
              </button>
              <button 
                onClick={() => setShowTabOverlay(false)}
                className="px-6 py-3 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default OrganizerPage;
